import { Fragment, useEffect, useState, useCallback, useMemo, useRef, lazy, Suspense } from 'react'
import { groupDuplicateRows } from './relatedContacts.js'
import RelatedChip from './RelatedChip.jsx'
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react'
import { useSignedInIdentity, loadMe } from './useSignedInIdentity.js'
import { InteractionType } from '@azure/msal-browser'
import {
  AppBar, Toolbar, Typography, Box, List, ListItemButton, ListItemText, ListSubheader,
  Chip, Stack, Paper, CircularProgress, ToggleButton, ToggleButtonGroup,
  Tabs, Tab, IconButton, Tooltip, TextField, Collapse, Select, MenuItem, Menu,
  FormControl, InputLabel, Button, Link, Checkbox, Alert, Dialog, Snackbar,
  DialogTitle, DialogContent, DialogActions,
  Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Divider, Badge,
} from '@mui/material'
import { apiRequest, captureDeepLinkPath, consumeDeepLinkPath } from './authConfig.js'
import DeclinedOrderHeader from './DeclinedOrderHeader.jsx'
import cxhubLogo from './assets/cxhub-logo.png'
import { otherMessagesFor, otherMessagesButtonState } from './otherMessages.js'
import { headerCustomerEmail } from './emailCc.js'
import { parkConversation, returnParkedToQueue, fetchMyParks, acknowledgeAutoRequeue, reparkConversation, fetchConversations, fetchConversationActors, fetchM365Addresses, fetchConversation, fetchConversationIdByNumber, pingPresence, fetchPresence, heartbeatAssignment, releaseAssignment, takeoverAssignment, confirmStillWorking, subscribeAssignmentStream, subscribeConversationStatusStream, fetchTranslations, fetchOrderSummary, fetchOrderSummaryItems, prefetchContactOrderSummary, fetchListingItem, fetchOrderSummaryOtherConversations, setIntentOverride, setTeamOverride, setErpOrderIdOverride, closeConversation, reopenConversation, generateAllSuggestions, ingestEbay, downloadAttachment, fetchAttachmentObjectUrl, INTENT_PRIORITIES_CHANGED_EVENT, fetchSessionTick } from './api.js'
import { createSessionTickBus, bindVisibilityRefresh } from './sessionTick.js'
import { setEbayDailyLimits } from './ebayDailyLimit.js'
import EbayMessagesView from './EbayMessagesView.jsx'
import { inlineImageAttachments, replaceCidImages } from './inlineImages.js'
import SuggestionPanel, { groupItemsForTable } from './SuggestionPanel.jsx'
import { SHADOW_COMPARE_COLUMN_WIDTH, SHADOW_COLUMN_MIN_WIDTH, SHADOW_COLUMN_MAX_WIDTH, RESIZE_HANDLE_WIDTH, clampAiColumnWidths } from './shadowDraft.js'
import ConversationActivity from './ConversationActivity.jsx'
import { feedbackIdFromSearch } from './feedbackScreenState.js'
import CostView from './CostView.jsx'
import ApiDocs from './ApiDocs.jsx'
import ApiStatusView from './ApiStatusView.jsx'
import HealthView from './HealthView.jsx'
import AlertsView from './AlertsView.jsx'
import WaitingOnOmsView from './WaitingOnOmsView.jsx'
import SpamRulesView from './SpamRulesView.jsx'
import FeedbackReview from './admin/pages/FeedbackReview.jsx'
import TemplatesPage from './admin/pages/TemplatesPage.jsx'
import ReportingPage from './admin/pages/ReportingPage.jsx'
import FlowsPage from './admin/pages/FlowsPage.jsx'
import PrioritizationPage from './admin/pages/PrioritizationPage.jsx'
import RuntimeFlagsPage from './admin/pages/RuntimeFlagsPage.jsx'
import { canSeeReportingTab, canSeeMyStatsTab, canSeeForecastingTab } from './reportingAccess.js'
import RolesPage from './admin/pages/RolesPage.jsx'
import FailuresPage from './admin/pages/FailuresPage.jsx'
import PromptReviewPage from './admin/pages/PromptReviewPage.jsx'
import QaObserverView from './QaObserverView.jsx'
import OrderNotesWindow, { NotepadIcon } from './OrderNotesWindow.jsx'
import DocumentDetailWindow from './DocumentDetailWindow.jsx'
import { AddressBlock } from './AddressBlock.jsx'
import DraggableModal from './DraggableModal.jsx'
import ManualActionsSidebar from './ManualActionsSidebar.jsx'
import AssistantChat, { AssistantChatButton } from './AssistantChat.jsx'
import ScheduleWidget from './ScheduleWidget.jsx'
import ActiveContactTimer from './ActiveContactTimer.jsx'
import MyStatsPage from './MyStatsPage.jsx'
import NewEmailView from './NewEmailView.jsx'
import TasksView, { TaskContactCard } from './TasksView.jsx'
import { TASKS_PATH, isTasksPath, canSeeTasks, navPageForTasksPath } from './tasks.js'
import { viewPath } from './viewUrl.js'
import { useResizableWidth, ResizeHandle, PANE_WIDTH_KEY_PREFIX } from './ResizablePane.jsx'
import { CarIcon, QueueIcon, ClosedIcon, SettingsIcon, OpsIcon, AuditIcon, ReportingIcon, ScheduleHubIcon, NewEmailIcon, TasksIcon, ChevronIcon } from './icons.jsx'
import { CopyButton } from './CopyButton.jsx'
import VinEdit from './VinEdit.jsx'
import { VIN_UPDATED_NOTICE, vinFieldsFromResponse, decodedVehicleText } from './vinEdit.js'
import { parentOrderTrackingLinks, trackingLinksClipboardText, returnLabelText } from './orderTracking.js'
import { orderRowsFor, itemsHalfFrom } from './orderLines.js'
import { HOVER_PREFETCH_DELAY_MS } from './orderSummaryPrefetch.js'
import { computeLineTax } from './itemTax.js'
import { computeUnitRefundAmounts } from './refundAmount.js'
// Lazy: mermaid (and its many per-diagram-type chunks) is only ever needed on
// the one special /flows/:flowKey/flowchart route below — a static import
// would ship it in every user's initial bundle for a feature almost none of
// them ever open.
const FlowchartViewer = lazy(() => import('./FlowchartViewer.jsx'))

// Deep-linkable per-conversation URLs (/contact/<convNumber>) — hand-rolled
// with the native History API rather than a routing library: the app is one
// large component tree gated by a handful of state flags, and react-router
// would be a much bigger change than the feature itself. Scope: only the
// primary/selected conversation (Shell's selectedId) is addressed by the
// URL — the separate multi-tab mechanism (openTabs/activeTabId) is neither
// encoded in nor restored from it; opening a link always opens just that
// one conversation as the primary selection.
//
// Module-evaluation time, not a React effect — must run before
// MsalAuthenticationTemplate's own redirect-trigger effect gets a chance to
// navigate away (see authConfig.js's own comment on why).
captureDeepLinkPath()

function parseConvNumberFromPath(pathname) {
  const m = /^\/contact\/(\d+)\/?$/.exec(pathname ?? '')
  return m ? m[1] : null
}


// A second, independent hand-rolled path — same "no general router" reasoning
// as /contact/<convNumber> above — for the per-flow Mermaid flowchart viewer,
// opened in a new tab from the admin Flows page. company/channel/client
// travel as query params rather than more path segments, since flowKey
// itself can already contain a colon (e.g. "replacement:damaged").
//
// Checked directly against window.location.pathname (not through the
// capture/consume deep-link round trip /contact/:id uses) — the expected
// case is opening this from an ALREADY-authenticated Flows page tab via
// window.open()/target="_blank", where the new tab's sessionStorage (MSAL's
// own cache location) is a same-origin clone of the opener's, per the
// storage spec, so MSAL resolves silently from cache with no redirect round
// trip at all — window.location.pathname never changes out from under this
// check. A genuinely cold session with no cached token (no opener, e.g. the
// link pasted into a browser with no prior CXHub session) WOULD still
// redirect through login and lose this path the same way an unauthenticated
// /contact/ visit would if it didn't use the deep-link machinery — accepted
// as a rarer edge case for this pure-documentation viewer rather than
// threading the same capture/consume plumbing through a second path pattern.
function parseFlowchartViewerPath(pathname) {
  const m = /^\/flows\/([^/]+)\/flowchart\/?$/.exec(pathname ?? '')
  return m ? decodeURIComponent(m[1]) : null
}

// Fallback lookup for a VIN Comp9 didn't successfully decode — a plain
// client-side link the CSR opens themselves (no server-side request), so
// North American Motoring's own Cloudflare check passes normally like any
// other real browser visit.
function northAmericanMotoringVinUrl(vin) {
  return `https://www.northamericanmotoring.com/forums/vindecoder.php?vin=${encodeURIComponent(vin)}`
}

// Environment labels for the app-bar badge, keyed by GET /api/me's deployEnv.
// The Lab spells out the consequence rather than just naming itself: it holds
// real production credentials and real customer data, and the one thing that
// makes that safe is that nothing can go out (server/src/outbound.js).
const ENV_LABELS = {
  prod: 'Production',
  staging: 'Staging',
  lab: 'LAB — sending disabled',
  local: 'Local',
}

// "Sending disabled" stops being true the moment infrastructure gives the Lab a
// recipient allowlist (server/src/outbound.js) — it can then send, to exactly
// those addresses and no others. Say which, by count: the badge must describe
// what the box will actually do, which is the whole reason it reads deployEnv
// from the server instead of guessing from the hostname.
function envLabel(me) {
  const n = me?.outboundRecipientAllowlistCount ?? 0
  if (me?.deployEnv === 'lab' && n > 0) {
    return `LAB — sending limited to ${n} allowlisted address${n > 1 ? 'es' : ''}`
  }
  return ENV_LABELS[me?.deployEnv] ?? me?.deployEnv
}

const STATUS_COLOR = { open: 'success', pending: 'warning', escalated: 'error', closed: 'default', waiting: 'info', parked: 'secondary' }
// TRQ order status (orderSummary.status) -> text color lives in
// ebayHeaderLayout.js (ORDER_STATUS_COLOR), shared by the Website status line
// and the eBay layout's Status / Total line. Case-insensitive lookup since
// the exact casing TRQ returns hasn't been confirmed live.
// 'parked' (2026-10-02, Lab-only): selectable by anyone in the filter, in no
// default selection — a parked contact is set aside by its owner, not in the
// queue.
const ALL_STATUSES = ['open', 'pending', 'escalated', 'closed', 'waiting', 'parked']
const CSR_DEFAULT_STATUS_FILTER = ['open', 'pending', 'escalated']
// 'waiting' is admin-only (same isAdmin value that gates its dropdown option
// below), so an admin's default queue view includes it and a CSR's doesn't.
const ADMIN_DEFAULT_STATUS_FILTER = ['open', 'pending', 'escalated', 'waiting']
function defaultStatusFilter(isAdmin) {
  return isAdmin ? ADMIN_DEFAULT_STATUS_FILTER : CSR_DEFAULT_STATUS_FILTER
}
function isDefaultStatusFilter(statusFilter, isAdmin) {
  const def = defaultStatusFilter(isAdmin)
  return statusFilter.length === def.length && def.every((s) => statusFilter.includes(s))
}
// Both live brands, for admin and CSR alike, on Queue and Closed (they share
// this state) and as what Clear filters resets to. Was 1A only while AM_EBAY
// was held dark (see git history); every AM account is surfaced now. TRQ has
// no live channel yet, so it isn't a toggle at all (ACTIVE_CHANNEL_COMPANIES).
// A CSR still can't reach a dark account by naming its company — the route
// forces includeDark off for non-elevated callers.
const DEFAULT_COMPANY_FILTER = ACTIVE_CHANNEL_COMPANIES
// eBay + Email (m365_email) are the two live channels agents actually work
// today — chat/phone/sms have no real traffic yet. A user can still
// manually narrow/widen via the Channel dropdown; this is only the default
// every agent starts from. Was eBay-only while M365 email was still test
// traffic (see git history); widened once it became live production mail
// (the two internal failure-notice accounts, 2026-09-11 onward).
const DEFAULT_CHANNEL_FILTER = ['ebay', 'm365_email']
// The Set By spam filter — scoped the same way as Reviewed by Mgmt
// (meaningless outside intent=spam), server-side per model_meta.spamRule.
const SPAM_SET_BY_OPTIONS = ['rule', 'agent']
const SPAM_SET_BY_LABEL = { rule: 'Set by Rules', agent: 'Set by Agent' }
// How many conversations to fetch per page. The list pages via offset + "Load
// more"; the server also returns the total so we can show "N of TOTAL".
const CONVERSATION_PAGE_SIZE = 500

// Left icon nav rail (replaces the old Agent/Ops/Settings/Reporting
// ToggleButtonGroup) — two widths, not a hide/show toggle: expanded shows
// icon + label, collapsed shows icon only. Persisted the same read-on-init/
// write-on-change way main.jsx's own color-mode key already is.
// Tightest width that fits the icon + longest label ("Reporting", ~52px
// rendered at the caption variant used here) without wrapping/clipping —
// measured directly via a headless render, not guessed (52px text + 8px
// padding each side, plus a small safety margin for font-rendering variance
// across environments).
const NAV_RAIL_WIDTH_EXPANDED = 80
const NAV_RAIL_WIDTH_COLLAPSED = 64
// A plain external link-out, not a real CXHub page -- Schedule Hub (formerly
// WFM-Portal) owns all scheduling/time-off/adherence data and UX on its own
// app; this rail entry is a doorway, not an integration. The separate,
// now-built read-only "today's schedule" header widget (ScheduleWidget.jsx)
// is deliberately still NOT this -- it only displays what Schedule Hub's own
// API already computed, no calculations/storage/time-off submission here.
const SCHEDULE_HUB_URL = 'https://schedule-hub.1aauto.inc/admin'
const NAV_RAIL_COLLAPSED_KEY = 'cxhub.navRailCollapsed'
// Replaces the old 'cxhub_layout' key, which was only ever written, never
// read anywhere in this codebase — this one actually restores navPage (and
// whatever mode/filter state that page implies) on the next page load.
const NAV_PAGE_KEY = 'cxhub.navPage'

// Queue filter persistence — localStorage, same idiom as pane widths
// (ResizablePane.jsx's PANE_WIDTH_KEY_PREFIX) and navRailCollapsed/navPage
// above: survives a reload, but is plain per-browser state, not a shareable
// link (unlike URL query params, which this codebase doesn't use for filter
// state anywhere else). A lazy useState initializer reads the stored value
// once at mount; the effect below keeps it written on every change, so every
// existing setXFilter(...) call site (nav-rail resets, filter UI clicks,
// etc.) persists for free with no changes to those call sites.
const QUEUE_FILTER_KEY_PREFIX = 'cxhub.queueFilter.'
function useLocalStorageState(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(QUEUE_FILTER_KEY_PREFIX + key)
      return stored != null ? JSON.parse(stored) : defaultValue
    } catch {
      return defaultValue
    }
  })
  useEffect(() => {
    try { localStorage.setItem(QUEUE_FILTER_KEY_PREFIX + key, JSON.stringify(value)) } catch {}
  }, [key, value])
  return [value, setValue]
}

// A non-selectable "Select all / Deselect all" header row inside a
// multi-select Select's menu, for filters with enough options that bulk
// actions are worth having (Intent, User) — sits inside the dropdown itself
// rather than as its own row below it, so it takes no space at all until the
// dropdown is actually open. MUI's Select treats a MenuItem click as toggling
// that item's own membership in the value, so this can't be a real MenuItem;
// onMouseDown's preventDefault stops the click from being interpreted as a
// value toggle (or the menu closing) before onClick's actual action runs.
function SelectAllMenuHeader({ onSelectAll, onDeselectAll }) {
  return (
    <ListSubheader sx={{ lineHeight: 'normal', py: 0.75 }}>
      <Stack direction="row" spacing={1.5}>
        <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={onSelectAll} sx={{ px: 0, minWidth: 0, fontSize: '0.75rem' }}>
          Select all
        </Button>
        <Button size="small" onMouseDown={(e) => e.preventDefault()} onClick={onDeselectAll} sx={{ px: 0, minWidth: 0, fontSize: '0.75rem' }}>
          Deselect all
        </Button>
      </Stack>
    </ListSubheader>
  )
}

import { INTENT_COLOR, intentLabel, intentOptionsFor } from './constants/intents.js'
import { ebayListingUrl, ebayReturnDetailsUrl, ebayCaseUrl } from './ebayLinks.js'
import { headerEbayReturn } from './headerEbayReturn.js'
import { itemInfoUrl, oneASiteUrl } from './itemLinks.js'
import { ORDER_STATUS_COLOR, isEbayClientEmail as isEbayClientEmailConversation, isNewEbayLayout, orderStatusTotal, ebayItemUrl, listingBannerFor, shouldShowListingBanner } from './ebayHeaderLayout.js'
import { mergeAndSort, conversationComparator, shouldReloadQueueForPriorities } from './conversationListMerge.js'
import { ACTIVE_CHANNEL_COMPANIES, COMPANY_TAG_STYLE } from './constants/companies.js'
import { conversationMatchesToggles, NO_TO_ADDRESS, returnReplyEbayMessageThreadLabel } from '@cxhub/shared'
import { hasSupportToggleAccess, defaultToggleSelection, sanitizeToggleSelection, toggleViewer as queueToggleViewerFor } from './queueToggles.js'
import { KNOWN_CHANNELS, CHANNEL_LABELS } from './constants/channels.js'
import { MESSAGE_TEXT_SX } from './constants/typography.js'
import ParkedView, { ALL_PARKED_SECTION_ID } from './ParkedView.jsx'
import { closeButtonTooltip, reparkErrorText } from './parking.js'
import AutoRequeueNotice, { ShiftEndReminderNotice } from './AutoRequeueNotice.jsx'
import { ReturnReplyResentNotice } from './ReturnReplyRefusedNotice.jsx'
import { convNumberPath } from './contactPath.js'
import { canPark, latestPark, parkNoteLine, shiftEndReminderDue, dateInZone, shiftReminderKey, readFlag, writeFlag, labOverrideSchedule, readLabShiftEnd, writeLabShiftEnd, PARKED_PATH, isParkedPath, navPageForParkedPath, PARK_ALERT_MINUTES } from './parking.js'
import { nowHHMMInZone } from './scheduleStatus.js'
import { formatOrderDateMdy, orderCandidateOptionText } from './suggestionActionInputs.js'
import { orderChoiceText } from './orderChoiceText.js'
import { newestContentOrWholeBody } from './emailChainParser.js'
import { stripExternalSenderBanner, TEAMS, teamLabel, effectiveTeam, headerChannelFor } from '@cxhub/shared'
import { sanitizeHtmlBody } from './htmlSanitize.js'
import { quotedTurnsFor, quotedTurnDateLine } from './quotedHistory.js'
import { taskPanelChannel, taskPanelProps, isTaskMessage, replyToTask, taskIdLabel, channelOptionsFor, defaultChannelFilterFor } from './taskContact.js'
import { defaultIncludeToAddress } from './toAddressAutoSelect.js'
import { groupToAddressDirectory, groupCheckState, toggleGroup, toggleAddress } from './toAddressGroups.js'
import { partyName } from './partyName.js'
import { tabsOpenLabel, tabNumber, tabCustomer, tabTimer, latestInboundId, nextSeenInbound, hasNewMessage, queueRelatedOpenMode } from './contactTabs.js'
import { messageBubbleBg, messageLinkSx, messageTextSx } from './messageColors.js'
import ImageLightbox from './ImageLightbox.jsx'
// Every "are you sure?" on the agent's screen is the styled dialog (2026-10-27).
import { useConfirmDialog } from './ConfirmDialog.jsx'
import { SWITCH_CONTACT_PROMPT, CLOSE_CONTACT_PROMPT, DISCARD_EMAIL_PROMPT } from './confirmPrompts.js'

// The one background poll this tab makes (2026-10-09; sessionTick.js):
// assignments, intent priorities, the failure count and parks arrive together
// every 30 s, and each consumer below subscribes for its own section. One
// bus per tab, created once at module load; it polls only while subscribed.
const sessionTick = createSessionTickBus({ fetch: fetchSessionTick })
bindVisibilityRefresh(sessionTick)

// sanitizeHtmlBody moved to htmlSanitize.js so SuggestionPanel.jsx's
// translated-draft preview can reuse it without a circular import (App.jsx
// already imports SuggestionPanel.jsx) — see that file's own comment.

// A single M365 HTML message's body can embed its OWN quote-chain history
// inline (Outlook's From:/Sent:/To:/Subject: divider, or any of the other
// recognized conventions) — see emailChainParser.js. This is a display-time
// parse of one message's existing body, not a new data model: no new DB
// rows, and a message with no detected chain (splitEmailChain returns
// exactly one segment) renders IDENTICALLY to before this existed. The
// quoted segment, when present, is dropped entirely rather than shown as
// its own bubble — see m365DisplayBodyHtml's own comment for why.
// The message's own body HTML to actually DISPLAY — trimmed to just the
// newest content (splitEmailChain's segment[0]) when an M365 HTML message
// has a detected quote chain (Outlook's own divider, or any of the other
// recognized conventions — see emailChainParser.js's own header comment),
// unchanged otherwise. A message with no detected chain — the common case —
// renders IDENTICALLY to before this existed, since splitEmailChain's own
// no-op case returns the whole body as segment[0]. The dropped, older
// segment is never rendered as its own bubble: everything we've ever sent
// or received is ALREADY its own top-level message earlier in this thread,
// so a quoted copy embedded in a later message is always redundant with
// something already shown there. Shared by both message-rendering call
// sites (the main Thread and the "Other Messages" preview) so their
// trimming behavior can't drift apart. Mimecast's "This message originated
// outside your organization." banner is removed first, with the same shared
// helper the server's sanitizer uses (@cxhub/shared external-banner.js) — at
// display time, so it disappears from messages already stored too.
// When the newest content shows nothing at all (2026-10-13, prod contact
// 7143870: a Gmail reply whose whole body sat inside the gmail_quote wrapper,
// leaving an empty bubble), the whole body is shown instead
// (emailChainParser.js's newestContentOrWholeBody()); it is sanitized by the
// caller like any other.
function m365DisplayBodyHtml(m) {
  if (m.metadata?.contactForm || m.metadata?.leaveUsAMessage || isSystemNotice(m) || m.bodyContentType !== 'html') return m.body
  return newestContentOrWholeBody(stripExternalSenderBanner(m.body))
}

// Shared style for every rendered message body in the conversation pane and
// its previews (the main Thread and OtherMessagesModal) — same fontSize/
// color/line-height the plain Typography variant="body2" branches already
// get, restated explicitly here for the ones that render as a plain Box
// (HTML content via dangerouslySetInnerHTML) rather than a Typography, so a
// message never looks visually different depending on channel (eBay/M365
// email/form-or-notice body) or direction (inbound/outbound). MUI's body2
// line-height (1.43) is a unitless multiplier, so restating it here scales
// correctly against this same fontSize rather than body2's own default 14px.
const MESSAGE_BODY_SX = { ...MESSAGE_TEXT_SX, color: 'text.secondary' }

// A contact-form email's raw HTML body is the form-page chrome (banner, blue
// company-name heading bar, address/copyright footer) around the handful of
// fields the customer actually filled in — server/src/channels/m365/
// contact-form.js already parses those out at ingest time and stores them as
// metadata.contactForm, so rendering that instead of the raw HTML shows only
// what the customer said, not the surrounding page furniture. Field layout
// confirmed with the product owner: every field labeled (including Name,
// Email, and Comments, none bare), grouped as Email/Order Number/Item
// Number, then Name/Company, then Inquiry Type/Comments — each group shown
// even when a field is blank (a consistent field layout regardless of what
// the customer filled in). Reads message text straight off cf.message
// (mapper.js's curated metadata.contactForm now carries it — see
// contact-form.js's parseContactForm()) rather than through a separate prop
// derived from the message's own bodySanitized.
// translatedText: the English translation of THIS field only (cf.message) —
// server/src/routes/conversations.js's /:id/translations route now
// translates just this one field for a contact_form message, not the whole
// raw body (mostly English form labels/structure). Rendered inline, right
// after the field it belongs to, same "English" caption convention as the
// generic per-message translation box, just scoped to one field.
function ContactFormMessageBody({ contactForm: cf, translatedText }) {
  return (
    <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13, color: 'text.secondary' }}>
      <Box component="span" sx={{ fontWeight: 700 }}>Email: </Box>{cf.customerEmail || ''}{'\n'}
      <Box component="span" sx={{ fontWeight: 700 }}>Order Number: </Box>{cf.orderNumber || ''}{'\n'}
      <Box component="span" sx={{ fontWeight: 700 }}>Item Number: </Box>{cf.itemNumber || ''}{'\n'}
      <Box component="span" sx={{ fontWeight: 700 }}>Name: </Box>{cf.name || ''}{'\n'}
      <Box component="span" sx={{ fontWeight: 700 }}>Company: </Box>{cf.company || ''}{'\n'}
      <Box component="span" sx={{ fontWeight: 700 }}>Inquiry Type: </Box>{cf.inquiryType || ''}{'\n'}
      <Box component="span" sx={{ fontWeight: 700 }}>Comments: </Box>{cf.message || ''}
      {translatedText && (<>{'\n'}<Box component="span" sx={{ fontWeight: 700, fontStyle: 'italic' }}>English: </Box>{translatedText}</>)}
    </Typography>
  )
}

// "Leave Us a Message" pre-sale question form — same "render the parsed
// fields, not the raw form-page HTML" reasoning as ContactFormMessageBody
// above (server/src/channels/m365/leave-us-a-message.js's parseLeaveUsAMessage()
// already extracts these at ingest time into metadata.leaveUsAMessage). Field
// labels match the real submission's own wording exactly. One field per
// line, no blank-line gaps anywhere — unlike ContactFormMessageBody's
// grouped layout, the product owner asked for this one fully flat.
// translatedText: the English translation of THIS field only
// (lm.customerQuestion) — same field-scoped translation as
// ContactFormMessageBody above, see that component's own comment.
function LeaveUsAMessageBody({ leaveUsAMessage: lm, translatedText, fullText = null }) {
  const line = (label, value, { first = false, italic = false } = {}) => (value
    ? (<>{first ? null : '\n'}<Box component="span" sx={{ fontWeight: 700, ...(italic ? { fontStyle: 'italic' } : {}) }}>{label}: </Box>{value}</>)
    : null)
  // The whole-body fallback (2026-10-08, leave-us-a-message.js's
  // useFullBody): the parser understood nothing but the email / Contact ID,
  // or an unknown label swallowed text into the email, so the full message
  // shows, with the parsed email and order number still above it.
  if (lm.useFullBody) {
    return (
      <Typography variant="body2" component="div" data-testid="leave-us-a-message-full" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13, color: 'text.secondary' }}>
        {line("Customer's Email", lm.customerEmail, { first: true })}
        {line('Order Number', lm.orderNumber, { first: !lm.customerEmail })}
        {(lm.customerEmail || lm.orderNumber) ? '\n\n' : null}
        {fullText ?? ''}
      </Typography>
    )
  }
  // Only labels with a value (2026-10-08), in the form's own order.
  const fields = [
    ["Customer's Email", lm.customerEmail],
    ["Customer's Question", lm.customerQuestion],
    ['English', translatedText, true],
    ['Type of part', lm.partType],
    ['Brand Number Being Viewed', lm.brandNumber],
    ['URL Being Viewed', lm.urlViewed ? <Link href={lm.urlViewed} target="_blank" rel="noopener noreferrer">{lm.urlViewed}</Link> : null],
    ['Vehicle Year', lm.vehicleYear],
    ['Vehicle Make', lm.vehicleMake],
    ['Vehicle Model', lm.vehicleModel],
    ['VIN', lm.vin],
    ['Order Number', lm.orderNumber],
    ['Replacement Reason', lm.replacementReason],
    ['Items to Replace', lm.itemsToReplace],
    ['Return Reference Number', lm.returnReferenceNumber],
    ['Contact ID', lm.contactId],
  ].filter(([, value]) => value)
  return (
    <Typography variant="body2" component="div" data-testid="leave-us-a-message" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 13, color: 'text.secondary' }}>
      {fields.map(([label, value, italic], i) => <Box component="span" key={label}>{line(label, value, { first: i === 0, italic })}</Box>)}
    </Typography>
  )
}

// Automated return notices (eBay "Automated Return Needs Review" and the
// Site Automation notices) render their own full body, like any other email
// (2026-10-12): never a parsed field list. metadata.automatedReturnNotice is
// still parsed at ingest for the pipeline, order resolution, the reply To and
// the header's Buyer ID link; it is not used to draw the message. On these
// notices only, sanitizeHtmlBody()'s systemNotice option shows content hidden
// by an inline style and drops the remote 1x1 tracking image.
function isSystemNotice(m) {
  return Boolean(m?.metadata?.automatedReturnNotice)
}

// Display-only cleanup for a plain-text message body — collapses a run of
// 2+ consecutive blank (or whitespace-only) lines down to a single one,
// without touching the stored raw data. Some system-generated bodies
// (Automated Return Notice, Sales Hold Applied) carry extra blank lines
// baked into the text itself; this just keeps the rendered bubble from
// showing several stacked empty lines for them.
function collapseExtraBlankLines(text) {
  if (!text) return text
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const collapsed = []
  for (const line of lines) {
    const isBlank = line.trim() === ''
    if (isBlank && collapsed.length > 0 && collapsed[collapsed.length - 1].trim() === '') continue
    collapsed.push(line)
  }
  while (collapsed.length > 0 && collapsed[0].trim() === '') collapsed.shift()
  while (collapsed.length > 0 && collapsed[collapsed.length - 1].trim() === '') collapsed.pop()
  return collapsed.join('\n')
}

// Sales Hold Applied notices have no ingest-time metadata parser at all
// (server/src/ai/sales-hold.js's own isSalesHoldEmail() header comment: the
// body is unstructured free text an agent typed, nothing to structure) — so
// unlike Automated Return Notice this can't be routed by a metadata key, only
// by the SAME subject-prefix check the server's own deterministic
// classification uses. These notices are real HTML documents
// (bodyContentType: 'html', confirmed against live messages) that would
// otherwise hit the raw-HTML render branch below — this routes them into the
// fixed-font/collapsed-blank-lines plain-text branch instead, regardless of
// bodyContentType, same as every other system-generated plain-text notice.
// Every call site also requires m.direction === 'inbound' — a conversation-
// level subject check on its own would force EVERY message in the thread
// (including our own outbound replies) through this plain-text branch;
// system notices always arrive inbound, so this scopes the check to the
// actual notice message, letting outbound HTML replies (e.g. the
// closed-conversation RichReplyEditor's `<p>...</p>` output) fall through to
// the normal bodyContentType-based rendering instead of showing as literal tags.
const SALES_HOLD_APPLIED_SUBJECT_PREFIX = 'Sales Hold Applied to'
function isSalesHoldAppliedSubject(subject) {
  return typeof subject === 'string' && subject.trim().startsWith(SALES_HOLD_APPLIED_SUBJECT_PREFIX)
}

// The Sales Hold Phone Call flow's own internal ops notice (server/src/ai/
// sales-hold-phone-call.js's isSalesHoldPhoneCallEmail() — the exact literal
// subject actions.js's callCustomerOpsEmailAction() uses, case-sensitive
// exact match kept intentionally strict, same as that server-side check).
// Mirrors isSalesHoldAppliedSubject() above for its sibling flow — no
// client-side equivalent existed for this subject at all before, so none of
// the three places that special-case isSalesHoldAppliedSubject also handled
// this one.
const SALES_HOLD_PHONE_CALL_SUBJECT = 'Call Customer to Resolve Sales Hold'
// The exact-match internal ops email only — a plain-text notice with no real
// content worth rendering as HTML, unlike its OB Call sibling below (which
// carries a real "View work item" hyperlink). Exported so the two
// message-body render blocks (Thread's main view and the compact
// OtherBuyerConversationsModal list) can force plain text for JUST this
// subject, not OB Call too — see isSalesHoldPhoneCallSubject()'s own comment
// on why OB Call must fall through to the normal HTML branch instead.
export function isSalesHoldPhoneCallOpsEmailSubject(subject) {
  return typeof subject === 'string' && subject.trim() === SALES_HOLD_PHONE_CALL_SUBJECT
}
// 'OB Call <work-item-number> - <erpOrderId>' — an Azure DevOps work-item-
// change notification for this same flow (server's isSalesHoldPhoneCallEmail(),
// sales-hold-phone-call.js, has the matching OB_CALL_SUBJECT_RE). Unlike the
// exact-match ops email above, this is a real HTML notification carrying a
// genuine "View work item" link — forcing it through the plain-text branch
// (as isSalesHoldPhoneCallSubject() below still does for other, non-body-
// rendering call sites) strips that link down to dead text. The two
// message-body render blocks use isSalesHoldPhoneCallOpsEmailSubject() above
// instead, specifically so this subject falls through to the ordinary
// bodyContentType === 'html' branch and its link renders clickable.
const OB_CALL_SUBJECT_RE = /^OB Call\b/i
export function isSalesHoldPhoneCallSubject(subject) {
  if (typeof subject !== 'string') return false
  const trimmed = subject.trim()
  return trimmed === SALES_HOLD_PHONE_CALL_SUBJECT || OB_CALL_SUBJECT_RE.test(trimmed)
}

// Light/dark toggle. Inline SVGs (currentColor) so there's no icon dependency.
function ColorModeToggle({ colorMode, onToggle }) {
  if (!onToggle) return null
  const dark = colorMode === 'dark'
  return (
    <Tooltip title={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <IconButton onClick={onToggle} size="small" color="inherit" aria-label="toggle color mode">
        {dark ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
          </svg>
        )}
      </IconButton>
    </Tooltip>
  )
}

// Account menu: click your name in the top bar to reveal a sign-out action.
// Sign-out uses MSAL's redirect flow (postLogoutRedirectUri is set in authConfig).
function AccountMenu({ accountName, parkingEnabled = false }) {
  const { instance } = useMsal()
  const [anchorEl, setAnchorEl] = useState(null)
  // Parking (Lab-only, 2026-10-02): signing out leaves every park in place,
  // but say so first. { count } while asking.
  const [parkedLogout, setParkedLogout] = useState(null)
  if (!accountName) return null
  const signOut = () => instance.logoutRedirect({ account: instance.getActiveAccount() })
  const open = Boolean(anchorEl)
  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0, px: 0.5 }}
        endIcon={
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        }
      >
        {accountName}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={async () => {
            setAnchorEl(null)
            if (parkingEnabled) {
              const mine = await fetchMyParks().catch(() => [])
              if (mine.length > 0) { setParkedLogout({ count: mine.length }); return }
            }
            signOut()
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
      <Dialog open={parkedLogout != null} onClose={() => setParkedLogout(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Log out?</DialogTitle>
        <DialogContent>
          <Typography>
            You have {parkedLogout?.count} parked contact{parkedLogout?.count === 1 ? '' : 's'}. They will stay parked to you. Log out anyway?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParkedLogout(null)}>No</Button>
          <Button variant="contained" onClick={() => { setParkedLogout(null); signOut() }}>Yes</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

function timeAgo(iso) {
  if (!iso) return ''
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`
}

// Plain-text "copy all" content for the Part Details modal — mirrors
// everything the modal itself renders (ebayTitleAM, qualifiers, item
// specifications, company specifications), in the same top-to-bottom order.
// row: one Items widget row's itemDetails entry (sku, ebayTitleAM,
// qualifiers, itemSpecifications, companySpecifications) — the modal is now
// per-SKU (opened from a specific item row), not conversation-wide.
function partDetailsCopyText(row) {
  const lines = []
  if (row?.ebayTitleAM) lines.push(row.ebayTitleAM, '')
  // Only ever populated on an Items widget row (groundPerItemDetails()) —
  // the OLD conversation-wide Item Links widget keeps Kit Includes as its
  // own separate modal, never merged in here.
  if (row?.kitIncludes?.length) lines.push(...row.kitIncludes.map((t) => `- ${t}`), '')
  if (row?.qualifiers?.length) lines.push(...row.qualifiers.map((t) => `- ${t}`), '')
  if (row?.itemSpecifications) lines.push(row.itemSpecifications, '')
  for (const spec of row?.companySpecifications ?? []) lines.push(spec.name, spec.text, '')
  return lines.join('\n').trim()
}

// Whole-calendar-day age of the parent order, evaluated in the business's
// timezone (same as formatEST below) so "Today" lines up with the same day
// boundary CSRs already see elsewhere in the app, not the browser's local tz.
// en-CA locale formats as YYYY-MM-DD, giving a clean UTC-midnight anchor for
// both dates so the diff is an exact whole-day count with no DST drift.
function orderAgeInfo(orderDateIso) {
  if (!orderDateIso) return null
  const orderDate = new Date(orderDateIso)
  if (isNaN(orderDate.getTime())) return null
  const toDayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
  const days = Math.max(0, Math.floor((new Date(`${toDayKey(new Date())}T00:00:00Z`) - new Date(`${toDayKey(orderDate)}T00:00:00Z`)) / 86400000))
  return {
    label: days === 0 ? 'Today' : `${days} Days Ago`,
    color: days <= 60 ? 'success.main' : 'error.main',
  }
}

// Duplicate of server/src/integrations/item-info/client.js's own
// normalizeVehicleToken() — same "duplicate small pure functions across the
// client/server boundary" convention as replacement.js's classifyItem (which
// duplicates enrichment.js's own). Used by the "View Fit List" modal's Make/
// Model filters so "Mercedes-Benz"/"Mercedes Benz"/"MercedesBenz" all match.
function normalizeVehicleToken(s) {
  return s?.toLowerCase().trim().replace(/[\s-]+/g, '') ?? ''
}

function formatEST(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

// One quoted email history turn (2026-10-13, ui/src/quotedHistory.js): the
// same bubble as a real message of its direction, with the date, From and
// "from quoted email history" beneath, and no actions. Its HTML goes through
// the same sanitizer as every email body; a turn with none shows its text.
function QuotedHistoryBubble({ item, compact = false }) {
  const { turn, direction } = item
  const body = turn.html ? (
    <Box sx={{ ...MESSAGE_BODY_SX, overflowWrap: 'anywhere', '& img': { maxWidth: '100%' }, '& table': { maxWidth: '100%' }, '& *': { fontFamily: 'inherit !important', fontSize: '13px !important' } }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtmlBody(turn.html) }} />
  ) : (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...MESSAGE_BODY_SX }}>{collapseExtraBlankLines(turn.text)}</Typography>
  )
  if (compact) {
    return (
      <Box sx={{ py: 0.5 }} data-testid="quoted-history-turn" data-direction={direction}>
        <Typography variant="caption" color="text.secondary">{direction === 'outbound' ? 'Agent' : 'Buyer'} · {quotedTurnDateLine(turn, formatEST)}</Typography>
        {body}
      </Box>
    )
  }
  return (
    <Box data-testid="quoted-history-turn" data-direction={direction}>
      <Stack alignItems={direction === 'outbound' ? 'flex-end' : 'flex-start'}>
        <Paper variant="outlined" data-testid={`quoted-history-bubble-${direction}`} sx={[{ px: 1.5, py: 1, maxWidth: direction === 'outbound' ? '85%' : 'calc(100% - 24px)', bgcolor: (theme) => messageBubbleBg(theme, direction) }, (theme) => messageTextSx(theme), (theme) => messageLinkSx(theme)]}>
          {body}
        </Paper>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" data-testid="quoted-history-date-line" sx={{ mt: 0.5, width: '100%', textAlign: direction === 'outbound' ? 'right' : 'left' }}>
        {quotedTurnDateLine(turn, formatEST)}
      </Typography>
    </Box>
  )
}

// MM/DD/YYYY hh:mm AM/PM EST — the M365 Email Info widget's own date format
// (distinct from formatEST above and from suggestionActionInputs.js's
// date-only formatOrderDateMdy). Hand-appends the literal "EST" rather than
// timeZoneName: 'short' (which would correctly emit "EDT" during daylight
// saving) — always "EST" regardless of season, per spec.
function formatMdyHmEst(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const datePart = d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' })
  const timePart = d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true })
  return `${datePart} ${timePart} EST`
}

// Same currency-formatting convention as CostView.jsx's money().
// One label style for box 2: the "Related Documents" heading and, under the
// eBay layout, the Status / Total / Payment labels — the caption variant's own
// size, bold, lineHeight 1.2, so the two can't drift. inline renders inside a
// line of values (which keep their own size); otherwise a block heading,
// exactly the heading's former markup.
const SECTION_LABEL_SX = { lineHeight: 1.2, fontWeight: 700 }
function SectionLabel({ children, inline = false }) {
  return inline
    ? <Typography variant="caption" component="span" sx={{ ...SECTION_LABEL_SX, color: 'text.primary' }}>{children}</Typography>
    : <Typography variant="caption" display="block" sx={SECTION_LABEL_SX}>{children}</Typography>
}

// A TRQ order status in its color — one piece for the Website status line and
// the eBay layout's Status / Total line.
function OrderStatusText({ status }) {
  return <Box component="span" sx={{ color: ORDER_STATUS_COLOR[String(status ?? '').toLowerCase()] }}>{status}</Box>
}

function money(v, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(v)
  } catch {
    return `$${v.toFixed(2)}`
  }
}

// Search icon SVG (no icon-library dependency)
function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
  )
}

// Human-readable labels for eBay referenceType values shown in the contact bar.
const REFTYPE_LABEL = { ORDER: 'eBay Order', RETURN: 'eBay Return', LISTING: 'eBay Listing', CANCELLATION: 'eBay Cancellation' }
// Chip color for the referenceType badge in the conversation list (LISTING is never shown there — see ConversationList).
const REFTYPE_COLOR = { ORDER: 'primary', RETURN: 'secondary', CANCELLATION: 'error' }

// Parse structured reference IDs out of FROM_EBAY system message bodies.
// eBay appends these to the end of their automated emails (order number, return
// ID, case #, INR request ID, etc.). Returns only refs not already shown via
// conversation.referenceType/referenceId or the buyer ID field.
const EBAY_REF_PATTERNS = [
  // Order number: typically XX-XXXXX-XXXXX or all-digit
  { key: 'orderNumber', label: 'eBay Order',   re: /order\s*(?:number|#|no\.?)?[:\s]+(\d{2,3}-[\d-]+|\d{10,20})/i },
  // Return ID: long digit string
  { key: 'returnId',    label: 'Return ID',     re: /return\s*(?:id|number|#|no\.?)?[:\s]+(\d{5,20})/i },
  // INR Request ID
  { key: 'requestId',   label: 'INR Request',   re: /request\s*(?:id|#|no\.?)?[:\s]+(\d{5,20})/i },
  // Case number
  { key: 'caseNumber',  label: 'Case #',        re: /case\s*(?:#|number|no\.?)?[:\s]+(\d{5,20})/i },
  // Item / listing ID (10–14 digit eBay item IDs)
  { key: 'itemId',      label: 'eBay Listing',  re: /item\s*(?:id|number|#|no\.?)?[:\s]+(\d{10,14})/i },
  // Buyer ID — captured so we can deduplicate; never shown as a separate field
  { key: 'buyerId',     label: null,             re: /buyer\s*(?:id|username|name)?[:\s]+([\w][\w\-\.]{0,39})/i },
]

function parseEbaySystemRefs(conversation) {
  if (conversation.metadata?.ebayConversationType !== 'FROM_EBAY') return []

  // Scan all message bodies (sanitized preferred) for known reference patterns.
  const fullText = (conversation.messages ?? [])
    .map((m) => m.bodySanitized ?? m.body ?? '')
    .join('\n')

  const found = {}
  for (const { key, re } of EBAY_REF_PATTERNS) {
    const match = fullText.match(re)
    if (match) found[key] = match[1].trim()
  }

  // Build the list of InfoField props to render, skipping duplicates.
  const result = []
  for (const { key, label } of EBAY_REF_PATTERNS) {
    const val = found[key]
    if (!val || !label) continue  // label=null means always skip (buyerId)

    // Skip if this value is already shown via conversation.referenceId
    if (val === conversation.referenceId) continue

    // Skip order number if the conversation.referenceType is already ORDER
    if (key === 'orderNumber' && conversation.referenceType === 'ORDER') continue
    // Skip return ID if already shown as eBay Return
    if (key === 'returnId' && conversation.referenceType === 'RETURN') continue
    // Skip item ID if already shown as eBay Listing
    if (key === 'itemId' && conversation.referenceType === 'LISTING') continue
    // Skip buyer ID if it matches the customer we're already displaying
    if (key === 'buyerId' && val.toLowerCase() === (conversation.customer?.externalUserId ?? '').toLowerCase()) continue

    result.push({ label, value: val })
  }
  return result
}

// Draggable-width columns (Conversations / Messages / AI Suggestion) —
// useResizableWidth/ResizeHandle live in ./ResizablePane.jsx so panes defined in
// their own modules (e.g. AssistantChat) can share them. Width persists
// per-column in localStorage (keyed by `key`) so a resize sticks across
// reloads — session-only would feel like the drag "didn't take."

// Height/vertical-drag counterpart to useResizableWidth — same
// localStorage-backed persistence (shares PANE_WIDTH_KEY_PREFIX; the prefix
// name predates height-based panes but there's no reason to split it).
// Unlike useResizableWidth, defaults to null ("no fixed height — size to
// content") rather than a hardcoded constant, so the wrapper takes exactly
// the height its content needs until the user actually drags it.
function useResizableHeight(key, { min = 120, max = 600 } = {}) {
  const [height, setHeight] = useState(() => {
    const stored = Number(localStorage.getItem(PANE_WIDTH_KEY_PREFIX + key))
    return Number.isFinite(stored) && stored > 0 ? Math.min(max, Math.max(min, stored)) : null
  })
  const elementRef = useRef(null)
  const startDrag = useCallback((e) => {
    e.preventDefault()
    const startY = e.clientY
    // Measure the current rendered height (whether auto-sized from content
    // or already a customized fixed height) so dragging starts from
    // wherever it visually sits right now, not a stale constant.
    const startHeight = elementRef.current?.getBoundingClientRect().height ?? min
    function onMove(moveEvent) {
      const delta = moveEvent.clientY - startY
      setHeight(Math.min(max, Math.max(min, startHeight + delta)))
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      setHeight((h) => { if (h != null) localStorage.setItem(PANE_WIDTH_KEY_PREFIX + key, String(h)); return h })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [min, max, key])
  return [height, startDrag, elementRef]
}

// Thin draggable strip between the header/widgets area and the Messages/AI
// Suggestion columns below it.
function RowResizeHandle({ onMouseDown, label }) {
  return (
    <Box
      onMouseDown={onMouseDown}
      role="separator"
      aria-orientation="horizontal"
      aria-label={label}
      sx={{
        height: 6,
        flexShrink: 0,
        cursor: 'row-resize',
        bgcolor: 'divider',
        '&:hover': { bgcolor: 'primary.main' },
      }}
    />
  )
}

// Timeframe values ('all'/'1'/'7'/'30'/'90', matching the Select's own
// MenuItem values) -> the wording for the "Timeframe is filtering these
// results" note below. 'all' is never looked up — every call site gates on
// sinceFilter !== 'all' first.
const TIMEFRAME_NOTE_TEXT = { '1': 'today', '7': 'the last 7 days', '30': 'the last 30 days', '90': 'the last 90 days' }

function ConversationList({ items, selectedId, onSelect, onOpenRelated = null, loading, width, total = 0, queueTotal = null, dateFrom = '', dateTo = '', onDateFrom, onDateTo, statusFilter = CSR_DEFAULT_STATUS_FILTER, onStatusFilterChange, intentFilter = [], onIntentFilterChange, companyFilter = DEFAULT_COMPANY_FILTER, onCompanyFilterChange, channelFilter = DEFAULT_CHANNEL_FILTER, onChannelFilterChange, sortBy = 'oldest', onSortByChange, reviewedByMgmtFilter = 'No', onReviewedByMgmtFilterChange, spamSetByFilter = ['rule', 'agent'], onSpamSetByFilterChange, actedByUserFilter = [], onActedByUserFilterChange, actedByUserOptions = [], includeToAddressFilter = [], onIncludeToAddressFilterChange, toAddressFilterOptions = [], toAddressDirectory = [], automatedFilter = false, onAutomatedFilterChange, sinceFilter = 'all', onSinceFilterChange, onSearchQueryChange, onLoadMore, hasMore = false, currentUser = null, assignmentNonce = 0, onBulkGenerated, canGenerateAll = false, onReload, deployEnv = 'prod', filtersOpen = false, onFiltersOpenChange, navPage = 'queue', isElevated = false, isAdmin = false, showSupportToggle = false, toggleViewer = null, defaultToggles = DEFAULT_COMPANY_FILTER, parkingEnabled = false, defaultChannels = DEFAULT_CHANNEL_FILTER, channelOptions = KNOWN_CHANNELS }) {
  // Hover prefetch (2026-10-29): resting on a queue row for a moment starts
  // that contact's header reads (orderSummaryPrefetch.js); moving off or
  // clicking cancels the wait (a click starts them with the contact itself).
  const hoverPrefetchTimerRef = useRef(null)
  const cancelHoverPrefetch = () => { clearTimeout(hoverPrefetchTimerRef.current); hoverPrefetchTimerRef.current = null }
  const startHoverPrefetch = (id) => {
    cancelHoverPrefetch()
    hoverPrefetchTimerRef.current = setTimeout(() => { hoverPrefetchTimerRef.current = null; prefetchContactOrderSummary(id) }, HOVER_PREFETCH_DELAY_MS)
  }
  useEffect(() => cancelHoverPrefetch, [])
  // Intent filter options: every live intent, plus a retired one (the legacy
  // fitment_confirmation) only while it's selected or a loaded contact has it.
  const intentFilterOptions = intentOptionsFor([...intentFilter, ...items.map((c) => c.intentOverride ?? c.latestIntent)])
  // Escalated conversations are admin-only (see server/src/repositories/
  // conversations.js's listConversations() veil) — a csr's queue never
  // contains one, so offering "Escalated" as a selectable filter would just
  // produce a silently empty result with no explanation. Scoped to this
  // dropdown's OFFERED options only; ALL_STATUSES and the role's default
  // status filter stay as-is (a csr's request still harmlessly includes
  // 'escalated' — it just never matches anything server-side).
  // 'waiting' is gated on isAdmin specifically, NOT the broader isElevated
  // (isElevatedOrSupportPermissions) flag escalated uses above — a CSR
  // holding the Support Permissions grant still must not see it (same
  // deliberate admin-only decision as the server-side veil, see
  // repositories/conversations.js's own maySeeWaiting comment).
  const visibleStatuses = ALL_STATUSES
    .filter((s) => isElevated || s !== 'escalated')
    .filter((s) => isAdmin || s !== 'waiting')
    // 'parked' (Lab-only, 2026-10-02) is offered to anyone, but only when
    // parking is on.
    .filter((s) => parkingEnabled || s !== 'parked')
  // "Generate All" — re-added as a manual fallback (2026-08) alongside the
  // poller-auto-generate gating fix: a backlog created outside a normal
  // poller tick (a bulk manual ingest, or a company's generation_enabled
  // flag flipping on after its conversations already exist) shouldn't
  // require waiting on the poller's own cadence to clear. Drains
  // generateAllSuggestions()'s bounded backlog (every conversation still
  // missing a suggestion, capped per call) in a loop until it reports no
  // more candidates, rather than a single capped call that would silently
  // leave most of a large backlog untouched. onBulkGenerated refreshes the
  // list/open detail after each batch so progress is visible as it goes,
  // not just once at the very end.
  const [bulkGenerating, setBulkGenerating] = useState(false)
  const [bulkGenerateProgress, setBulkGenerateProgress] = useState(null) // { generated, failed }

  // Manual "Load N" — the pull-10-real-messages loop from the 1A eBay build-out,
  // restored for NON-PROD ONLY. It was removed from the UI when live ingestion +
  // auto-generate went live and the poller made it redundant in prod; the
  // backend (POST /api/ebay/ingest) never went away. The Lab deliberately runs
  // with no poller, so this is the only way data arrives there.
  //
  // Hidden in prod, where the poller owns ingestion and a stray manual pull is
  // just noise. `deployEnv` defaults to 'prod' at the call site, so an older
  // server that doesn't report it hides the control rather than showing it.
  const [ingesting, setIngesting] = useState(false)
  const [ingestResult, setIngestResult] = useState(null) // string | null
  const [ingestLimit, setIngestLimit] = useState(10)

  async function handleLoad() {
    setIngesting(true)
    setIngestResult(null)
    try {
      const r = await ingestEbay(ingestLimit)
      setIngestResult(`${r.messagesIngested ?? 0} new from ${r.conversationsScanned ?? 0} scanned`)
      onReload?.()
    } catch (e) {
      // Surface the server's own reason (e.g. "eBay reads are disabled…")
      // rather than a generic failure — on a fresh Lab that message is usually
      // the actual next action.
      setIngestResult(e.message || 'Load failed')
    } finally {
      setIngesting(false)
    }
  }

  async function handleGenerateAll() {
    setBulkGenerating(true)
    let generated = 0
    let failed = 0
    setBulkGenerateProgress({ generated, failed })
    try {
      // Defensive iteration cap (not a real-world limit — 50/call already
      // means 5000 conversations): guards against looping forever if a
      // future change ever makes `capped` fail to resolve to false.
      for (let i = 0; i < 100; i++) {
        const r = await generateAllSuggestions()
        generated += r.generated
        failed += r.failed
        setBulkGenerateProgress({ generated, failed })
        onBulkGenerated?.()
        if (!r.capped) break
      }
    } catch (e) {
      console.error('Generate All failed', e)
    } finally {
      setBulkGenerating(false)
    }
  }
  const [q, setQ] = useState('')
  // Debounced (300ms) search query, min 2 characters — drives the filter
  // memo AND the auto-load-more effect below, so neither re-runs/re-fetches
  // on every keystroke (the memo re-filtering every fetched row, and the
  // load-more effect firing a fresh 500-row page fetch whenever the results
  // are still empty — on Closed that walks the entire archive). The
  // TextField's own value (q) stays immediate so typing itself feels
  // responsive; a query under 2 characters is treated as no search at all.
  const [debouncedQ, setDebouncedQ] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(q), 300)
    return () => clearTimeout(handle)
  }, [q])
  const debouncedQuery = debouncedQ.trim().length >= 2 ? debouncedQ.trim() : ''
  // Search is now server-side (2026-09-25) — this component still owns the
  // raw/debounced input state for a responsive TextField, but the actual
  // filtering happens on the server (see Shell's reloadConversations/
  // loadMoreConversations/poll, which take this as `q`), same shape as
  // sinceFilter's own value-down/onChange-up wiring above. Reports only the
  // debounced (2+ char, or empty) value up, not every raw keystroke.
  useEffect(() => {
    onSearchQueryChange?.(debouncedQuery)
  }, [debouncedQuery, onSearchQueryChange])
  // Typing the FIRST character of a search (the box going empty -> non-
  // empty) switches Timeframe to All time — a narrow Timeframe (e.g.
  // Closed's default Last 7 days) would otherwise hide a real match with no
  // visible reason why. Keyed off the raw, immediate `q` (not the debounced/
  // 2-char-minimum search value) so this fires on the very first keystroke,
  // not after the debounce/length gate. Fires ONLY on that one transition —
  // narrowing Timeframe back down again mid-search sticks, not re-forced by
  // further typing — and clearing the box does NOT restore the previous
  // Timeframe (deliberate; it stays on All time until changed or navigated
  // away from).
  const wasSearchNonEmptyRef = useRef(false)
  useEffect(() => {
    const nonEmpty = q.trim().length > 0
    if (nonEmpty && !wasSearchNonEmptyRef.current) onSinceFilterChange?.('all')
    wasSearchNonEmptyRef.current = nonEmpty
  }, [q, onSinceFilterChange])
  const [loadingMore, setLoadingMore] = useState(false)
  // { [conversationId]: {username, displayName, assignedAt} | undefined } —
  // kept live by the SSE subscription below; the poll further down is now
  // just a slow reconciliation fallback, not the primary update path.
  const [assignmentsById, setAssignmentsById] = useState({})

  // Primary update path: pushes every ownership change (claim/release/
  // takeover/stale-prune) to every logged-in agent immediately — see
  // repositories/assignments.js's broadcast() and routes/assignments.js's
  // GET /assignments/stream. Deliberately NOT scoped to items.length like the
  // poll below: this should stay connected regardless of whether any rows
  // are currently loaded, since a row could arrive later already assigned.
  // Mounted once; subscribeAssignmentStream() owns its own reconnect/backoff
  // loop internally, so this effect has nothing to retry itself.
  useEffect(() => {
    const unsubscribe = subscribeAssignmentStream(({ conversationId, owner }) => {
      setAssignmentsById((prev) => ({ ...prev, [conversationId]: owner }))
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    // Every active assignment rather than the loaded ids: at the 500-row page
    // size that id list overflowed the request line and every call failed
    // with a 431 (see fetchAssignments). Rows that aren't on screen just sit
    // unused in the map.
    if (!items.length) return
    // Reconciliation only, now that the SSE subscription above is the primary
    // path: the shared 30 s session tick (sessionTick.js) corrects drift if a
    // connection silently drops before its own reconnect loop notices (e.g. a
    // tab that was asleep). Subscribing also fetches at once, the old
    // `tick()` on mount.
    return sessionTick.subscribe((tick) => {
      if (tick.assignments) setAssignmentsById(tick.assignments)
      // eBay's daily message limit (2026-10-29): the reply box's banner reads it.
      if (Array.isArray(tick.ebayDailyLimit)) setEbayDailyLimits(tick.ebayDailyLimit)
    })
    // items.length, not items: the request no longer depends on WHICH
    // conversations are loaded, only on there being some, so a list refresh
    // that returns the same rows shouldn't restart the poll.
    // assignmentNonce: bumped by the acting agent's own release/takeover
    // (see Thread's onAssignmentChanged) so THEIR list updates immediately
    // instead of waiting up to 60s for the next reconciliation tick — the SSE
    // subscription above also already reflects it right away.
  }, [items.length, assignmentNonce])

  const statusFilterIsDefault = isDefaultStatusFilter(statusFilter, isAdmin)
  const companyFilterIsDefault = companyFilter.length === defaultToggles.length
    && defaultToggles.every((c) => companyFilter.includes(c))
  const channelFilterIsDefault = channelFilter.length === defaultChannels.length
    && defaultChannels.every((c) => channelFilter.includes(c))
  const hasActiveFilter = q || !statusFilterIsDefault || intentFilter.length > 0 || sinceFilter !== 'all' || dateFrom || dateTo
    || !companyFilterIsDefault || !channelFilterIsDefault || actedByUserFilter.length > 0 || automatedFilter

  // The Queue's expanded duplicate groups (related contacts), by primary id.
  const [expandedDuplicateGroups, setExpandedDuplicateGroups] = useState(() => new Set())
  const toggleDuplicateGroup = (id) => setExpandedDuplicateGroups((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const filtered = useMemo(() => {
    // q is no longer filtered here (2026-09-25) — it's sent to the server as
    // `q` (see Shell's reloadConversations/loadMoreConversations/poll) and
    // `items` already comes back pre-filtered, same reason statusFilter's
    // server-side fetch already scopes `items` before this memo runs would
    // suggest, except status stays doubly-filtered client-side too (see its
    // own condition below) — company/channel remain client-side only.
    let list = items
    if (statusFilter.length < ALL_STATUSES.length) list = list.filter((c) => statusFilter.includes(c.status))
    // intentFilter is no longer applied here — it's now a server-side filter
    // (see Shell's reloadConversations/loadMoreConversations), same reason
    // statusFilter's server-side fetch already scopes `items` before this
    // memo runs. Re-filtering client-side would just be redundant.
    // sinceFilter (Timeframe) is ALSO no longer applied here, for the same
    // reason — it's now sent to the server as `since` (see Shell's
    // reloadConversations/loadMoreConversations) rather than re-filtering
    // already-scoped rows client-side, which would double-filter and mask a
    // server-side mistake.
    // Kept as a belt alongside the server-side company filter (Shell sends the
    // selection on every fetch): it still covers the deselect-everything case,
    // which sends no filter at all, and keeps merged incremental-poll rows in
    // line with the selection. c.companyCode defaults to '1A' when absent
    // (matches the server's own '1A_EBAY' fallback in mapConversation()) —
    // never silently hide a conversation just because this newer field wasn't
    // populated.
    //
    // The toggles are company + team (2026-10-07): the same rule the server
    // applies (conversationMatchesToggles() in @cxhub/shared), so this belt
    // never disagrees with what the server returned.
    list = list.filter((c) => conversationMatchesToggles(c, companyFilter, toggleViewer))
    if (channelFilter.length < channelOptions.length) list = list.filter((c) => channelFilter.includes(c.channel))
    return list
  }, [items, statusFilter, intentFilter, companyFilter, channelFilter])

  // The auto-load-more-while-searching effect that used to live here is gone
  // (2026-09-25): it existed only because `q` was a purely client-side filter
  // over whatever page happened to already be loaded, so a match beyond the
  // current page was otherwise invisible. Search is now server-side — a
  // conversation matching `q` anywhere in the system comes back regardless of
  // paging, so there is nothing left for this effect to compensate for.

  // To Address dropdown groups (2026-10-28): each mailbox's aliases under its
  // primary address. Which groups are expanded is local, reset to all
  // collapsed each time the menu opens, never persisted.
  const toAddressGroups = useMemo(() => groupToAddressDirectory(toAddressDirectory), [toAddressDirectory])
  const anyToAddressAliases = toAddressGroups.some((g) => g.aliases.length > 0)
  const [expandedToAddressGroups, setExpandedToAddressGroups] = useState(() => new Set())

  function clearFilters() {
    setQ(''); onIntentFilterChange?.([])
    onCompanyFilterChange?.(defaultToggles); onChannelFilterChange?.(defaultChannels)
    onIncludeToAddressFilterChange?.(defaultIncludeToAddress({ companyFilter: defaultToggles, toAddressDirectory }))
    onDateFrom?.(''); onDateTo?.('')
    onAutomatedFilterChange?.(false)
    // Status, acted-by AND Timeframe are the filters whose "default" differs
    // by tab (see goToNavPage) — Clear must reset to THAT tab's canonical
    // state, not always Queue's, or clicking it from Closed would silently
    // navigate the CSR back to Queue's defaults while still displaying the
    // Closed tab.
    if (navPage === 'closed') {
      onStatusFilterChange?.(['closed'])
      onActedByUserFilterChange?.([currentUser ?? ''])
      onSinceFilterChange?.('7')
    } else {
      onStatusFilterChange?.(defaultStatusFilter(isAdmin))
      onActedByUserFilterChange?.([])
      onSinceFilterChange?.('all')
    }
  }

  // "Timeframe is filtering these results" — shown wherever a narrowed
  // Timeframe could be hiding a real match with no visible reason why (the
  // empty state, and again below the last loaded row). Actionable, not
  // plain text: reflects the actual selected value rather than hardcoding a
  // number, and its own control switches Timeframe to All time in one
  // click — server-side now, so this triggers a refetch.
  const timeframeFilterNote = sinceFilter !== 'all' && (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 3, py: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Showing {TIMEFRAME_NOTE_TEXT[sinceFilter] ?? sinceFilter}.
      </Typography>
      <Link component="button" variant="caption" onClick={() => onSinceFilterChange?.('all')}>
        Search all time
      </Link>
    </Stack>
  )

  return (
    <Box data-testid="conversation-list" sx={{ width: width ?? 216, flexShrink: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Always-visible company toggle row — legible at a glance (unlike the
          old dropdown, which just showed comma-separated text until opened) */}
      <Box sx={{ px: 2, pt: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            value={companyFilter}
            onChange={(_e, newValue) => onCompanyFilterChange?.(newValue)}
            size="small"
            aria-label="Company filter"
            sx={{ flexWrap: 'wrap' }}
          >
            {/* 1A / AM, plus Support for admins and agents with Support
                Email or Support Voice (2026-10-07): company + team. */}
            {[...ACTIVE_CHANNEL_COMPANIES, ...(showSupportToggle ? ['Support'] : [])].map((c) => (
              <ToggleButton
                key={c}
                value={c}
                aria-label={c}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.25,
                  fontSize: '0.75rem',
                  '&.Mui-selected': {
                    bgcolor: COMPANY_TAG_STYLE[c]?.bgcolor,
                    color: COMPANY_TAG_STYLE[c]?.color,
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: COMPANY_TAG_STYLE[c]?.bgcolor,
                    opacity: 0.85,
                  },
                }}
              >
                {c}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={sortBy}
            exclusive
            onChange={(_e, newValue) => { if (newValue) onSortByChange?.(newValue) }}
            size="small"
            aria-label="Sort order"
            sx={{ flexWrap: 'wrap' }}
          >
            <ToggleButton value="oldest" sx={{ textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.75rem' }}>Oldest First</ToggleButton>
            <ToggleButton value="newest" sx={{ textTransform: 'none', px: 1.5, py: 0.25, fontSize: '0.75rem' }}>Newest First</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {/* Header row */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {hasActiveFilter
            ? <Chip size="small" label={`${filtered.length} / ${items.length}`} color="primary" variant="outlined" />
            : <Typography color="text.secondary" variant="body2">{queueTotal != null ? `${queueTotal} in queue` : `${items.length}${total > items.length ? ` of ${total}` : ''}`}</Typography>}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {/* Non-prod only — see handleLoad above. In the Lab there is no
              poller, so this is how real messages get in. */}
          {deployEnv !== 'prod' && (
            <>
              <TextField
                size="small" type="number" label="Count" value={ingestLimit}
                onChange={(e) => setIngestLimit(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 50, 'aria-label': 'Count' }}
                sx={{ width: 80 }}
              />
              <Tooltip title={ingestResult ?? 'Pull the latest N eBay conversations into this environment (read-only on eBay’s side)'}>
                <span>
                  <Button
                    size="small" variant="outlined" onClick={handleLoad} disabled={ingesting}
                    startIcon={ingesting ? <CircularProgress size={14} /> : null}
                  >
                    {ingesting ? 'Loading…' : 'Load'}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {canGenerateAll && (
            <Tooltip title="Generate AI suggestions for every conversation in the queue that doesn't have one yet">
              <span>
                <Button
                  size="small" variant="outlined" onClick={handleGenerateAll} disabled={bulkGenerating}
                  startIcon={bulkGenerating ? <CircularProgress size={14} /> : null}
                >
                  {bulkGenerating
                    ? `Generating… ${bulkGenerateProgress?.generated ?? 0}${bulkGenerateProgress?.failed ? ` (${bulkGenerateProgress.failed} failed)` : ''}`
                    : 'Generate All'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={filtersOpen ? 'Collapse search & filter' : 'Search & filter'}>
            <IconButton size="small" onClick={() => onFiltersOpenChange?.(!filtersOpen)} color={hasActiveFilter ? 'primary' : 'default'} aria-label="toggle search and filter">
              <SearchIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Collapsible search + filter panel */}
      <Collapse in={filtersOpen}>
        <Box sx={{ px: 2, pt: 1, pb: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
          {/* Text search */}
          <TextField
            size="small"
            fullWidth
            placeholder="Name, email, buyer/order/case/inquiry/return/cancel ID…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ '& .MuiInputBase-input': { py: '4px', fontSize: '0.75rem' } }}
            InputProps={{
              startAdornment: (
                <Box component="span" sx={{ mr: 0.5, color: 'text.secondary', display: 'flex' }}>
                  <SearchIcon />
                </Box>
              ),
              endAdornment: q ? (
                <IconButton size="small" onClick={() => setQ('')} edge="end" aria-label="clear search">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                </IconButton>
              ) : null,
            }}
          />

          {/* Status + Timeframe side by side */}
          <Stack direction="row" spacing={0.75}>
            <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' } }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Status</InputLabel>
              <Select
                multiple
                value={statusFilter}
                label="Status"
                onChange={(e) => onStatusFilterChange?.(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) =>
                  selected.filter((s) => visibleStatuses.includes(s)).length === visibleStatuses.length
                    ? 'All'
                    : selected.map((s) => s[0].toUpperCase() + s.slice(1)).join(', ')
                }
              >
                {visibleStatuses.map((s) => (
                  <MenuItem key={s} value={s}>
                    <Checkbox size="small" checked={statusFilter.includes(s)} />
                    <ListItemText primary={s[0].toUpperCase() + s.slice(1)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' } }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Timeframe</InputLabel>
              <Select value={sinceFilter} label="Timeframe" onChange={(e) => onSinceFilterChange?.(e.target.value)}>
                <MenuItem value="all">All time</MenuItem>
                <MenuItem value="1">Today</MenuItem>
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* Channel — was paired side-by-side with Company; Company is now
              the always-visible toggle row above, so Channel gets the full row. */}
          <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' } }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Channel</InputLabel>
            <Select
              multiple
              value={channelFilter}
              label="Channel"
              onChange={(e) => onChannelFilterChange?.(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) =>
                selected.length === channelOptions.length ? 'All' : selected.map((ch) => CHANNEL_LABELS[ch] ?? ch).join(', ')
              }
            >
              {channelOptions.map((ch) => (
                <MenuItem key={ch} value={ch}>
                  <Checkbox size="small" checked={channelFilter.includes(ch)} />
                  <ListItemText primary={CHANNEL_LABELS[ch] ?? ch} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* To Address — server-side (routes/conversations.js's
              includeToAddress param), shown only while Channel includes
              m365_email (the only channel that has one). Registry-sourced
              options (toAddressFilterOptions, from GET /m365-addresses), not
              invented client-side. INCLUDE-style (2026-09-24): the checkbox
              list's "included/visible" reading (checked = shown) now matches
              the state threaded up to Shell (includeToAddressFilter)
              directly — no more computing the excluded complement on every
              toggle. "All" only when literally every known address is
              included — the actual default (every address except the
              shared test mailbox's own) reads as "N selected" instead,
              since there's no existing "all except X" display convention to
              reuse.
              Grouped by mailbox (2026-10-28, toAddressGroups.js): one row per
              mailbox's primary address, its aliases under it, collapsed each
              time the menu opens. The group checkbox includes or removes the
              whole group (checked / indeterminate / unchecked by how much of
              it is included); the caret only expands or collapses. Rows carry
              no `value`, so MUI's own one-value toggle never runs: each row's
              onClick computes the next flat address list itself. The filter
              state, the includeToAddress param and the auto-select are
              unchanged, and the summary still counts addresses. */}
          {channelFilter.includes('m365_email') && (
            <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' } }}>
              <InputLabel shrink sx={{ fontSize: '0.75rem' }}>To Address</InputLabel>
              <Select
                multiple
                displayEmpty
                value={includeToAddressFilter}
                label="To Address"
                onOpen={() => setExpandedToAddressGroups(new Set())}
                renderValue={(selected) =>
                  selected.length === 0 ? 'None' : selected.length === toAddressFilterOptions.length ? 'All' : `${selected.length} selected`
                }
              >
                <SelectAllMenuHeader
                  onSelectAll={() => onIncludeToAddressFilterChange?.(toAddressFilterOptions)}
                  onDeselectAll={() => onIncludeToAddressFilterChange?.([])}
                />
                {/* Email contacts with no To address (2026-10-30), ticked by default. */}
                <MenuItem key={NO_TO_ADDRESS} onClick={() => onIncludeToAddressFilterChange?.(toggleAddress(NO_TO_ADDRESS, includeToAddressFilter))}>
                  {anyToAddressAliases && <Box sx={{ width: 24, ml: -0.75, flexShrink: 0 }} />}
                  <Checkbox size="small" checked={includeToAddressFilter.includes(NO_TO_ADDRESS)} />
                  <ListItemText primary="No To address" />
                </MenuItem>
                {toAddressGroups.flatMap((group) => {
                  const hasAliases = group.aliases.length > 0
                  const expanded = expandedToAddressGroups.has(group.primary)
                  const { checked, indeterminate } = groupCheckState(group, includeToAddressFilter)
                  const groupRow = (
                    <MenuItem key={group.primary} onClick={() => onIncludeToAddressFilterChange?.(toggleGroup(group, includeToAddressFilter))}>
                      {hasAliases ? (
                        <IconButton
                          size="small"
                          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.primary}`}
                          aria-expanded={expanded}
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setExpandedToAddressGroups((prev) => {
                              const next = new Set(prev)
                              if (next.has(group.primary)) next.delete(group.primary)
                              else next.add(group.primary)
                              return next
                            })
                          }}
                          sx={{ p: 0.25, ml: -0.75 }}
                        >
                          <ChevronIcon expanded={expanded} fontSize="small" sx={{ color: 'text.secondary' }} />
                        </IconButton>
                      ) : anyToAddressAliases ? (
                        <Box sx={{ width: 24, ml: -0.75, flexShrink: 0 }} />
                      ) : null}
                      <Checkbox size="small" checked={checked} indeterminate={indeterminate} />
                      <ListItemText
                        primary={hasAliases ? (
                          <>
                            {group.primary}
                            <Box component="span" sx={{ color: 'text.secondary', ml: 0.75 }}>
                              +{group.aliases.length} {group.aliases.length === 1 ? 'alias' : 'aliases'}
                            </Box>
                          </>
                        ) : group.primary}
                      />
                    </MenuItem>
                  )
                  if (!hasAliases || !expanded) return [groupRow]
                  return [groupRow, ...group.aliases.map((addr) => (
                    <MenuItem key={addr} onClick={() => onIncludeToAddressFilterChange?.(toggleAddress(addr, includeToAddressFilter))} sx={{ pl: 6 }}>
                      <Checkbox size="small" checked={includeToAddressFilter.includes(addr)} />
                      <ListItemText primary={addr} />
                    </MenuItem>
                  ))]
                })}
              </Select>
            </FormControl>
          )}

          {/* Last-message datetime range — server-side; changes what's fetched (not just filtered) */}
          <Stack direction="row" spacing={0.75}>
            <TextField
              size="small" fullWidth type="datetime-local" label="From" InputLabelProps={{ shrink: true }}
              value={dateFrom} onChange={(e) => onDateFrom?.(e.target.value)}
              sx={{ '& .MuiInputBase-input': { py: '4px', fontSize: '0.72rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
            />
            <TextField
              size="small" fullWidth type="datetime-local" label="To" InputLabelProps={{ shrink: true }}
              value={dateTo} onChange={(e) => onDateTo?.(e.target.value)}
              sx={{ '& .MuiInputBase-input': { py: '4px', fontSize: '0.72rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
            />
          </Stack>

          {/* Intent filter — multi-select (like Channel/Set By above): pick
              several intents at once instead of one-or-all. An empty
              selection means unfiltered, same meaning "All intents" used to
              carry. Select all/Deselect all live inside the menu itself
              (SelectAllMenuHeader) rather than as their own row below the
              dropdown, so they take no space until it's actually open. */}
          <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' } }}>
            {/* shrink: displayEmpty means renderValue's placeholder text is always
                visible, but MUI only auto-shrinks the label when it considers the
                value "filled" -- an empty array (our unfiltered default) isn't, so
                without this the label sits unshrunk on top of the placeholder text. */}
            <InputLabel shrink sx={{ fontSize: '0.75rem' }}>Intent</InputLabel>
            <Select
              multiple
              displayEmpty
              value={intentFilter}
              label="Intent"
              onChange={(e) => onIntentFilterChange?.(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) =>
                selected.length === 0 ? 'All intents' : selected.length === intentFilterOptions.length ? 'All' : `${selected.length} selected`
              }
            >
              <SelectAllMenuHeader
                onSelectAll={() => onIntentFilterChange?.(intentFilterOptions.map(([k]) => k))}
                onDeselectAll={() => onIntentFilterChange?.([])}
              />
              {intentFilterOptions.map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Checkbox size="small" checked={intentFilter.includes(k)} />
                  <ListItemText primary={v} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Reviewed by Mgmt — only meaningful (and only shown) when
              filtering to the Spam intent. */}
          {intentFilter.includes('spam') && (
            <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' } }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Reviewed by Mgmt</InputLabel>
              <Select value={reviewedByMgmtFilter} label="Reviewed by Mgmt" onChange={(e) => onReviewedByMgmtFilterChange?.(e.target.value)}>
                <MenuItem value="No">No</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="All">All</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Set By — same scoping as Reviewed by Mgmt (only meaningful when
              filtering to the Spam intent), but a multi-select checkbox
              control (like Company/Channel above) rather than a single-value
              dropdown, since "rule" and "agent" aren't mutually exclusive. */}
          {intentFilter.includes('spam') && (
            <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' } }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Set By</InputLabel>
              <Select
                multiple
                value={spamSetByFilter}
                label="Set By"
                onChange={(e) => onSpamSetByFilterChange?.(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) =>
                  selected.length === SPAM_SET_BY_OPTIONS.length ? 'All' : selected.map((v) => SPAM_SET_BY_LABEL[v]).join(', ')
                }
              >
                {SPAM_SET_BY_OPTIONS.map((v) => (
                  <MenuItem key={v} value={v}>
                    <Checkbox size="small" checked={spamSetByFilter.includes(v)} />
                    <ListItemText primary={SPAM_SET_BY_LABEL[v]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* User — filter to one or more acting users' activity (anyone
              who's decided a suggestion, closed a conversation, or processed
              a step for it), regardless of role. Multi-select, same
              rationale/pattern as Intent above (Select all/Deselect all
              live in the menu itself). */}
          <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: '0.75rem' }, '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' } }}>
            <InputLabel shrink sx={{ fontSize: '0.75rem' }}>User</InputLabel>
            <Select
              multiple
              displayEmpty
              value={actedByUserFilter}
              label="User"
              onChange={(e) => onActedByUserFilterChange?.(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              renderValue={(selected) =>
                selected.length === 0 ? 'All users' : selected.length === actedByUserOptions.length ? 'All' : `${selected.length} selected`
              }
            >
              <SelectAllMenuHeader
                onSelectAll={() => onActedByUserFilterChange?.(actedByUserOptions)}
                onDeselectAll={() => onActedByUserFilterChange?.([])}
              />
              {actedByUserOptions.map((u) => (
                <MenuItem key={u} value={u}>
                  <Checkbox size="small" checked={actedByUserFilter.includes(u)} />
                  <ListItemText primary={u} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Automated — contacts where a Flows automation attempt ran,
              whether it succeeded or failed. */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Checkbox
              size="small"
              checked={automatedFilter}
              onChange={(e) => onAutomatedFilterChange?.(e.target.checked)}
              sx={{ p: 0.5 }}
            />
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Automated contacts only</Typography>
          </Stack>

          {hasActiveFilter && (
            <Button size="small" onClick={clearFilters} sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0, fontSize: '0.75rem' }}>
              Clear filters
            </Button>
          )}
        </Box>
      </Collapse>

      {/* Conversation list */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : filtered.length === 0 ? (
          <>
            <Typography sx={{ p: 3, pb: timeframeFilterNote ? 0 : 3 }} color="text.secondary">
              {/* items.length === 0 alone used to mean "truly nothing" — safe
                  when search was client-side, since a search match-nothing
                  case still left `items` holding the unfiltered page (only
                  `filtered` went to 0). Now that q is sent to the server,
                  a real "no matches" response is ALSO items.length === 0, so
                  an active search must win regardless of the count. */}
              {items.length === 0 && !debouncedQuery ? 'No conversations yet.' : 'No conversations match your search.'}
            </Typography>
            {timeframeFilterNote}
          </>
        ) : (
          <List disablePadding>
            {/* Related contacts (2026-10-20, relatedContacts.js): duplicates
                of a primary on this page fold into its row, "×n", with an
                expand control; everything else stays its own row. */}
            {groupDuplicateRows(filtered).map(({ row: c, duplicates }) => {
              // Gray out (but never disable — it must stay clickable, opening
              // it is what surfaces the "assigned to X" takeover modal) a row
              // someone ELSE currently has assigned. The owner's own row never
              // grays out for themselves.
              const owner = assignmentsById[c.id]
              const assignedToOther = owner && owner.username !== currentUser
              const expanded = expandedDuplicateGroups.has(c.id)
              return (
                <Fragment key={c.id}>
                <ListItemButton
                  key={c.id} selected={c.id === selectedId} onClick={() => { cancelHoverPrefetch(); onSelect(c.id, c.convNumber) }} divider alignItems="flex-start"
                  onMouseEnter={() => { if (c.id !== selectedId) startHoverPrefetch(c.id) }} onMouseLeave={cancelHoverPrefetch}
                  sx={assignedToOther ? { opacity: 0.55, bgcolor: 'action.hover' } : undefined}
                >
                  <ListItemText
                    sx={{ minWidth: 0 }}
                    primary={
                      <>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography fontWeight={600} variant="body2" noWrap sx={{ minWidth: 0 }}>{partyName(c)}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{timeAgo(c.awaitingSince ?? c.lastMessageAt)}</Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} display="block" noWrap>
                          {c.convNumber ?? c.externalConversationId ?? c.id}
                        </Typography>
                      </>
                    }
                    secondary={
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                        {c.referenceType && c.referenceType !== 'LISTING' && (
                          <Chip size="small" label={c.referenceType} color={REFTYPE_COLOR[c.referenceType] ?? 'default'} variant="outlined" />
                        )}
                        {(c.status === 'pending' || c.status === 'escalated' || c.status === 'waiting') && (
                          <Chip size="small" label={c.status} color={STATUS_COLOR[c.status] ?? 'default'} />
                        )}
                        {c.latestIntent && (
                          <Chip size="small" label={intentLabel(c.latestIntent)} color={INTENT_COLOR[c.latestIntent] ?? 'default'} variant="outlined" />
                        )}
                        {/* The quick-reply rule raised this contact's Queue
                            level (2026-10-29, Settings → Prioritization). */}
                        {c.priorityReason === 'quick_reply' && (
                          <Tooltip title={`The customer answered our message quickly, so this contact moved up to priority level ${c.priorityTier} while it waits for our reply.`}>
                            <Chip size="small" label="Quick reply" color="secondary" data-testid="quick-reply-chip" />
                          </Tooltip>
                        )}
                        {assignedToOther && (
                          <Tooltip title={`Assigned to ${owner.displayName}`}>
                            <Chip size="small" label={owner.displayName} variant="outlined" />
                          </Tooltip>
                        )}
                        {duplicates.length > 0 && (
                          <Tooltip title={`${duplicates.length + 1} copies of the same message: this is the primary. Click to ${expanded ? 'hide' : 'show'} the duplicates.`}>
                            <Chip size="small" color="warning" data-testid="duplicate-count-chip" label={`×${duplicates.length + 1} ${expanded ? '▴' : '▾'}`}
                              onClick={(e) => { e.stopPropagation(); toggleDuplicateGroup(c.id) }} onMouseDown={(e) => e.stopPropagation()} />
                          </Tooltip>
                        )}
                        {duplicates.length === 0 && c.relatedContacts?.hasRelated && (
                          <RelatedChip conversationId={c.id} onOpen={(id, convNumber) => (onOpenRelated ?? onSelect)(id, convNumber)} />
                        )}
                      </Stack>
                    }
                  />
                </ListItemButton>
                {expanded && duplicates.map((d) => (
                  <ListItemButton key={d.id} data-testid="duplicate-member-row" selected={d.id === selectedId} onClick={() => onSelect(d.id, d.convNumber)} divider sx={{ pl: 4, py: 0.5 }}>
                    <ListItemText
                      primary={<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{d.convNumber ?? d.id}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">Duplicate of {c.convNumber ?? c.id} · {timeAgo(d.awaitingSince ?? d.lastMessageAt)}</Typography>}
                    />
                  </ListItemButton>
                ))}
                </Fragment>
              )
            })}
          </List>
        )}
        {filtered.length > 0 && !loading && timeframeFilterNote}
        {hasMore && !loading && (
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Button
              size="small"
              disabled={loadingMore}
              onClick={async () => { setLoadingMore(true); try { await onLoadMore?.() } finally { setLoadingMore(false) } }}
              startIcon={loadingMore ? <CircularProgress size={12} color="inherit" /> : null}
            >
              {loadingMore ? 'Loading…' : `Load more (${total - items.length} more)`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

// A labelled field in the contact info bar. Renders nothing if value is absent.
// Pass href to hyperlink the value itself (opens in a new tab). Pass
// copyable to show a copy-to-clipboard icon next to the value.
// copyValue: overrides what the copy icon copies, for fields whose displayed
// value includes extra detail (e.g. "5382957226 (CS_CLOSED)") but should only
// copy the bare id. Defaults to value when not given.
// valueColor: optional theme color path (e.g. "success.main") to override the
// value's default text color — used by fields like order age that convey
// status through color, same green/red convention used elsewhere in this file.
function InfoField({ label, value, href, copyable, copyValue, valueColor, hideLabel }) {
  if (!value) return null
  return (
    <Box sx={{ minWidth: 0 }}>
      {!hideLabel && <Typography variant="caption" display="block" sx={{ lineHeight: 1.2, fontWeight: 700 }}>{label}</Typography>}
      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
        {href ? (
          <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13, minWidth: 0, ...(valueColor ? { color: valueColor } : {}) }} noWrap>
            {value}
          </Link>
        ) : (
          <Typography variant="body2" sx={{ fontSize: 13, minWidth: 0, color: 'text.secondary', ...(valueColor ? { color: valueColor } : {}) }} noWrap>{value}</Typography>
        )}
        {copyable && <CopyButton value={copyValue ?? value} />}
      </Stack>
    </Box>
  )
}

// Titled card for a group of related header fields (Order Info, Return/Case
// Info, Item Links, Vehicle Info) — the caller is responsible for not
// rendering one at all when it has no populated fields (InfoField itself
// already returns null for an empty value, but an EMPTY widget shell with
// just a title and no fields would still show as a bare card).
// wide: opts a widget OUT of fit-to-content sizing, pinning it to a shared
// wider basis instead — used for Order Info + Vehicle Info so they render at
// the same width as each other (Order Info's content needs more room than a
// short single-line vehicle description, so left on its own each would
// shrink to very different widths). Pure CSS can't dynamically match one
// flex sibling's width to another's without JS measurement or a CSS Grid
// restructure — pinning both to the same explicit basis is the practical
// equivalent.
// The one spinner every order-header widget shows while switching orders.
// How long a header order fetch may spin before the header gives up and shows
// its error / Retry state (2026-10-09).
const HEADER_FETCH_CAP_MS = 30_000

// A header response for another order than the one the contact now has
// (2026-10-29): a prefetched read, started before the contact moved to
// another order. An older server sends no erpOrderId: taken as is.
function forOtherOrder(data, erpOrderId) {
  return data != null && typeof data === 'object' && 'erpOrderId' in data && data.erpOrderId !== erpOrderId
}

function HeaderWidgetLoading() {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%', py: 1 }} data-testid="header-widget-loading">
      <CircularProgress size={14} />
      <Typography variant="caption" color="text.secondary">Loading…</Typography>
    </Stack>
  )
}

function InfoWidget({ children, wide = false, grow = false, flush = false }) {
  return (
    <Paper data-testid="header-widget" variant="outlined" sx={{ px: flush ? 0 : 1, py: 0.25, flex: grow ? '1 1 480px' : wide ? '0 1 320px' : '0 1 auto', minWidth: 0 }}>
      <Stack spacing={0.5}>{children}</Stack>
    </Paper>
  )
}

// One row of the Items widget's table (App.jsx, TESTING-PERIOD ONLY /
// trqClientCode 1AW/AMW) — everything the old, now-removed Item Info/Item
// Links + Vehicle Info widgets showed once per conversation, resolved per
// line item instead (item: one entry of orderSummary.itemDetails, merged
// with its own tracking — see routes/conversations.js's groundPerItemDetails()
// call). Owns its own "fit detail expanded" toggle — independent per row, so
// a CSR can inspect more than one item's fitment at once.
//
// SKU's 1A Site/Item Info links are hover-revealed (opacity toggle on the
// row, not a separate always-visible InfoField pair like the old widget) —
// no existing hover-reveal-links component in this codebase to copy, so this
// is a small fresh UI piece rather than a port of an existing one.
// Same three cases the old Vehicle Info widget's fit indicator mapped onto
// (fits===true && additionalFactorsMatch===true -> green; fits===true &&
// additionalFactorsMatch===false -> orange; fits===false -> red) — null (no
// icon at all) when fitment wasn't checked for this row (no vehicle
// resolved, or the getFitment lookup found nothing). Standalone (not inline
// in ItemDetailsRow) so both the row's icon AND the fit-detail modal's
// title/label share this exact mapping without drifting apart.
function fitIconFor(fitment) {
  if (!fitment) return null
  if (fitment.fits === true && fitment.additionalFactorsMatch === false) return { color: 'warning.main', symbol: '?', label: 'YMM Match — Please Review' }
  if (fitment.fits === true && fitment.additionalFactorsMatch === true) return { color: 'success.main', symbol: '✓', label: 'Listed to Fit - Please Confirm' }
  if (fitment.fits === false) return { color: 'error.main', symbol: '✕', label: 'Does Not Fit' }
  return null
}

// Single-line truncate-with-tooltip, for header rows too narrow for a full
// value — same "wrap in a Tooltip once it's too long to show in full"
// principle as ItemDetailsRow's Description cell (which wraps to 2 lines at
// a 100-char cap instead, since a table cell has more room than one header
// line).
// cap={null}: never truncated (the eBay layout's vehicle text, 2026-10-07).
function TruncatedCaption({ text, cap = 45, noWrap = true, sx = {} }) {
  if (!text) return null
  if (cap != null && text.length > cap) {
    return (
      <Tooltip title={text}>
        <Typography variant="caption" noWrap={noWrap} sx={{ fontSize: 13, lineHeight: 1.4, color: 'text.secondary', cursor: 'default', ...sx }}>
          {text.slice(0, cap)}…
        </Typography>
      </Tooltip>
    )
  }
  return <Typography variant="caption" noWrap={noWrap} sx={{ fontSize: 13, lineHeight: 1.4, color: 'text.secondary', ...sx }}>{text}</Typography>
}

// A slow cell still loading (2026-10-29): a 12px spinner in place of the
// value. A row whose slow half failed or brought nothing shows "—".
function SlowCellLoading({ testId }) {
  return <CircularProgress size={12} data-testid={testId} aria-label="Loading" />
}
function SlowCellMissing({ testId }) {
  return <Typography component="span" variant="caption" data-testid={testId} sx={{ fontSize: 13, color: 'text.secondary' }}>—</Typography>
}

// showEbayLink (the eBay layout, 2026-10-07): the SKU hover reveal also links
// the ordered item's own eBay listing, from its TRQ ebay_id (ebayItemId).
// item.slow (2026-10-29, orderLines.js): { details, fit }, each 'loading' |
// 'ok' | 'none' — Stock and Tags follow details, Fit follows fit. A row with
// no slow (the no-order row) shows its values as before.
function ItemDetailsRow({ item, onOpenPartDetails, onOpenFitDetail, showTracking = true, detailed = false, lineTax = null, unitRefundAmt = null, showAmSku = false, showEbayLink = false }) {
  const itemEbayUrl = showEbayLink ? ebayItemUrl(item.ebayItemId) : null
  const isComponent = item.itemType === 'component'
  const fitIcon = fitIconFor(item.fitment)
  const hasPartDetails = Boolean(item.kitIncludes?.length > 0 || item.qualifiers?.length > 0 || item.companySpecifications?.length > 0 || item.itemSpecifications || item.ebayTitleAM)
  // Component rows always carry unitPrice 0 by design (confirmed against the
  // tax-calc spreadsheet's own component rows, which leave unitPrice/
  // extendedPrice blank) — render Price/Line Total EMPTY for that case rather
  // than a misleading "$0.00" (different from '—', which still means "no
  // value at all").
  const zeroPriceComponent = isComponent && Number(item.unitPrice ?? 0) === 0
  const detailsState = item.slow?.details ?? 'ok'
  const fitState = item.slow?.fit ?? 'ok'

  return (
    <TableRow sx={{
      // Kit and standalone rows get a light background (the same
      // action.hover token used elsewhere in this widget); component rows
      // stay unshaded — "not a component" rather than an explicit
      // kit/standalone allowlist, so a future third non-component type
      // inherits the same treatment for free.
      ...(item.itemType !== 'component' ? { bgcolor: 'action.hover' } : {}),
      '&:hover .item-links': { opacity: 1, maxHeight: 24 },
    }}>
      <TableCell sx={{ pl: isComponent ? 3 : 1, fontSize: 13 }}>
        <Stack spacing={0.25}>
          <Box component="span" sx={{ color: 'text.secondary' }}>{isComponent ? '↳ ' : ''}{item.sku ?? '—'}</Box>
          {item.sku && (
            <Stack direction="row" spacing={0.5} className="item-links" sx={{ opacity: 0, maxHeight: 0, overflow: 'hidden', transition: 'opacity 0.15s, max-height 0.15s' }}>
              {itemEbayUrl && (
                <Link href={itemEbayUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>eBay</Link>
              )}
              <Link href={oneASiteUrl(item.sku)} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>1A Site</Link>
              <Link href={itemInfoUrl(item.sku)} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>Item Info</Link>
            </Stack>
          )}
        </Stack>
      </TableCell>
      {detailed && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{item.brandId ?? '—'}</TableCell>
      )}
      {(detailed && showAmSku) && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{item.amSku ?? '—'}</TableCell>
      )}
      <TableCell>
        {item.description && item.description.length > 100 ? (
          <Tooltip title={item.description}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.2, cursor: 'default' }}>
              {item.description.slice(0, 50)}<br />{item.description.slice(50, 100)}…
            </Typography>
          </Tooltip>
        ) : item.description && item.description.length > 50 ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.2 }}>
            {item.description.slice(0, 50)}<br />{item.description.slice(50)}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.2 }}>{item.description ?? '—'}</Typography>
        )}
      </TableCell>
      {detailed && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{item.quantity ?? '—'}</TableCell>
      )}
      <TableCell sx={{ pl: 2 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {zeroPriceComponent ? '' : (item.unitPrice != null ? money(item.unitPrice) : '—')}
        </Typography>
      </TableCell>
      {detailed && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
          {zeroPriceComponent ? '' : money((item.unitPrice ?? 0) * (item.quantity ?? 0))}
        </TableCell>
      )}
      {detailed && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{item.weight ?? '—'}</TableCell>
      )}
      {detailed && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{item.unitCost != null ? money(item.unitCost) : '—'}</TableCell>
      )}
      {detailed && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
          {lineTax != null && item.quantity ? money(lineTax / item.quantity) : '—'}
        </TableCell>
      )}
      {detailed && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{unitRefundAmt != null ? money(unitRefundAmt) : '—'}</TableCell>
      )}
      {showTracking && (
        <TableCell sx={{ pr: 0.25 }}>
          <Stack spacing={0.25}>
            {(item.tracking ?? []).map((t) => (
              <Box key={t.trackingNumber}>
                {t.source !== 'parent' && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    {`Repl. ${t.source}`}
                  </Typography>
                )}
                {t.url ? (
                  <Link href={t.url} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ fontSize: 13 }}>
                    {t.trackingNumber} ({t.carrier})
                  </Link>
                ) : (
                  <Typography component="span" variant="caption" sx={{ fontSize: 13, color: 'text.secondary' }}>{t.trackingNumber}</Typography>
                )}
                <CopyButton value={t.trackingNumber} />
              </Box>
            ))}
          </Stack>
        </TableCell>
      )}
      {detailed && (
        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{item.fulfillmentLocation ?? '—'}</TableCell>
      )}
      <TableCell sx={{ pl: 0.25 }}>
        {detailsState === 'loading' ? <SlowCellLoading testId="items-stock-loading" />
          : detailsState === 'none' ? <SlowCellMissing testId="items-stock-missing" />
            : item.quantityAvailable != null && (
          <Typography variant="caption" sx={{ fontSize: 13, fontWeight: 700 }} color={item.quantityAvailable > 0 ? 'success.main' : 'error.main'}>
            {item.quantityAvailable > 0 ? item.quantityAvailable : 'OOS'}
          </Typography>
        )}
      </TableCell>
      <TableCell align="center">
        {fitState === 'loading' ? <SlowCellLoading testId="items-fit-loading" />
          : fitState === 'none' ? <SlowCellMissing testId="items-fit-missing" />
            : fitIcon && (
          <Tooltip title={fitIcon.label}>
            <IconButton size="small" onClick={() => onOpenFitDetail(item)} sx={{ color: fitIcon.color }} aria-label="view fitment detail">
              <Typography sx={{ fontWeight: 700, lineHeight: 1 }}>{fitIcon.symbol}</Typography>
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          {/* Dropship comes with the order's lines; the rest with the details. */}
          {item.dropship && <Typography variant="caption" sx={{ fontSize: 13, color: 'warning.main', fontWeight: 700, lineHeight: 1.2 }}>Dropship</Typography>}
          {detailsState === 'loading' ? (
            <>
              <SlowCellLoading testId="items-tags-loading" />
              {/* Part Details opens once this row's details are in. */}
              <Tooltip title="Loading part details…">
                <span>
                  <Link component="button" variant="caption" disabled data-testid="items-part-details-loading" sx={{ textAlign: 'left', color: 'text.disabled', pointerEvents: 'none' }}>
                    Part Details
                  </Link>
                </span>
              </Tooltip>
            </>
          ) : detailsState === 'none' ? <SlowCellMissing testId="items-tags-missing" /> : (
            <>
              {item.electricalPart && <Typography variant="caption" sx={{ fontSize: 13, color: 'error.main', fontWeight: 700, lineHeight: 1.2 }}>Electrical</Typography>}
              {item.warranty && <Typography variant="caption" sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.2 }}>{item.warranty}</Typography>}
              {hasPartDetails && (
                <Link component="button" variant="caption" onClick={() => onOpenPartDetails(item)} sx={{ textAlign: 'left' }}>
                  Part Details
                </Link>
              )}
            </>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  )
}

// The Items widget's table, factored out since it now renders in two places:
// the capped inline view (top 3 groups) and the "All Items" modal's full
// view. Reuses ItemDetailsRow as-is. detailed=true (modal only) swaps in the
// expanded column set (Brand #/Qty/Line Total/Weight/Cost/Unit Tax/Unit
// Refund Amt/FC, "Details" instead of "Tags") — the inline preview stays on
// the original column set.
// order: { orderTax, shippingCost, orderTotal } — only read when detailed,
// to compute each line's Unit Tax column via computeLineTax() (itemTax.js).
// allItems (2026-10-12): every item of the order, for the compact widget's
// "Copy all tracking links for the order" (the rows shown may be only the
// first few).
function ItemsTable({ items, totalCount = null, allItems = null, onOpenPartDetails, onOpenFitDetail, showTracking = true, detailed = false, order = null, showAmSku = false, showEbayLink = false }) {
  const trackingLinks = useMemo(() => (detailed ? [] : parentOrderTrackingLinks(allItems ?? items)), [detailed, allItems, items])
  const copyAllRef = useRef(null)
  const lineTaxes = useMemo(
    () => detailed ? computeLineTax(items, order ?? {}) : null,
    [detailed, items, order]
  )
  // computeUnitRefundAmounts() only needs the items themselves (no order-level
  // tax inputs) — same per-item cost this contact's own refund breakdown
  // (SuggestionPanel.jsx's estimatedRefundBreakdown()) uses, surfaced here for
  // every contact rather than only within a return/refund action's own
  // item-selection table.
  const unitRefundAmts = useMemo(
    () => detailed ? computeUnitRefundAmounts(items) : null,
    [detailed, items]
  )
  const fullCount = totalCount ?? items.length
  return (
    <Box sx={{ pl: 1 }}>
      <TableContainer>
        <Table size="small" sx={{
          '& .MuiTableCell-root': { px: 0.5, py: 0.25, verticalAlign: 'top' },
          '& .MuiTableCell-head': { backgroundColor: 'transparent' },
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                SKU{fullCount > items.length && (
                  // Red, so an agent notices only some of the parts are shown
                  // (2026-10-12). The theme's error color, light and dark.
                  <Box component="span" data-testid="items-sku-count" sx={{ color: 'error.main' }}>{` (${items.length}/${fullCount})`}</Box>
                )}
              </TableCell>
              {detailed && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Brand #</TableCell>}
              {(detailed && showAmSku) && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>AM SKU</TableCell>}
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Description</TableCell>
              {detailed && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Qty</TableCell>}
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, pl: 2 }}>Price</TableCell>
              {detailed && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Line Total</TableCell>}
              {detailed && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Weight</TableCell>}
              {detailed && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Cost</TableCell>}
              {detailed && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Unit Tax</TableCell>}
              {detailed && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Unit Refund Amt</TableCell>}
              {showTracking && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, pr: 0.25 }}>Tracking</TableCell>}
              {detailed && <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>FC</TableCell>}
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, pl: 0.25 }}>Stock</TableCell>
              <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Fit</TableCell>
              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{detailed ? 'Details' : 'Tags'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it, ri) => (
              <ItemDetailsRow key={it.sku ?? `${it.description}-${ri}`} item={it} showTracking={showTracking}
                detailed={detailed} lineTax={lineTaxes ? lineTaxes[ri] : null} unitRefundAmt={unitRefundAmts ? unitRefundAmts[ri] : null} showAmSku={showAmSku}
                showEbayLink={showEbayLink} onOpenPartDetails={onOpenPartDetails} onOpenFitDetail={onOpenFitDetail} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* The compact header widget only (2026-10-12): a red "Viewing x of y"
          when not every part is shown, and one click to copy every parent-
          order tracking link (from every part, not just the rows shown). */}
      {!detailed && (fullCount > items.length || trackingLinks.length > 0) && (
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pl: 0.5, mt: 0.25 }}>
          {fullCount > items.length && (
            <Typography variant="caption" data-testid="items-viewing-count" sx={{ color: 'error.main' }}>
              {`Viewing ${items.length} of ${fullCount} parts ordered`}
            </Typography>
          )}
          {trackingLinks.length > 0 && (
            <Stack direction="row" spacing={0.25} alignItems="center" data-testid="items-copy-tracking">
              <Link component="button" type="button" variant="caption" underline="hover" onClick={() => copyAllRef.current?.click()}>
                Copy all tracking links for the order
              </Link>
              <CopyButton value={trackingLinksClipboardText(trackingLinks)} ariaLabel="Copy all tracking links for the order" buttonRef={copyAllRef} />
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  )
}

// The Items widget's per-row Fitment popup — factored out (same "already
// getting long, extract it" precedent as ItemsTable above) now that it
// covers Customer Vehicle + Fit Listing + an always-visible, filterable Fit
// List, roughly doubling what used to be a ~45-line inline block. Owns its
// own Year/Make/Model filter state, entirely separate from the OLD Vehicle
// Info widget's fitListYearFilter/fitListMakeFilter/fitListModelFilter (that
// trio — and its own "Fit List" DraggableModal — stays dedicated to the OLD
// widget, a different code path serving eBay conversations). Filters reset
// whenever `row` changes (including closing, row -> null), so switching
// between rows in the same session never leaks one row's typed filter text
// into the next row's view — same "close resets its filters" convention
// closeFitList() uses for the OLD widget's modal.
function FitmentDetailModal({ row, onClose }) {
  const [yearFilter, setYearFilter] = useState('')
  const [makeFilter, setMakeFilter] = useState('')
  const [modelFilter, setModelFilter] = useState('')

  useEffect(() => {
    setYearFilter('')
    setMakeFilter('')
    setModelFilter('')
  }, [row])

  const fitment = row?.fitment ?? null
  const vehicle = row?.vehicle ?? null
  const fitIcon = fitIconFor(fitment)
  const allEntries = fitment?.allFitEntries ?? []
  const yearNum = yearFilter.trim() === '' ? null : Number(yearFilter)
  const yearFilterActive = yearNum !== null && Number.isFinite(yearNum)
  const makeFilterNorm = normalizeVehicleToken(makeFilter)
  const modelFilterNorm = normalizeVehicleToken(modelFilter)
  const filteredEntries = allEntries.filter((e) => {
    if (yearFilterActive && !(yearNum >= e.yearStart && yearNum <= e.yearEnd)) return false
    if (makeFilterNorm && !normalizeVehicleToken(e.make).includes(makeFilterNorm)) return false
    if (modelFilterNorm && !normalizeVehicleToken(e.model).includes(modelFilterNorm)) return false
    return true
  })
  // "Fit Listing" only has content for the hasVariants case (matched/all
  // variant titles) and the confident-single-match case (fitment.title) —
  // fits === false has no matched listing to label, so no heading there
  // either, same "only show a heading when there's content under it" rule
  // Customer Vehicle above already follows.
  const hasFitListing = Boolean(fitment?.hasVariants) || (fitment?.fits !== false && Boolean(fitment?.title))

  return (
    <DraggableModal
      open={row != null}
      onClose={onClose}
      title={`Fitment — ${row?.sku ?? ''}`}
      centered
      width={680} height={660}
      minWidth={420} minHeight={400}
      actions={<Button onClick={onClose}>Close</Button>}
    >
      {fitment && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, color: fitIcon?.color }}>{fitIcon?.label}</Typography>
            {fitment.note && (
              <Chip size="small" label={fitment.note}
                sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'break-word', display: 'block', py: 0.5 } }} />
            )}
          </Stack>

          {vehicle && (
            <Box>
              <Typography variant="caption" fontWeight={700} display="block">Customer Vehicle</Typography>
              <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>{vehicle.label}</Typography>
              {vehicle.details && (
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>{vehicle.details}</Typography>
              )}
              {vehicle.vin && (
                <Stack direction="row" spacing={0.5} alignItems="baseline">
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{vehicle.vin}</Typography>
                  <CopyButton value={vehicle.vin} />
                </Stack>
              )}
            </Box>
          )}

          {hasFitListing && (
            <Box>
              <Typography variant="caption" fontWeight={700} display="block">Possible Matching Fit Listing</Typography>
              {fitment.hasVariants ? (
                <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                  {(fitment.matchedVariantTitles?.length ? fitment.matchedVariantTitles : fitment.variantTitles).map((t) => (
                    <Typography key={t} component="li" variant="caption" display="list-item" sx={{ overflowWrap: 'break-word' }}>
                      {t}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ overflowWrap: 'break-word' }}>{fitment.title}</Typography>
              )}
            </Box>
          )}

          {/* Extra gap beyond the outer Stack's own spacing={1.5}, specifically
              between Possible Matching Fit Listing and Full Fit List — the
              outer Stack's spacing already covers every other section, so this
              is added locally rather than raising that shared value. */}
          <Box sx={{ mt: hasFitListing ? 1.5 : 0 }}>
            <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.75 }}>Full Fit List</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <TextField size="small" label="Year" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} sx={{ width: 150 }} />
              <TextField size="small" label="Make" value={makeFilter} onChange={(e) => setMakeFilter(e.target.value)} fullWidth />
              <TextField size="small" label="Model" value={modelFilter} onChange={(e) => setModelFilter(e.target.value)} fullWidth />
            </Stack>
            {allEntries.length > 0 && filteredEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No matches</Typography>
            ) : (
              <Stack spacing={1} sx={{ '& > *:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider', pb: 1 } }}>
                {filteredEntries.map((e) => (
                  <Stack key={e.title} direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>{e.title}</Typography>
                    <CopyButton value={e.title} />
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </DraggableModal>
  )
}

// Eye icon for the "also viewing" indicator
function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  )
}

// "Stop viewing" close icon for the Thread header's deselect button
function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18M6 6l12 12"/>
    </svg>
  )
}

// A viewer is "recently opened" for the first 10 minutes after their
// firstSeenAt — re-derived on every 30s presence poll tick, no dedicated timer needed.
const RECENTLY_OPENED_MS = 10 * 60 * 1000
function isRecentlyOpenedViewer(viewer) {
  return typeof viewer.firstSeenAt === 'number' && Date.now() - viewer.firstSeenAt < RECENTLY_OPENED_MS
}

// "Other Messages" modal — other recent (last-10-days) conversations from the
// same buyer, across every company/channel (see findRecentConversationsByBuyer()).
// Read-only preview + two actions per row: close a still-open one with no
// reply needed, or open it alongside the current conversation as a new tab
// (onOpenConversationTab, wired up to Shell's tab-strip state).
function OtherMessagesModal({ open, onClose, conversations, onOpenConversationTab, onConversationClosed }) {
  const [expandedId, setExpandedId] = useState(null)
  const [detailsById, setDetailsById] = useState({})
  const [closingId, setClosingId] = useState(null)
  const [localStatusById, setLocalStatusById] = useState({})

  useEffect(() => {
    if (!open) { setExpandedId(null); setDetailsById({}); setLocalStatusById({}) }
  }, [open])

  async function handleToggleDetails(id) {
    if (expandedId === id) { setExpandedId(null); return }
    setExpandedId(id)
    if (detailsById[id]) return
    setDetailsById((prev) => ({ ...prev, [id]: { loading: true, messages: [], error: null } }))
    try {
      const d = await fetchConversation(id)
      setDetailsById((prev) => ({ ...prev, [id]: { loading: false, messages: d.messages ?? [], error: null } }))
    } catch (e) {
      setDetailsById((prev) => ({ ...prev, [id]: { loading: false, messages: [], error: e.message } }))
    }
  }

  async function handleCloseNoReply(id) {
    setClosingId(id)
    try {
      await closeConversation(id)
      setLocalStatusById((prev) => ({ ...prev, [id]: 'closed' }))
      onConversationClosed?.()
    } catch (e) {
      window.alert(`Couldn't close this conversation: ${e.message}`)
    } finally {
      setClosingId(null)
    }
  }

  const sorted = [...(conversations ?? [])].sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt))

  return (
    <DraggableModal open={open} onClose={onClose} title="Other Messages" centered
      width={Math.round(window.innerWidth * 0.9)} height={Math.round(window.innerHeight * 0.85)}
      minWidth={380} minHeight={260}>
      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No other recent conversations from this buyer.</Typography>
      ) : (
        <Stack spacing={1}>
          {sorted.map((c) => {
            const status = localStatusById[c.id] ?? c.status
            const detail = detailsById[c.id]
            return (
              <Paper key={c.id} variant="outlined" sx={{ p: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ minWidth: 82 }}>{formatOrderDateMdy(c.lastMessageAt)}</Typography>
                  <Chip size="small" label={status} color={STATUS_COLOR[status] ?? 'default'} />
                  <Chip size="small" variant="outlined" label={`${c.companyCode ?? '1A'} · ${c.client ?? CHANNEL_LABELS[c.channel] ?? c.channel}`} />
                  <Chip size="small" variant="outlined" label={CHANNEL_LABELS[c.channel] ?? c.channel} />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button size="small" onClick={() => handleToggleDetails(c.id)}>
                    {expandedId === c.id ? 'Hide details' : 'View details'}
                  </Button>
                  {status === 'open' && (
                    <Button size="small" color="warning" disabled={closingId === c.id} onClick={() => handleCloseNoReply(c.id)}>
                      {closingId === c.id ? 'Closing…' : 'Close - No Reply Needed'}
                    </Button>
                  )}
                  <Button size="small" variant="contained" onClick={() => onOpenConversationTab(c.id)}>
                    Open Conversation
                  </Button>
                </Stack>
                {expandedId === c.id && (
                  <Box data-testid="other-messages-preview" sx={[{ mt: 1, pl: 1, borderLeft: 2, borderColor: 'divider', maxHeight: 220, overflowY: 'auto' }, (theme) => messageTextSx(theme), (theme) => messageLinkSx(theme)]}>
                    {detail?.loading && <CircularProgress size={16} />}
                    {detail?.error && <Alert severity="error" sx={{ py: 0 }}>{detail.error}</Alert>}
                    {detail && !detail.loading && !detail.error && detail.messages.length === 0 && (
                      <Typography variant="caption" color="text.secondary">No messages.</Typography>
                    )}
                    {detail && !detail.loading && !detail.error && detail.messages.map((m) => {
                      return (
                      <Fragment key={m.id}>
                      {quotedTurnsFor(m).map((item) => <QuotedHistoryBubble key={item.key} item={item} compact />)}
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {m.direction === 'outbound' ? 'Agent' : 'Buyer'} · {formatEST(m.receivedAt)}
                        </Typography>
                        {m.metadata?.leaveUsAMessage ? (
                          <LeaveUsAMessageBody leaveUsAMessage={m.metadata.leaveUsAMessage} fullText={m.bodySanitized} />
                        ) : m.metadata?.contactForm ? (
                          <ContactFormMessageBody contactForm={m.metadata.contactForm} />
                        ) : !isSystemNotice(m) && (isSalesHoldAppliedSubject(c.subject) || isSalesHoldPhoneCallOpsEmailSubject(c.subject)) && m.direction === 'inbound' ? (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...MESSAGE_BODY_SX }}>
                            {collapseExtraBlankLines(m.bodySanitized || m.body)}
                          </Typography>
                        ) : m.bodyContentType === 'html' ? (
                          <Box sx={{
                            ...MESSAGE_BODY_SX,
                            overflowWrap: 'anywhere',
                            '& img': { maxWidth: '100%' },
                            '& table': { maxWidth: '100%' },
                            // Same "render every body at the theme's own font,
                            // regardless of channel/direction" rule as the main
                            // Thread's own HTML branch below (App.jsx's
                            // MESSAGE_BODY_SX comment) — this preview had no
                            // override at all before, so an inline-styled body
                            // (either direction) rendered at whatever size/font
                            // the sender's own HTML happened to carry.
                            '& *': { fontFamily: 'inherit !important', fontSize: '13px !important' },
                          }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtmlBody(m365DisplayBodyHtml(m), { systemNotice: isSystemNotice(m) }) }} />
                        ) : (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...MESSAGE_BODY_SX }}>
                            {collapseExtraBlankLines(m.bodySanitized || m.body)}
                          </Typography>
                        )}
                      </Box>
                      </Fragment>
                      )
                    })}
                  </Box>
                )}
              </Paper>
            )
          })}
        </Stack>
      )}
    </DraggableModal>
  )
}

function Thread({ conversation: conversationProp, loading, onDecided, onGenerated, onSpamMarked, onIntentOverrideChanged, suggestionRefreshKey, refreshSkipDraft, forcedDraftText, currentUser, isAdmin, isElevatedOrSupport, autoRegenerateStaleMinutes, onDraftDirtyChange, onUntoldCustomerChange = null, onDeselect, onAssignmentChanged, onOpenConversationTab, onOtherConversationClosed, onInactivityPrompt, onOrderSummaryEmailChange, parkingEnabled = false, onParked, onParkChanged, showShadowCompare = false, onAssignmentState = null }) {
  // An agent's VIN edit (2026-10-21, VinEdit.jsx): the saved VIN, its decode
  // and the override show at once, over the detail it was saved on, until
  // the detail refreshes (a new object, which carries the same from the
  // server). vinNoticeFor: the contact whose VIN was just changed, for the
  // "Regenerate the suggestion to use it" line; it goes once the suggestion
  // is regenerated or the contact changes.
  const [vinLocal, setVinLocal] = useState(null)
  const [vinNoticeFor, setVinNoticeFor] = useState(null)
  const [regenerateRequest, setRegenerateRequest] = useState(0)
  const conversation = useMemo(
    () => (conversationProp && vinLocal?.base === conversationProp ? { ...conversationProp, ...vinLocal.fields } : conversationProp),
    [conversationProp, vinLocal],
  )
  const [suggestionPanelWidth, startSuggestionPanelDrag] = useResizableWidth('suggestionPanel', 480, { min: 360, max: 900, invert: true })
  // Whether SuggestionPanel shows the agent's shadow draft side by side
  // (2026-10-16): the column grows by SHADOW_COMPARE_COLUMN_WIDTH meanwhile.
  const [shadowCompareSide, setShadowCompareSide] = useState(false)
  // The agent-draft column's own width (2026-10-19): resizable, remembered per
  // browser like the other panes; SHADOW_COMPARE_COLUMN_WIDTH is its default.
  const [shadowDraftWidth, startShadowDraftDrag] = useResizableWidth('shadowDraftColumn', SHADOW_COMPARE_COLUMN_WIDTH, { min: SHADOW_COLUMN_MIN_WIDTH, max: SHADOW_COLUMN_MAX_WIDTH, invert: true })
  // The thread row's width, so the AI column never pushes the messages column
  // under MESSAGES_COLUMN_MIN_WIDTH (clampAiColumnWidths()).
  const threadRowRef = useRef(null)
  const [threadRowWidth, setThreadRowWidth] = useState(null)
  useEffect(() => {
    const el = threadRowRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver((entries) => setThreadRowWidth(entries[0]?.contentRect?.width ?? null))
    ro.observe(el)
    return () => ro.disconnect()
  }, [conversation?.id])
  const aiWidths = clampAiColumnWidths({ rowWidth: threadRowWidth, suggestionWidth: suggestionPanelWidth, shadowWidth: shadowDraftWidth, side: shadowCompareSide })
  const [headerHeight, startHeaderDrag, headerRef] = useResizableHeight('headerArea', { min: 100, max: 500 })
  const [overrideBusy, setOverrideBusy] = useState(false)
  const [overrideError, setOverrideError] = useState(null)
  const [erpOrderOverrideBusy, setErpOrderOverrideBusy] = useState(false)
  const [erpOrderOverrideError, setErpOrderOverrideError] = useState(null)
  // The just-picked candidate order, shown as the Select's value WHILE
  // erpOrderOverrideBusy is true — see handleErpOrderIdOverrideChange's own
  // comment for why this is needed (no server-sent progress to key off of).
  const [pendingErpOrderId, setPendingErpOrderId] = useState(null)
  const [intentMenuAnchor, setIntentMenuAnchor] = useState(null)
  const [teamMenuAnchor, setTeamMenuAnchor] = useState(null)
  // The "Initiated Conversation From Listing" banner (eBay channel, from a
  // listing): the stored Item Info title when the pipeline resolved one,
  // otherwise the server's listing-item route. Keyed on the conversation so a
  // stale title never shows on the next one.
  const listingBanner = conversation ? listingBannerFor(conversation) : null
  const [fetchedListingTitle, setFetchedListingTitle] = useState({ for: null, title: null, sku: null })
  useEffect(() => {
    if (!listingBanner || conversation?.itemInfoEbayTitleAM) return undefined
    let cancelled = false
    fetchListingItem(conversation.id)
      .then((d) => { if (!cancelled) setFetchedListingTitle({ for: conversation.id, title: d?.title ?? null, sku: d?.sku ?? null }) })
      .catch(() => { if (!cancelled) setFetchedListingTitle({ for: conversation.id, title: null, sku: null }) })
    return () => { cancelled = true }
  }, [conversation?.id, listingBanner?.listingId, conversation?.itemInfoEbayTitleAM])
  const listingTitle = conversation?.itemInfoEbayTitleAM
    ?? (fetchedListingTitle.for === conversation?.id ? fetchedListingTitle.title : null)
  // The listing's part (2026-10-19): one value for the banner's visibility
  // check and its Item Info / 1A Site links.
  const listingSku = conversation?.itemInfoSku ?? (fetchedListingTitle.for === conversation?.id ? fetchedListingTitle.sku : null)
  const [otherMessagesOpen, setOtherMessagesOpen] = useState(false)
  const [view, setView] = useState('messages')
  const [activityKey, setActivityKey] = useState(0)
  // The Feedback Review's "Activity log" link (2026-10-26) opens
  // /contact/<n>?view=activity: the first contact shown lands on the
  // Activity log tab, once, for an admin.
  const requestedViewRef = useRef(peekBootThreadView())
  // The report the Activity Log marks "Reported here" (2026-10-29).
  const [reportedFeedbackId] = useState(bootFeedbackId)
  const messagesBottomRef = useRef(null)
  const messagesPaneRef = useRef(null)
  const messagesContentRef = useRef(null)
  const [viewers, setViewers] = useState([])
  const [showTranslation, setShowTranslation] = useState(false)
  const [translationMap, setTranslationMap] = useState(null)
  const [translating, setTranslating] = useState(false)
  const [orderSummary, setOrderSummary] = useState(null)
  // A Sales Hold's "Other Messages" list: { for, loading, list }, or null for
  // any other conversation (see the order-summary effect below).
  const [salesHoldOtherMessages, setSalesHoldOtherMessages] = useState(null)
  const [orderSummaryLoading, setOrderSummaryLoading] = useState(false)
  // Set when the last order-summary fetch failed — surfaced as a small
  // warning + Retry control rather than silently discarding whatever
  // orderSummary data was already successfully assembled (see the fetch
  // effect below: a failure now preserves prior state instead of nulling it).
  const [orderSummaryError, setOrderSummaryError] = useState(false)
  // Bumped by the Retry control to re-run the order-summary fetch effect
  // without needing to close/reopen the conversation.
  const [orderSummaryRetryKey, setOrderSummaryRetryKey] = useState(0)
  // The one genuinely slow, per-item piece of order-summary (Items/Item Info
  // widget) — fetched independently of the rest via GET
  // /:id/order-summary/items, so it gets its OWN small loading state rather
  // than holding up orderSummaryLoading above.
  const [itemDetailsLoading, setItemDetailsLoading] = useState(false)
  // The order whose items request failed (Lab-only, 2026-10-08): the listing
  // banner then shows rather than waiting forever.
  const [itemDetailsErrorFor, setItemDetailsErrorFor] = useState(null)
  // The Items widget's slow fields (2026-10-29, orderLines.js): { for,
  // details, fitment, lines }, each half { status, rows } from GET
  // /:id/order-summary/items with X-Items-Part, so Fit (the model call) never
  // holds up Stock and Tags. The rows themselves come with the order summary
  // (orderLines). fitLoading: the fitment half in flight. itemsRetryKey:
  // the widget's own Retry, which reruns only the items requests.
  const [itemsSlow, setItemsSlow] = useState(null)
  const [fitLoading, setFitLoading] = useState(false)
  const [itemsRetryKey, setItemsRetryKey] = useState(0)
  // The order each fetch last ran for, per conversation: a different order
  // on the same conversation (the order picker, a regenerate) and a Retry
  // read fresh (X-Order-Fresh), never the server's cached order or a
  // prefetched response.
  const lastSummaryFetchRef = useRef({ id: null, erpOrderId: null, retryKey: 0 })
  const lastItemsFetchRef = useRef({ id: null, erpOrderId: null, retryKey: 0 })
  // Open notepad-icon notes windows — an array (not a single boolean/object)
  // since multiple can be open simultaneously, even for the same document
  // (reopening while one's already open just adds a second window; no dedupe
  // by erpOrderId). Each entry's position/size is uncontrolled (react-rnd's
  // own internal state) and deliberately NOT persisted across reloads.
  const [notesWindows, setNotesWindows] = useState([])
  const notesWindowCounterRef = useRef(0)
  function openNotesWindow(erpOrderId, title) {
    notesWindowCounterRef.current += 1
    setNotesWindows((prev) => [...prev, { key: `notes-${notesWindowCounterRef.current}`, erpOrderId, title }])
  }
  function closeNotesWindow(key) {
    setNotesWindows((prev) => prev.filter((w) => w.key !== key))
  }
  // Open eye-icon document-detail windows — every channel/trqClientCode as
  // of 2026-09-03 (previously 1AW/AMW only), same "array of independently-
  // draggable instances" convention as notesWindows above. `doc` is the
  // already-fetched relatedDocuments entry (Part 1's richer per-document
  // fields) — no new network call needed to open one, unlike notes windows
  // which fetch on open.
  const [docDetailWindows, setDocDetailWindows] = useState([])
  const docDetailWindowCounterRef = useRef(0)
  function openDocDetailWindow(doc) {
    docDetailWindowCounterRef.current += 1
    setDocDetailWindows((prev) => [...prev, { key: `doc-${docDetailWindowCounterRef.current}`, doc }])
  }
  function closeDocDetailWindow(key) {
    setDocDetailWindows((prev) => prev.filter((w) => w.key !== key))
  }
  const [translateError, setTranslateError] = useState(null)
  const [closingConversation, setClosingConversation] = useState(false)
  const [closeError, setCloseError] = useState(null)
  const [reopeningConversation, setReopeningConversation] = useState(false)
  const [reopenError, setReopenError] = useState(null)
  // Truncate the Tracking (Order Info) / Related Documents (Return/Case
  // Info) widget sections to a few rows by default, with a "View more"
  // expander — reset on conversation switch so an expanded state doesn't
  // leak into the next conversation opened.
  const [trackingExpanded, setTrackingExpanded] = useState(false)
  const [relatedDocsExpanded, setRelatedDocsExpanded] = useState(false)
  useEffect(() => { setTrackingExpanded(false); setRelatedDocsExpanded(false) }, [conversation?.id])
  useEffect(() => { setVinNoticeFor(null) }, [conversation?.id])
  // { owner: {username, displayName, assignedAt}, isOwner } from the last
  // assignment heartbeat response. assignmentRef mirrors it for reading
  // inside the heartbeat tick() closure below — tick() runs on a setInterval
  // and needs to compare each response against the PREVIOUS one to detect a
  // takeover; reading the `assignment` state variable directly there would
  // only ever see the value from when the effect was set up; the ref is
  // updated in lockstep with the state on every tick so it's always current.
  const [assignment, setAssignment] = useState(null)
  const assignmentRef = useRef(null)
  // The contact tab strip's timer (2026-10-21, contactTabs.js) reads the same
  // heartbeat state the header's timer does; no request of its own.
  useEffect(() => { onAssignmentState?.(assignment) }, [assignment, onAssignmentState])
  // A closed contact opens read-only (2026-10-21): from a related-contacts
  // banner, Other Messages or the Queue, opening it never claims it, so no
  // handle-time span starts and nobody else sees it as taken. Presence still
  // runs. Reopening it (status no longer closed) claims as before.
  const readOnlyClosed = conversation?.status === 'closed'
  // conversationId -> username last shown to THIS viewer as the owner in the
  // 'assigned-to-other' popup. Unlike assignmentRef (reset to null on every
  // conversation switch, in the heartbeat effect's cleanup below), this map
  // is declared once and never reset — it persists for Thread's whole
  // lifetime (Thread itself never unmounts across a session). Without this,
  // switching away from a still-owned-by-someone-else conversation and back
  // re-initializes assignmentRef to null, making the popup fire again for the
  // exact same, unchanged ownership — a normal multi-contact workflow, not a
  // new fact worth re-notifying about.
  const lastShownOwnerRef = useRef(new Map())
  // 'assigned-to-other' | 'taken-over', plus whose name to show.
  const [assignmentModal, setAssignmentModal] = useState(null)
  // Parking (Lab-only, 2026-10-02). parkedRef: set the moment this tab's own
  // owner parks it, so closing the tab right after does NOT release the
  // claim (a parked contact keeps its owner) — see the heartbeat cleanup.
  const parkedRef = useRef(false)
  // Park saves the work (2026-10-27, workDraft.js): the suggestion panel
  // fills this with a function returning the CSR's work in progress, sent
  // with the park in one request.
  const workCaptureRef = useRef(null)
  const [parkDialog, setParkDialog] = useState(null) // { note, saving, error }
  // The note field takes focus once the dialog has opened (the Dialog's own
  // focus handling would otherwise win over autoFocus).
  const parkNoteRef = useRef(null)
  // Whether the "View Fit List" modal is open — the OLD Vehicle Info
  // widget's own modal (conversation-wide fitmentCheck, non-testing-period
  // conversations only). The NEW Items widget's per-row fitment (1AW/AMW)
  // now has its own always-visible, filterable Fit List inline in
  // FitmentDetailModal instead of sharing this one — fitListEntries is
  // never set to anything but null anymore; kept (rather than simplified
  // away) since this modal still needs a real distinction between "no
  // per-row entries were ever set" (falls back to
  // conversation.fitmentCheck.allFitEntries below) in case that changes.
  const [fitListOpen, setFitListOpen] = useState(false)
  const [fitListEntries, setFitListEntries] = useState(null)
  // Year/Make/Model filter inputs for the "View Fit List" modal — client-side
  // only, filtering the resolved entries as the user types.
  const [fitListYearFilter, setFitListYearFilter] = useState('')
  const [fitListMakeFilter, setFitListMakeFilter] = useState('')
  const [fitListModelFilter, setFitListModelFilter] = useState('')
  // Shared by both close paths (X/backdrop via onClose, and the Close
  // button) — clears the filters so the next open starts fresh rather than
  // showing whatever was left over from the last time it was open.
  function closeFitList() {
    setFitListOpen(false)
    setFitListEntries(null)
    setFitListYearFilter('')
    setFitListMakeFilter('')
    setFitListModelFilter('')
  }
  // "View Full Fit List" (Vehicle Info widget, hasVariants case) — pre-fills
  // the same modal's Year/Make/Model filters from the vehicle CXHub already
  // resolved for this conversation, rather than opening it blank the way the
  // no-fit case's unfilled "View Fit List" link does.
  function openFitListPrefilled() {
    setFitListYearFilter(String(conversation.fitmentCheck?.vehicleYear ?? ''))
    setFitListMakeFilter(conversation.fitmentCheck?.vehicleMake ?? '')
    setFitListModelFilter(conversation.fitmentCheck?.vehicleModel ?? '')
    setFitListOpen(true)
  }
  // Whether the "Kit Includes" modal (Item Links widget) is open.
  const [kitIncludesOpen, setKitIncludesOpen] = useState(false)
  // Whether the "Part Details" modal is open, and which data it's showing —
  // shared by the OLD Item Links widget (conversation-wide conversation.itemInfo*,
  // qualifiersOpen true, partDetailsRow null) AND the NEW Items widget's
  // per-row version (partDetailsRow set to that row's own data) — mutually
  // exclusive per conversation (isTestingPeriodTrqRole), same reasoning as
  // the Fit List modal above.
  const [qualifiersOpen, setQualifiersOpen] = useState(false)
  const [partDetailsRow, setPartDetailsRow] = useState(null)
  function closePartDetails() {
    setQualifiersOpen(false)
    setPartDetailsRow(null)
  }
  // Whether the per-row "Fitment" modal (Items widget) is open, and which
  // row's own fitment it's showing — same DraggableModal-per-row-state
  // convention as partDetailsRow above, replacing the row's own inline
  // expansion.
  const [fitDetailRow, setFitDetailRow] = useState(null)
  // Whether the "All Items" modal (Items widget's expand button) is open.
  const [itemsModalOpen, setItemsModalOpen] = useState(false)
  // Whether the "Dropship PO Details" modal (Order Info widget) is open.
  const [dropshipPoOpen, setDropshipPoOpen] = useState(false)
  // Whether the VIN decode results modal (Vehicle Info widget) is open.
  const [vinDecodeModalOpen, setVinDecodeModalOpen] = useState(false)

  // "eBay Listing" comes from one of two sources depending on referenceType —
  // either the referenceId itself (referenceType === 'LISTING') or the itemId
  // parsed from a FROM_EBAY system message body. At most one is ever present
  // (parseEbaySystemRefs skips itemId when referenceType is already LISTING).
  // Computed here (ahead of the loading/no-conversation early returns below,
  // with null-safe access) rather than down in the render body, since
  // otherSystemRefs below also depends on ebaySystemRefs — every hook must
  // run unconditionally on every render, same reason the heartbeat effect
  // below already guards on conversation?.id instead of conversation.id.
  const ebaySystemRefs = conversation ? parseEbaySystemRefs(conversation) : []
  const listingRef = ebaySystemRefs.find((r) => r.label === 'eBay Listing')
  const listingValue = conversation?.referenceType === 'LISTING'
    ? conversation.referenceId
    : (listingRef?.value ?? null)

  // Flat, ordered list of every image across the whole conversation (message
  // order, then media order within a message) — gives the lightbox a stable
  // global index to step "next/previous" through, independent of which
  // message a thumbnail happens to belong to.
  const conversationImages = useMemo(() => {
    return (conversation?.messages ?? []).flatMap((m) =>
      (m.metadata?.media ?? [])
        .filter((media) => media.url && media.type === 'IMAGE')
        .map((media) => ({ url: media.url, name: media.name || 'attachment', messageId: m.id }))
    )
  }, [conversation?.messages])
  // null = closed, otherwise the index into conversationImages currently shown.
  const [lightboxIndex, setLightboxIndex] = useState(null)
  // An email image attachment in the same photo viewer (2026-10-27): its
  // original bytes through the authenticated download, as an object URL,
  // revoked when the viewer closes. { images: [{ url, name }], index } or null.
  const [attachmentLightbox, setAttachmentLightbox] = useState(null)
  const closeAttachmentLightbox = useCallback(() => {
    setAttachmentLightbox((prev) => {
      for (const img of prev?.images ?? []) URL.revokeObjectURL(img.url)
      return null
    })
  }, [])
  const identityImageUrl = useCallback((url) => url, [])
  const openAttachmentImage = useCallback((conversationId, att) => {
    fetchAttachmentObjectUrl(conversationId, att.id)
      .then((url) => setAttachmentLightbox({ images: [{ url, name: att.name }], index: 0 }))
      .catch((err) => window.alert(`Couldn't open attachment: ${err.message}`))
  }, [])

  // Inline email images (<img src="cid:...">): fetched with the bearer token
  // and turned into object URLs, keyed by message id then Content-ID, so the
  // body render can swap each cid: reference for its image (inlineImages.js).
  // Loaded per conversation and revoked when it changes. A failed fetch leaves
  // that image as the broken reference it was, and the paperclip link below
  // the message still downloads it.
  const [inlineImageUrls, setInlineImageUrls] = useState({})
  useEffect(() => {
    const convId = conversation?.id
    const wanted = (conversation?.messages ?? []).flatMap((m) =>
      inlineImageAttachments(m).map((a) => ({ messageId: m.id, attachmentId: a.id, contentId: a.contentId })))
    if (!convId || !wanted.length) { setInlineImageUrls({}); return }
    let alive = true
    const created = []
    Promise.all(wanted.map((w) => fetchAttachmentObjectUrl(convId, w.attachmentId)
      .then((url) => { created.push(url); return { ...w, url } })
      .catch(() => null)))
      .then((results) => {
        if (!alive) {
          for (const url of created) URL.revokeObjectURL(url)
          return
        }
        const next = {}
        for (const r of results) {
          if (!r) continue
          next[r.messageId] = { ...(next[r.messageId] ?? {}), [r.contentId]: r.url }
        }
        setInlineImageUrls(next)
      })
    return () => {
      alive = false
      for (const url of created) URL.revokeObjectURL(url)
    }
  }, [conversation?.id, conversation?.messages])

  // Heartbeat: register presence and refresh the viewer list every 30 s.
  // Piggybacks the assignment (soft-lock) heartbeat on the SAME tick/interval
  // — no separate timer. Claims the conversation on first open if unassigned/
  // stale; on every later tick, compares the response against the PREVIOUS
  // assignment (via assignmentRef, not the `assignment` state — see its own
  // comment) to detect either "someone else already has this" (first tick,
  // not the owner) or "we've just been displaced" (was the owner, now isn't)
  // and surfaces the matching modal. Releases the assignment on cleanup
  // (conversation switch or unmount) if we're still the owner at that point —
  // fire-and-forget, doesn't block teardown on the network call.
  useEffect(() => {
    if (!conversation?.id) return
    let alive = true
    parkedRef.current = false
    // The FIRST heartbeat of this open says so (opening): that one, and only
    // that one, unparks the owner's own parked contact — never a later 30s
    // tick, which may already be in flight when the owner parks it.
    let opening = true
    const tick = () => {
      pingPresence(conversation.id)
      fetchPresence(conversation.id).then((v) => { if (alive) setViewers(v) })

      const isOpening = opening
      opening = false
      if (readOnlyClosed) return
      heartbeatAssignment(conversation.id, { opening: isOpening }).then((result) => {
        if (!result) return
        if (result.unparked) onParkChanged?.()
        if (!alive) {
          // The conversation was switched away from (or unmounted) while this
          // heartbeat was in flight — the cleanup below already ran and
          // released whatever assignmentRef held AT THAT TIME. If this
          // response is the one that claimed/renewed ownership, it lands
          // after cleanup and never gets released, leaving a phantom lock
          // for up to the full assignment TTL.
          if (result.isOwner && !parkedRef.current) releaseAssignment(conversation.id)
          return
        }
        // Queued for automation (2026-10-13): the claim was refused, not held
        // by someone else, so no "Conversation Assigned" dialog; the panel's
        // "Queued for automation" banner says what is happening, and an
        // admin can Take over.
        if (result.automationPending) {
          assignmentRef.current = result
          setAssignment(result)
          return
        }
        const prev = assignmentRef.current
        const lastShown = lastShownOwnerRef.current.get(conversation.id)
        if (!result.isOwner && result.owner.username !== lastShown) {
          // Parked by its owner: the same popup, in its parked variant.
          setAssignmentModal(result.park
            ? { type: 'parked', ownerName: result.park.agentDisplayName ?? result.owner.displayName, parkedAt: result.park.parkedAt, note: result.park.note ?? null }
            : { type: 'assigned-to-other', ownerName: result.owner.displayName })
          lastShownOwnerRef.current.set(conversation.id, result.owner.username)
        } else if (prev?.isOwner && !result.isOwner) {
          setAssignmentModal({ type: 'taken-over', ownerName: result.owner.displayName })
          lastShownOwnerRef.current.set(conversation.id, result.owner.username)
        }
        if (result.isOwner) lastShownOwnerRef.current.delete(conversation.id)
        // Parked while this tab was open (2026-10-08: Re-Park from the
        // re-queue notice in another tab, after View in new tab): the claim
        // belongs to the park now, so this tab's cleanup keeps it, exactly
        // as when the owner parks from this tab's own header.
        if (result.isOwner && result.park) parkedRef.current = true
        assignmentRef.current = result
        setAssignment(result)
        onInactivityPrompt?.(conversation.id, result.inactivityPrompt ?? null)
      })
    }
    tick()
    const id = setInterval(tick, 30000)
    return () => {
      alive = false
      clearInterval(id)
      // A tab closed because its owner just PARKED it keeps the claim.
      if (assignmentRef.current?.isOwner && !parkedRef.current) {
        releaseAssignment(conversation.id)
        onAssignmentChanged?.()
      }
      assignmentRef.current = null
      setAssignment(null)
      setAssignmentModal(null)
      onInactivityPrompt?.(conversation.id, null)
    }
  }, [conversation?.id, readOnlyClosed])

  async function handleTakeOverAssignment() {
    const wasParked = assignmentModal?.type === 'parked'
    const result = await takeoverAssignment(conversation.id)
    if (result) {
      assignmentRef.current = result
      setAssignment(result)
      onAssignmentChanged?.()
      if (wasParked) onParkChanged?.()
    }
    setAssignmentModal(null)
  }

  // Admin: end the park AND the owner's claim, so nobody owns it.
  async function handleReturnParkedToQueue() {
    try {
      await returnParkedToQueue(conversation.id)
      onAssignmentChanged?.()
      onParkChanged?.()
    } catch (err) {
      console.error('[parking] return to queue failed', err)
    }
    setAssignmentModal(null)
  }

  async function confirmPark() {
    setParkDialog((d) => ({ ...d, saving: true, error: null }))
    try {
      let work = null
      try { work = workCaptureRef.current?.() ?? null } catch { work = null }
      await parkConversation(conversation.id, parkDialog?.note ?? '', work)
      // Keep the claim when this tab closes (see the heartbeat cleanup).
      parkedRef.current = true
      setParkDialog(null)
      onParked?.(conversation.id)
    } catch (err) {
      setParkDialog((d) => ({ ...d, saving: false, error: 'Could not park this contact. It may no longer be yours, or its status changed.' }))
    }
  }

  // Reset translation + order summary state whenever the open conversation changes.
  useEffect(() => {
    setShowTranslation(false)
    setTranslationMap(null)
    setOrderSummary(null)
    setOrderSummaryLoading(false)
    setOrderSummaryError(false)
    setItemDetailsLoading(false)
    setFitLoading(false)
    setItemsSlow(null)
    setTranslateError(null)
  }, [conversation?.id])

  // Fetch order summary when the conversation has a TRQ erpOrderId. Two
  // INDEPENDENT requests, not sequential: order-summary/items is the one
  // genuinely slow, per-item piece (Item Info/stock/fitment per line item) —
  // firing it separately means the header's fast fields (address/totals/
  // payment/related documents/tracking/holds/eBay case-or-return status/etc.)
  // render as soon as the fast route resolves, rather than waiting on the
  // slow one. Since 2026-10-29 the fast route also carries the order's lines
  // (orderLines), so the Items widget shows its rows with it, and the slow
  // request runs in two halves (details, fitment) in their own effect below.
  //
  // fresh (2026-10-29): a different order on the same conversation (the
  // order picker, a regenerate) or a Retry reads the order fresh; otherwise a
  // request prefetched when the contact opened is taken (api.js). A response
  // for an order the contact no longer has (its erpOrderId differs) is
  // fetched again, fresh.
  useEffect(() => {
    // No order (2026-10-09, prod contact 5377188): a regenerate can leave the
    // contact with no erpOrderId while a fetch for the previous order was in
    // flight. That fetch is cancelled below, so its own finally never clears
    // the loading flags, and the previous order's data no longer matches. Both
    // used to hold the header on "Loading…" forever. Clear them here: the
    // widgets show their normal no-order state.
    if (!conversation?.erpOrderId) {
      setOrderSummaryLoading(false)
      setItemDetailsLoading(false)
      setFitLoading(false)
      setOrderSummaryError(false)
      setItemDetailsErrorFor(null)
      setOrderSummary(null)
      setItemsSlow(null)
      return
    }
    let cancelled = false
    // Each piece of order data remembers which order it was fetched for
    // (_for / itemsSlow.for), so a header widget can tell its data is still
    // the PREVIOUS order's after a switch and hide it until the new order's
    // arrives (see summarySwitching / orderRowsFor() below).
    const fetchedFor = conversation.erpOrderId
    const last = lastSummaryFetchRef.current
    const fresh = (last.id === conversation.id && (last.erpOrderId !== fetchedFor || last.retryKey !== orderSummaryRetryKey))
    lastSummaryFetchRef.current = { id: conversation.id, erpOrderId: fetchedFor, retryKey: orderSummaryRetryKey }
    // A Sales Hold's "Other Messages" list has its own request now
    // (2026-10-07), started once the header's order-summary settles so the
    // header never waits on it. Only the Other Messages button shows it loading.
    const salesHold = isSalesHoldAppliedSubject(conversation.subject)
    setSalesHoldOtherMessages(salesHold ? { for: conversation.id, loading: true, list: [] } : null)
    const loadSalesHoldOtherMessages = () => {
      if (!salesHold || cancelled) return
      fetchOrderSummaryOtherConversations(conversation.id)
        .then((data) => { if (!cancelled) setSalesHoldOtherMessages({ for: conversation.id, loading: false, list: data?.otherBuyerConversations ?? [] }) })
        .catch(() => { if (!cancelled) setSalesHoldOtherMessages({ for: conversation.id, loading: false, list: [] }) })
    }
    let summarySettled = false
    setOrderSummaryLoading(true)
    fetchOrderSummary(conversation.id, { fresh })
      .then((data) => (forOtherOrder(data, fetchedFor) ? fetchOrderSummary(conversation.id, { fresh: true }) : data))
      .then((data) => {
        if (cancelled) return
        setOrderSummary({ ...data, _for: fetchedFor })
        setOrderSummaryError(false)
        // Other Actions' Resend Order/Shipping Confirmation Email buttons
        // (a Shell-level sibling, not a descendant of this pane) prefill
        // from this same already-loaded customerEmail — see
        // handleOrderSummaryEmailChange below, rather than a second
        // order-summary fetch of their own.
        onOrderSummaryEmailChange?.(conversation.id, data?.customerEmail ?? null)
      })
      .catch(() => {
        if (cancelled) return
        // Preserve whatever order-summary data was already successfully
        // fetched (e.g. an earlier request, or the fast route's own
        // already-good response on a request that fails only on retry) —
        // a single failed fetch must never blank out data that already
        // rendered correctly. Surface the failure instead via
        // orderSummaryError, with a Retry control below.
        // Only THIS order's data is kept (2026-10-09): a previous order's would
        // otherwise stay "stale" with no fetch in flight.
        setOrderSummary((prev) => (prev?._for === fetchedFor ? prev : null))
        setOrderSummaryError(true)
      })
      .finally(() => { if (!cancelled) { summarySettled = true; setOrderSummaryLoading(false) } loadSalesHoldOtherMessages() })

    // The defensive cap (2026-10-09): a header fetch that hasn't settled in
    // 30 seconds stops its spinner and shows the existing error / Retry state.
    // A response that arrives later still fills the header in.
    const capTimer = setTimeout(() => {
      if (cancelled) return
      if (!summarySettled) {
        setOrderSummaryLoading(false)
        setOrderSummaryError(true)
        setOrderSummary((prev) => (prev?._for === fetchedFor ? prev : null))
      }
    }, HEADER_FETCH_CAP_MS)

    return () => { cancelled = true; clearTimeout(capTimer) }
  }, [conversation?.id, conversation?.erpOrderId, orderSummaryRetryKey, onOrderSummaryEmailChange])

  // The Items widget's slow fields (2026-10-29): GET /:id/order-summary/items
  // in two halves, started with the summary request above, not after it.
  // details (getItem, inventory: Stock, Tags, Part Details) and fitment (the
  // fitment check and its model comparison: Fit) each merge in when they
  // arrive (orderLines.js's mergeOrderRows()), so Fit fills in last without
  // holding up the rest. The summary's Retry reruns these too; the widget's
  // own Retry (itemsRetryKey) reruns only these.
  useEffect(() => {
    if (!conversation?.erpOrderId) return
    let cancelled = false
    const fetchedFor = conversation.erpOrderId
    const last = lastItemsFetchRef.current
    const retryKey = orderSummaryRetryKey * 100_000 + itemsRetryKey
    const fresh = (last.id === conversation.id && (last.erpOrderId !== fetchedFor || last.retryKey !== retryKey))
    lastItemsFetchRef.current = { id: conversation.id, erpOrderId: fetchedFor, retryKey }
    const settled = { details: false, fitment: false }
    setItemsSlow((prev) => (prev?.for === fetchedFor
      ? { ...prev, details: prev.details?.status === 'loaded' ? prev.details : null, fitment: prev.fitment?.status === 'loaded' ? prev.fitment : null }
      : { for: fetchedFor, details: null, fitment: null, lines: null }))
    setItemDetailsLoading(true)
    setFitLoading(true)
    setItemDetailsErrorFor(null)
    const half = (part) => {
      const setLoading = part === 'details' ? setItemDetailsLoading : setFitLoading
      fetchOrderSummaryItems(conversation.id, { part, fresh })
        .then((data) => (forOtherOrder(data, fetchedFor) ? fetchOrderSummaryItems(conversation.id, { part, fresh: true }) : data))
        .then((data) => {
          if (cancelled) return
          const got = itemsHalfFrom(data)
          setItemsSlow((prev) => {
            const base = prev?.for === fetchedFor ? prev : { for: fetchedFor, details: null, fitment: null, lines: null }
            return { ...base, [part]: { status: 'loaded', rows: got.rows }, lines: base.lines ?? got.lines }
          })
        })
        .catch(() => {
          if (cancelled) return
          setItemDetailsErrorFor(fetchedFor)
          setItemsSlow((prev) => {
            const base = prev?.for === fetchedFor ? prev : { for: fetchedFor, details: null, fitment: null, lines: null }
            return { ...base, [part]: { status: 'error', rows: [] } }
          })
        })
        .finally(() => { if (!cancelled) { settled[part] = true; setLoading(false) } })
    }
    half('details')
    half('fitment')

    // The same 30-second cap as the summary: a half still out by then shows
    // as failed ("—" and Retry); a later answer still fills it in.
    const capTimer = setTimeout(() => {
      if (cancelled) return
      for (const part of ['details', 'fitment']) {
        if (settled[part]) continue
        ;(part === 'details' ? setItemDetailsLoading : setFitLoading)(false)
        setItemDetailsErrorFor(fetchedFor)
        setItemsSlow((prev) => (prev?.for === fetchedFor && prev[part]?.status !== 'loaded' ? { ...prev, [part]: { status: 'error', rows: [] } } : prev))
      }
    }, HEADER_FETCH_CAP_MS)

    return () => { cancelled = true; clearTimeout(capTimer) }
  }, [conversation?.id, conversation?.erpOrderId, orderSummaryRetryKey, itemsRetryKey])

  // Drop back to messages view when effective role becomes CSR (e.g. "View as Agent" toggled on).
  useEffect(() => {
    if (!isAdmin) setView('messages')
    else if (requestedViewRef.current) { setView(requestedViewRef.current); requestedViewRef.current = null; clearBootThreadView() }
  }, [isAdmin])

  // Scroll to the bottom of the messages list whenever a new conversation is
  // opened. A plain one-shot scroll is enough for eBay (plain-text bodies,
  // known height synchronously) but not for M365 email: a body can carry
  // <img> tags pointing at remote URLs (sanitizeHtmlBody's DOMPurify config
  // doesn't strip normal http(s) img src) that load asynchronously AFTER this
  // initial scroll, growing the pane's scrollHeight afterward and leaving the
  // view short of the true bottom. Track the bottom via a ResizeObserver on
  // the messages CONTENT box (messagesContentRef) — the scrollable pane
  // itself (messagesPaneRef) has a fixed clientHeight set by its flex parent,
  // so watching IT reports nothing when only the overflowing content grows —
  // until the content's height settles (no resize for SETTLE_MS). Defer to
  // the user the moment they scroll manually — a real user scroll always
  // wins, even mid-load.
  useEffect(() => {
    const pane = messagesPaneRef.current
    const content = messagesContentRef.current
    const SETTLE_MS = 400
    let userScrolled = false
    let pendingProgrammaticScrolls = 0
    let settleTimer = null
    let resizeObserver = null

    const followBottom = () => {
      const before = pane ? pane.scrollTop : null
      messagesBottomRef.current?.scrollIntoView({ behavior: 'instant' })
      // Only credit this as "our own" scroll if it actually moved the pane —
      // a no-op call (already at bottom) fires no 'scroll' event to consume,
      // so crediting it anyway would risk swallowing a later real user scroll.
      if (pane && pane.scrollTop !== before) pendingProgrammaticScrolls += 1
    }

    followBottom()
    if (!pane || !content) return

    const handleScroll = () => {
      if (pendingProgrammaticScrolls > 0) { pendingProgrammaticScrolls -= 1; return }
      userScrolled = true
    }
    pane.addEventListener('scroll', handleScroll)

    const stopObserving = () => { resizeObserver?.disconnect() }
    const armSettleTimer = () => {
      if (settleTimer) clearTimeout(settleTimer)
      settleTimer = setTimeout(stopObserving, SETTLE_MS)
    }
    resizeObserver = new ResizeObserver(() => {
      if (userScrolled) return
      followBottom()
      armSettleTimer()
    })
    resizeObserver.observe(content)
    armSettleTimer()

    return () => {
      if (settleTimer) clearTimeout(settleTimer)
      resizeObserver?.disconnect()
      pane.removeEventListener('scroll', handleScroll)
    }
  }, [conversation?.id])

  if (loading) return <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
  if (!conversation) return <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>Select a conversation.</Box>

  const handleDecided = (conversationStatus, extra) => {
    setActivityKey((k) => k + 1)
    onDecided?.(conversationStatus, extra)
  }

  // Admin intent-override: the header intent chip itself is the dropdown
  // trigger for admins (see the Chip + Menu below) — picking an intent force-
  // regenerates the suggestion under it; "Use AI classification" (value ===
  // '') clears the override and regenerates normally. Sticky server-side —
  // see api.js's setIntentOverride().
  async function handleIntentOverrideChange(value) {
    setOverrideBusy(true); setOverrideError(null)
    try {
      await setIntentOverride(conversation.id, value === '' ? null : value)
      setActivityKey((k) => k + 1)
      onIntentOverrideChanged?.()
    } catch (e) {
      setOverrideError(e.message)
    } finally {
      setOverrideBusy(false)
    }
  }

  // Admin team override (2026-10-07): same shape as the intent override —
  // sticky, re-runs classification and generation; '' clears it back to the
  // account's own team.
  async function handleTeamOverrideChange(value) {
    setOverrideBusy(true); setOverrideError(null)
    try {
      await setTeamOverride(conversation.id, value === '' ? null : value)
      setActivityKey((k) => k + 1)
      onIntentOverrideChanged?.()
    } catch (e) {
      setOverrideError(e.message)
    } finally {
      setOverrideBusy(false)
    }
  }

  // Order-correction override — any csr, not just admin/senior (see api.js's
  // setErpOrderIdOverride()). Reuses onIntentOverrideChanged's own reload
  // signal: same three things need to refresh either way (the conversation
  // list chips, this conversation's own detail — which is what makes the
  // header/order-summary widget pick up the new order — and the suggestion
  // itself, for Proposed Steps).
  //
  // pendingErpOrderId: the just-picked value, set BEFORE the request starts
  // — the whole round-trip (order lookup + suggestion regeneration) runs
  // synchronously inside the server request, so there's no server-sent
  // progress; the only way to show the pick "sticking" immediately (rather
  // than the Select snapping back to the still-stale conversation.erpOrderId
  // until the response returns) is this client-side optimistic value.
  // Cleared once the request settles either way, handing back off to the
  // normal refreshDetail()-driven conversation.erpOrderId.
  // A VIN saved or cleared from the header (VinEdit.jsx). No regeneration:
  // the info line under the header offers it.
  function handleVinSaved(res) {
    setVinLocal({ base: conversationProp, fields: vinFieldsFromResponse(res) })
    setVinNoticeFor(conversation.id)
    setActivityKey((k) => k + 1)
    onGenerated?.()
  }

  async function handleErpOrderIdOverrideChange(erpOrderId) {
    setPendingErpOrderId(erpOrderId)
    setErpOrderOverrideBusy(true); setErpOrderOverrideError(null)
    try {
      await setErpOrderIdOverride(conversation.id, erpOrderId)
      setActivityKey((k) => k + 1)
      onIntentOverrideChanged?.()
    } catch (e) {
      setErpOrderOverrideError(e.message)
    } finally {
      setErpOrderOverrideBusy(false)
      setPendingErpOrderId(null)
    }
  }

  // Manually close a pending conversation whose reply/resolution happened
  // outside the normal decision flow (see api.js's closeConversation()) —
  // status-only, no suggestion decision involved. Reuses the same
  // reload/refresh path a suggestion decision triggers (handleDecided ->
  // onDecided) so the sidebar list and this header pick up the new status.
  async function handleCloseConversation() {
    setClosingConversation(true); setCloseError(null)
    try {
      await closeConversation(conversation.id)
      handleDecided()
    } catch (e) {
      setCloseError(e.message)
    } finally {
      setClosingConversation(false)
    }
  }

  // Admin only — reopen a conversation closed by mistake, back into
  // the queue. Status-only, same reload path as handleCloseConversation
  // above (no suggestion regenerated; see api.js's reopenConversation()).
  async function handleReopenConversation() {
    setReopeningConversation(true); setReopenError(null)
    try {
      await reopenConversation(conversation.id)
      handleDecided()
    } catch (e) {
      setReopenError(e.message)
    } finally {
      setReopeningConversation(false)
    }
  }

  async function handleTranslateToggle() {
    if (showTranslation) { setShowTranslation(false); return }
    if (translationMap !== null) { setShowTranslation(true); return }
    setTranslateError(null)
    setTranslating(true)
    try {
      const data = await fetchTranslations(conversation.id)
      setTranslationMap(data)
      setShowTranslation(true)
    } catch {
      setTranslateError('Translation unavailable — TRANSLATION_ENABLED may not be set in the environment')
    } finally { setTranslating(false) }
  }

  // Exclude the current user from the viewer list so you only see others.
  // currentUser is null until /api/me resolves — show nothing rather than an
  // unfiltered list in that window, to avoid a flash of seeing yourself.
  const otherViewers = currentUser == null ? [] : viewers.filter((v) => v.username !== currentUser)

  // Dedup pass: the same case/inquiry ID can otherwise show up more than once
  // in the header — the live order-summary lookup (orderSummary.
  // ebayCaseOrInquiry) is the status-bearing, most-authoritative source, so a
  // plain bare-ID display of the SAME value elsewhere is suppressed in favor
  // of it (rendered in the Return/Case Info widget).
  // A Sales Hold conversation's own customer.externalUserId is the meaningless
  // M365 sender address, not the buyer's real identity — its own
  // otherBuyerConversations (matched server-side on that address in GET /:id)
  // is therefore meaningless too. The real match, keyed on orderSummary's
  // TRQ-resolved email, only becomes known once the order-summary fetch
  // resolves (see routes/conversations.js's order-summary route) — until
  // then, treat it as empty (no button) rather than show the wrong list.
  // Its list now has its own request (GET /:id/order-summary/other-conversations,
  // 2026-10-07), loaded after the header, never blocking it.
  // No order yet: nothing to look up, so no list (never the sender-keyed one).
  const salesHoldOther = salesHoldOtherMessages?.for === conversation.id
    ? salesHoldOtherMessages
    : { loading: Boolean(conversation.erpOrderId), list: [] }
  const { list: effectiveOtherBuyerConversations, loading: otherMessagesLoading } = otherMessagesFor({
    isSalesHold: isSalesHoldAppliedSubject(conversation.subject),
    conversationList: conversation.otherBuyerConversations,
    salesHold: salesHoldOther,
  })
  const otherMessagesButton = otherMessagesButtonState({ list: effectiveOtherBuyerConversations, loading: otherMessagesLoading })
  const liveCaseOrInquiryId = orderSummary?.ebayCaseOrInquiry?.id != null ? String(orderSummary.ebayCaseOrInquiry.id) : null
  const isDupOfLiveCaseOrInquiry = (val) => liveCaseOrInquiryId != null && val != null && String(val) === liveCaseOrInquiryId
  const ebayCaseIdIsDup = isDupOfLiveCaseOrInquiry(conversation.ebayCaseId)
  // One "eBay Return" field (2026-11-02, headerEbayReturn.js): the order
  // summary's ebayReturnFound, searched or a known return read by its id, so
  // the field never depends on which suggestion is latest. A RETURN-type
  // thread's own reference field carries it when it is the same return.
  const headerReturn = headerEbayReturn({ conversation, orderSummary })
  const foundReturnId = headerReturn.found?.id ?? null
  const otherSystemRefs = ebaySystemRefs
    .filter((r) => r.label !== 'eBay Listing')
    .filter((r) => !isDupOfLiveCaseOrInquiry(r.value))
    .filter((r) => !(r.label === 'Return ID' && [conversation.returnId, foundReturnId, conversation.referenceType === 'RETURN' ? conversation.referenceId : null].some((v) => v != null && String(v) === String(r.value))))
  // otherSystemRefs' possible labels (eBay Listing already filtered out above)
  // are 'eBay Order' (order-related) or 'Return ID'/'Case #'/'INR Request'
  // (return/case-related) — split them into the two widgets they route to.
  const orderSystemRefs = otherSystemRefs.filter((r) => r.label === 'eBay Order')
  const caseSystemRefs = otherSystemRefs.filter((r) => r.label !== 'eBay Order')

  // TESTING-PERIOD ONLY (see trq-role-override.js's own TEMPORARY posture),
  // scoped ENTIRELY to trqClientCode 1AW/AMW per Jenn's explicit instruction:
  // when it's anything else (1AE/AME included), none of this feature's
  // changes apply at all — the old Item Info/Item Links + Vehicle Info
  // widgets and the Order Info widget's own Tracking block render exactly as
  // they did before this feature existed. This can only be true for
  // m365_email (a real eBay 1AE/AME conversation never resolves to 1AW/AMW),
  // so no separate channel check is needed.
  // A task contact (2026-10-13) takes its order's header (headerChannelFor(),
  // shared with the order-summary routes): a Website order this one, an eBay
  // order the eBay layout (isNewEbayLayout()), an unrouted task neither.
  // Walmart Marketplace (WMT, 2026-10-14) gets the same Website header.
  const headerChannel = headerChannelFor(conversation)
  const isTestingPeriodTrqRole = (conversation.trqClientCode === '1AW' || conversation.trqClientCode === 'AMW' || conversation.trqClientCode === 'WMT')
    && (conversation.channel !== 'task' || headerChannel === 'm365_email')
  // Sales Hold conversations whose client is eBay (1AE/AME) but channel is
  // m365_email — same "channel === m365_email + Sales Hold subject" signal
  // already used above for the Customer Email field, now also excluding
  // 1AW/AMW so it only fires for the eBay client. Gates two header additions
  // below (eBay Buyer ID, the billing/shipping address widget) that are
  // already available in orderSummary but not shown in this specific
  // channel/client combination.
  const isSalesHoldEbayEmail = conversation.channel === 'm365_email'
    && !isTestingPeriodTrqRole
    && isSalesHoldAppliedSubject(conversation.subject)
  // Any m365_email conversation whose CLIENT resolves to eBay (1AE/AME) —
  // not just Sales Hold ones. repositories/conversations.js already
  // overrides conversation.client to 'eBay' for these regardless of channel;
  // this just reuses that signal for the Return-Case Info widget below,
  // which otherwise only shows eBay-specific fields for the plain eBay
  // channel, wrongly treating these like genuine Website (1AW/AMW) email
  // orders (which deliberately get no eBay treatment).
  const isEbayClientEmail = isEbayClientEmailConversation(conversation)
  // The automated_return_notice's own parsed fields (buyerId, returnId, etc.
  // — server/src/channels/m365/automated-return-notice.js's
  // parseAutomatedReturnNotice(), surfaced by mapper.js) live on the
  // ORIGINATING message's own metadata, not conversation.metadata itself —
  // found once here for header display. Mutually exclusive with
  // isSalesHoldEbayEmail (different subjects), so gating on this directly
  // (rather than the broader isEbayClientEmail) can't double-render the
  // eBay Buyer ID field below.
  const automatedReturnNotice = conversation.messages?.find((m) => m.metadata?.automatedReturnNotice)?.metadata?.automatedReturnNotice ?? null
  // The eBay layout (Lab-only experiment, 2026-10-07): the eBay channel or an
  // eBay-client email gets the Website header's look with its own eBay data
  // (ebayHeaderLayout.js). Website (1AW / AMW) never does.
  const ebayLayout = isNewEbayLayout(conversation)
  const hasOrderInfoWidget = Boolean(conversation.erpOrderId)
    || Boolean(orderSummary?.orderDate)
    || Boolean(conversation.customer?.externalUserId)
    || Boolean(conversation.customer?.email)
    || Boolean(orderSummary?.holds?.length)
    || (!isTestingPeriodTrqRole && !ebayLayout && Boolean(orderSummary?.trackingSummary?.length))
    || Boolean(conversation.referenceId && conversation.referenceType === 'ORDER')
    || orderSystemRefs.length > 0
  // Switching orders (2026-10-01): from the moment an order is picked
  // (erpOrderOverrideBusy) until each widget's OWN data for the newly selected
  // order has arrived, every header widget hides the previous order's content
  // and shows a spinner. A piece of data is "stale" when it was fetched for a
  // different order than the one now selected (_for / _itemsFor, set by the
  // fetch effect). Stale with no fetch in flight (the fetch failed) shows
  // nothing rather than the old order — the error banner above offers Retry.
  const summaryStale = Boolean(orderSummary?._for && orderSummary._for !== conversation.erpOrderId)
  const itemsStale = Boolean(itemsSlow?.for && itemsSlow.for !== conversation.erpOrderId)
  // Stale data spins only while a fetch is in flight (2026-10-09): with no
  // fetch the widget shows its normal state, never a spinner.
  const summarySwitching = erpOrderOverrideBusy || (summaryStale && orderSummaryLoading)
  const itemsSwitching = erpOrderOverrideBusy || (itemsStale && itemDetailsLoading)
  const summarySwitchingLoading = erpOrderOverrideBusy || orderSummaryLoading
  const itemsSwitchingLoading = erpOrderOverrideBusy || itemDetailsLoading
  // orderTax/shippingCost/orderTotal/orderBatch/status render above the
  // Related Documents heading inside this SAME widget — included here too so
  // the widget doesn't stay hidden when one of those is the only populated
  // field.
  const hasOrderTotalsBlock = orderSummary?.orderTax != null || orderSummary?.shippingCost != null
    || orderSummary?.orderTotal != null || orderSummary?.orderBatch != null || orderSummary?.status != null
    || orderSummary?.paymentId != null || orderSummary?.paymentMethodDisplay != null
  // Every trigger here EXCEPT relatedDocuments/hasOrderTotalsBlock (TRQ's own
  // order data, not eBay-specific) is eBay-only (Cancel/Case/Return IDs, the
  // live eBay case/return search, the RETURN/CANCELLATION reference type,
  // caseSystemRefs) — gated out for a Website (1AW/AMW) m365_email
  // conversation so the widget doesn't appear just because stale/inherited
  // data happens to be present, while still showing for a genuinely useful
  // TRQ related-document list. isEbayClientEmail (an eBay-CLIENT conversation
  // over m365_email, e.g. Sales Hold Applied or an Automated Return Notice)
  // gets the SAME full trigger list the plain eBay channel does — this is a
  // genuine eBay order, not a Website one, regardless of transport.
  const hasReturnCaseInfoWidget = (headerChannel === 'm365_email' && !isEbayClientEmail)
    ? Boolean(orderSummary?.relatedDocuments) || hasOrderTotalsBlock || Boolean(orderSummary?.returnLabelTracking?.length)
    : Boolean(conversation.ebayCancelId)
      || Boolean(conversation.ebayCaseId && !ebayCaseIdIsDup)
      || Boolean(orderSummary?.ebayCaseOrInquiry)
      || foundReturnId != null
      || Boolean(orderSummary?.relatedDocuments)
      || hasOrderTotalsBlock
      || Boolean(conversation.referenceId && (conversation.referenceType === 'RETURN' || conversation.referenceType === 'CANCELLATION'))
      || caseSystemRefs.length > 0
      || Boolean(orderSummary?.returnLabelTracking?.length)
  // conversation.itemInfo*/fitmentCheck are resolved independent of any TRQ
  // order (lookupItemInfoSku()/groundFitment(), off the listing/classifier/
  // message text) — when no order was ever found, this is the only item data
  // there is, so the Items widget falls back to it as a single synthetic row.
  const hasNoOrderItemInfo = isTestingPeriodTrqRole && !conversation.erpOrderId && Boolean(conversation.itemInfoSku)
  // The Items widget's rows (2026-10-29, orderLines.js): the order's lines
  // from the summary, merged with the slow fields as each half arrives. Only
  // ever this order's (orderRowsFor() checks _for / itemsSlow.for), so a
  // switch never shows the previous order's rows. null until the rows are in.
  const orderRows = orderRowsFor(conversation.erpOrderId, orderSummary, itemsSlow)
  const hasItemsWidget = (isTestingPeriodTrqRole && (Boolean(conversation.vin) || Boolean(orderRows?.length) || hasNoOrderItemInfo || itemsSwitching || summarySwitching))
    || (ebayLayout && (Boolean(conversation.vin) || Boolean(orderRows?.length) || Boolean(orderSummary?.vehicleInfo) || Boolean(conversation.messageVehicleInfo) || itemsSwitching || summarySwitching))
  // Computed once here so the button-visibility check and both table renders
  // (capped inline view + "All Items" modal) share the same grouping instead
  // of each recomputing groupItemsForTable() independently.
  const itemsGrouped = orderRows?.length > 0 ? groupItemsForTable(orderRows) : []
  // The slow fields still on their way, and a half that failed (2026-10-29):
  // a caption, or "didn't load" with a Retry that reruns only the items
  // requests. The rows stay either way.
  const slowLoading = itemsGrouped.length > 0 && (itemDetailsLoading || fitLoading)
  const slowFailed = itemsGrouped.length > 0 && !slowLoading && itemDetailsErrorFor === conversation.erpOrderId
  // AM Website only — groundOneItem() (enrichment.js) only ever populates
  // item.amSku for an AM order; gating here too keeps a 1A conversation's
  // "All Items" modal exactly as it was before this column existed.
  const showAmSku = conversation.companyCode === 'AM' && conversation.client === 'Website'
  // No true per-order "description" exists pre-order (that column normally
  // comes from the TRQ order line item) — itemInfoEbayTitleAM (the eBay
  // listing title) is the closest substitute. No dropship value either —
  // dropship detection needs order-level TRQ attributes that don't exist
  // pre-order, so that tag just doesn't show here (unknown, not "confirmed
  // not dropship").
  const noOrderItem = hasNoOrderItemInfo ? {
    sku: conversation.itemInfoSku,
    description: conversation.itemInfoEbayTitleAM ?? null,
    unitPrice: conversation.itemInfoPriceWebsite,
    tracking: [],
    quantityAvailable: conversation.itemInfoQuantityAvailable,
    electricalPart: conversation.itemInfoElectricalPart,
    warranty: conversation.itemInfoWarranty,
    kitIncludes: conversation.itemInfoKitIncludes,
    qualifiers: conversation.itemInfoQualifiers,
    companySpecifications: conversation.itemInfoCompanySpecifications,
    itemSpecifications: conversation.itemInfoItemSpecifications,
    ebayTitleAM: conversation.itemInfoEbayTitleAM,
    itemType: 'standalone',
    fitment: conversation.fitmentCheck?.checked ? conversation.fitmentCheck : null,
  } : null
  // Widget 3's header: the deduped set of VINs/vehicles actually resolved
  // per-item (item.vehicle, from enrichment.js's per-item fitment check —
  // see groundPerItemDetails()) across every row, not just the single
  // conversation-wide VIN. conversation.vin is a fallback ONLY — shown when
  // no line item produced its own vehicle.vin (e.g. the order's items carry
  // no vehicle_vin attribute at all, or there's no order yet).
  const itemVinsFromOrder = [...new Set(itemsGrouped.map((it) => it.vehicle?.vin).filter(Boolean))]
  const itemVins = itemVinsFromOrder.length > 0 ? itemVinsFromOrder : (conversation.vin ? [conversation.vin] : [])
  const itemVehicleLabels = [...new Set(itemsGrouped.map((it) => it.vehicle?.label).filter(Boolean))]
  // The synthetic no-order row (noOrderItem above) has no real per-item
  // vehicle at all (nothing from a TRQ order to read vehicle_ymm/etc off
  // of) — fall back to the same conversation.fitmentCheck vehicleYear/Make/
  // Model join the OLD "Customer Vehicle" no-order widget already uses.
  const noOrderVehicleLabel = hasNoOrderItemInfo && itemVehicleLabels.length === 0
    ? ([conversation.fitmentCheck?.vehicleYear, conversation.fitmentCheck?.vehicleMake, conversation.fitmentCheck?.vehicleModel].filter(Boolean).join(' ') || null)
    : null
  const vehicleLabelsToShow = itemVehicleLabels.length > 0 ? itemVehicleLabels : (noOrderVehicleLabel ? [noOrderVehicleLabel] : [])
  // The eBay layout's Items header reads the same vehicle and VIN sources
  // the old eBay Vehicle Info box did: the order's vehicle, else the
  // customer's, and the conversation's VIN.
  // An agent's VIN override (2026-10-21) is the contact's VIN in every
  // layout, ahead of the order items' own VINs.
  const headerVins = (ebayLayout || conversation.vinOverride) ? (conversation.vin ? [conversation.vin] : []) : itemVins
  // The vehicle the override's decode names, shown beside it.
  const overrideVehicle = conversation.vinOverride ? decodedVehicleText(conversation.vinDecodeResult) : null
  // The Items header always offers the VIN edit ("+ Add VIN" with no VIN).
  const vinEditable = Boolean(conversation.id)
  const headerVehicleLabels = ebayLayout
    ? (orderSummary?.vehicleInfo ? [orderSummary.vehicleInfo] : [conversation.messageVehicleInfo, conversation.messageVehicleDetails].filter(Boolean))
    : vehicleLabelsToShow
  // Replaced by the Items widget under the eBay layout.
  const hasItemLinksWidget = !isTestingPeriodTrqRole && !ebayLayout && (Boolean(listingValue) || Boolean(conversation.itemInfoSku))
  const hasVehicleInfoWidget = !isTestingPeriodTrqRole && !ebayLayout
    && (Boolean(orderSummary?.vehicleInfo) || Boolean(conversation.vin) || Boolean(conversation.messageVehicleInfo) || Boolean(conversation.fitmentCheck?.checked))
  // Widget 2 (order totals/Related Documents/eBay case data) is entirely
  // order-bound and already correctly renders nothing when there's no
  // order — this is what appears in its place instead: vehicle info if the
  // customer provided any, same source/fallback order the old (now
  // testing-period-hidden) Vehicle Info widget used. orderSummary.vehicleInfo
  // doesn't apply here (a TRQ order attribute), so only the message-derived
  // fallback is ever used.
  const statusTotal = ebayLayout ? orderStatusTotal(orderSummary) : null
  // The Items widget's rows come with the order summary (2026-10-29), so the
  // widget waits on the summary while switching orders, not on the slow
  // fields (those show per cell).
  const itemsWidgetSwitching = summarySwitching
  const itemsWidgetSwitchingLoading = summarySwitchingLoading
  const hasNoOrderVehicleInfo = isTestingPeriodTrqRole && !conversation.erpOrderId
    && Boolean(conversation.messageVehicleInfo || conversation.fitmentCheck?.checked)

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>

      {/* Row 1 + the info widgets row, wrapped together in one draggable-height
          area (persists to localStorage, same pattern as the column resize
          handles) — scrolls internally rather than clipping if dragged
          shorter than its content needs. */}
      <Box ref={headerRef} data-testid="header-area" sx={{ height: 'auto', maxHeight: headerHeight ?? undefined, flexShrink: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

      {/* ── Row 1: Status & navigation ──────────────────────────────────── */}
      <Box sx={{ px: 2, py: 0.125, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        {/* How long the viewing agent has held this contact's claim (own
            heartbeat state, already polled every ~30s for the "Take Over
            Contact" flow above) — hidden once released/taken over or when
            someone else owns it. Also gated on status === 'open': the
            heartbeat still claims a closed/escalated/waiting contact just by
            opening it (the soft-lock has no status check of its own), but
            "time on this contact" should only read as a queue/working clock,
            never tick on a contact that's already done or parked. See
            ActiveContactTimer.jsx. */}
        <ActiveContactTimer
          assignedAt={(conversation.status === 'open' && assignment?.isOwner) ? assignment.owner.assignedAt : null}
          parkedMs={assignment?.owner?.parkedMs ?? 0}
          sx={{ height: 20, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }}
        />
        {/* Park (Lab-only, 2026-10-02): only for the current owner, on eBay or
            M365 email, while open/pending/escalated. Patrick's active-contact
            timer (ui/src/ActiveContactTimer.jsx) sits right beside it; it only
            runs while the contact is open, so parked time never ticks there. The most recent
            park's note sits next to it for anyone who opens the contact,
            unparked or not. */}
        {parkingEnabled && canPark({ parkingEnabled, conversation, isOwner: assignment?.isOwner === true }) && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setParkDialog({ note: '', saving: false, error: null })}
            sx={{ fontSize: '0.75rem', py: 0, px: 1, minWidth: 0, height: 22 }}
          >
            Park
          </Button>
        )}
        {parkingEnabled && latestPark(conversation.parks) && (
          <Typography variant="caption" color="text.secondary" data-testid="latest-park-note" sx={{ lineHeight: 1.2, maxWidth: 420 }} noWrap title={parkNoteLine(latestPark(conversation.parks))}>
            {parkNoteLine(latestPark(conversation.parks))}
          </Typography>
        )}
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, fontWeight: 700 }}>ID:</Typography>
          <Tooltip title={`Channel ID: ${conversation.externalConversationId ?? conversation.id}`}>
            <Typography variant="caption" sx={{ lineHeight: 1.2 }} noWrap>
              {conversation.convNumber ?? conversation.externalConversationId ?? conversation.id}
            </Typography>
          </Tooltip>
          <CopyButton value={conversation.convNumber ?? conversation.externalConversationId ?? conversation.id} />
        </Box>
        <Tooltip title={
          conversation.status !== 'waiting' ? ''
            : conversation.waitingReason === 'automation'
              ? 'Queued for Flows automation to run once its delay has passed'
              : 'Order data not yet available to complete automation, will check every 5 minutes'
        }>
          <Chip
            data-testid="conversation-status"
            size="small"
            label={conversation.status}
            color={STATUS_COLOR[conversation.status] ?? 'default'}
            sx={{ height: 20, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }}
          />
        </Tooltip>
        {/* An internal email between CXHub mailboxes (2026-10-12): the
            "customer" here is another of our mailboxes, so the agent can
            tell at a glance this isn't a customer contact. */}
        {conversation.isInternalEmail && (
          <Tooltip title={`An internal email to ${conversation.metadata?.internalEmail?.toAccount ?? 'another CXHub mailbox'}, not a customer contact. Their reply arrives here.`}>
            <Chip
              data-testid="internal-email-chip"
              size="small"
              variant="outlined"
              label={`Internal · to ${teamLabel(conversation.metadata?.internalEmail?.toTeam ?? 'support')}`}
              sx={{ fontWeight: 700, fontSize: '0.75rem', height: 20, '& .MuiChip-label': { px: 0.75 } }}
            />
          </Tooltip>
        )}
        {/* A customer's reply to a task's email response (Tasks Phase 1,
            2026-10-13): its own contact, linked back to the task. */}
        {replyToTask(conversation) && (
          <Link component="button" type="button" variant="body2" data-testid="reply-to-task-link"
            onClick={() => onOpenConversationTab?.(replyToTask(conversation).conversationId)}
            sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
            Reply to task {taskIdLabel(replyToTask(conversation).taskId)}
          </Link>
        )}
        {/* Team (2026-10-07) rides on the client chip rather than a chip of
            its own, so the header row keeps its width: " · Support" is
            appended for team Support, and for an admin the chip opens the
            team override menu (outlined while overridden). */}
        <Tooltip title={!isAdmin ? '' : conversation.teamOverride
          ? `Team: ${teamLabel(effectiveTeam(conversation))}, manually overridden${conversation.teamOverrideSetBy ? ` by ${conversation.teamOverrideSetBy}` : ''}${conversation.teamOverrideSetAt ? ` on ${new Date(conversation.teamOverrideSetAt).toLocaleString()}` : ''}. Click to change.`
          : `Team: ${teamLabel(effectiveTeam(conversation))}. Click to move this contact to another team and regenerate the suggestion.`}>
          <span>
            <Chip
              data-testid="team-chip"
              size="small"
              variant={conversation.teamOverride ? 'outlined' : 'filled'}
              clickable={isAdmin}
              disabled={isAdmin && overrideBusy}
              onClick={isAdmin ? (e) => setTeamMenuAnchor(e.currentTarget) : undefined}
              label={`${conversation.companyCode ?? '1A'} · ${conversation.client ?? CHANNEL_LABELS[conversation.channel] ?? conversation.channel}${effectiveTeam(conversation) === 'support' ? ` · ${teamLabel('support')}` : ''}`}
              sx={{ ...(COMPANY_TAG_STYLE[conversation.companyCode] ?? {}), fontWeight: 700, fontSize: '0.75rem', height: 20, '& .MuiChip-label': { px: 0.75 } }}
            />
          </span>
        </Tooltip>
        <Menu anchorEl={teamMenuAnchor} open={Boolean(teamMenuAnchor)} onClose={() => setTeamMenuAnchor(null)}>
          <MenuItem
            disabled={!conversation.teamOverride}
            onClick={() => { setTeamMenuAnchor(null); handleTeamOverrideChange('') }}
          >
            <em>Use the account's team ({teamLabel(conversation.team ?? 'customer_service')})</em>
          </MenuItem>
          {TEAMS.map((t) => (
            <MenuItem
              key={t}
              selected={t === effectiveTeam(conversation)}
              onClick={() => { setTeamMenuAnchor(null); handleTeamOverrideChange(t) }}
            >
              {teamLabel(t)}
            </MenuItem>
          ))}
        </Menu>
        {isAdmin ? (
          // The intent chip itself is the override dropdown for admins — no
          // separate control next to it. Filled = manually overridden;
          // outlined = still the AI's own classification.
          <Tooltip title={conversation.intentOverride
            ? `Manually overridden${conversation.intentOverrideSetBy ? ` by ${conversation.intentOverrideSetBy}` : ''}${conversation.intentOverrideSetAt ? ` on ${new Date(conversation.intentOverrideSetAt).toLocaleString()}` : ''} — every regeneration uses this intent until cleared. Click to change.`
            : 'Click to force-regenerate the suggestion under a manually-chosen intent instead of the AI classifier.'}>
            <span>
              <Chip
                size="small"
                clickable
                disabled={overrideBusy}
                onClick={(e) => setIntentMenuAnchor(e.currentTarget)}
                label={`${conversation.latestIntent ? intentLabel(conversation.latestIntent) : 'Set intent'} ▾`}
                color={conversation.latestIntent ? (INTENT_COLOR[conversation.latestIntent] ?? 'default') : 'default'}
                variant={conversation.intentOverride ? 'filled' : 'outlined'}
                sx={{ height: 20, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            </span>
          </Tooltip>
        ) : (
          conversation.latestIntent && (
            <Tooltip title={conversation.latestIntentConfidence != null ? `${Math.round(conversation.latestIntentConfidence * 100)}% confidence` : ''}>
              <Chip
                size="small"
                label={intentLabel(conversation.latestIntent)}
                color={INTENT_COLOR[conversation.latestIntent] ?? 'default'}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            </Tooltip>
          )
        )}
        {/* Related contacts (2026-10-20): the same clickable chip as the
            Queue row's, from this contact's own (viewer-filtered) list. */}
        {(conversation.relatedContacts?.members ?? []).length > 0 && (
          <RelatedChip conversationId={conversation.id} members={conversation.relatedContacts.members}
            onOpen={(id) => onOpenConversationTab?.(id)} sx={{ height: 20, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }} />
        )}
        {conversation.channel === 'm365_email' && conversation.subject && (
          <Typography variant="body2" noWrap sx={{ minWidth: 0, flexShrink: 1 }}>
            {conversation.subject}
          </Typography>
        )}
        {otherMessagesButton === 'loading' && (
          <Button size="small" variant="outlined" disabled data-testid="other-messages-loading" startIcon={<CircularProgress size={12} color="inherit" />} sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}>
            Other Messages
          </Button>
        )}
        {otherMessagesButton === 'button' && (
          <Button size="small" variant="outlined" onClick={() => setOtherMessagesOpen(true)} sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}>
            Other Messages
          </Button>
        )}
        <OtherMessagesModal
          open={otherMessagesOpen}
          onClose={() => setOtherMessagesOpen(false)}
          conversations={effectiveOtherBuyerConversations}
          onOpenConversationTab={(id) => { setOtherMessagesOpen(false); onOpenConversationTab?.(id) }}
          onConversationClosed={onOtherConversationClosed}
        />
        <Menu anchorEl={intentMenuAnchor} open={Boolean(intentMenuAnchor)} onClose={() => setIntentMenuAnchor(null)}>
          <MenuItem
            disabled={!conversation.intentOverride}
            onClick={() => { setIntentMenuAnchor(null); handleIntentOverrideChange('') }}
          >
            <em>Use AI classification</em>
          </MenuItem>
          {/* A retired intent (the legacy fitment_confirmation) is offered
              only when this contact already has it. */}
          {intentOptionsFor([conversation.intentOverride, conversation.latestIntent]).filter(([k]) => k !== 'spam').map(([k, v]) => (
            <MenuItem
              key={k}
              selected={k === (conversation.intentOverride ?? conversation.latestIntent)}
              onClick={() => { setIntentMenuAnchor(null); handleIntentOverrideChange(k) }}
            >
              {v}
            </MenuItem>
          ))}
        </Menu>
        {overrideError && <Alert severity="error" onClose={() => setOverrideError(null)} sx={{ py: 0 }}>{overrideError}</Alert>}
        <Box sx={{ flexGrow: 1 }} />
        {otherViewers.length > 0 && (
          <>
            <Box sx={{ color: otherViewers.some(isRecentlyOpenedViewer) ? 'error.main' : 'info.main', display: 'flex' }}><EyeIcon /></Box>
            {otherViewers.map((v) => {
              const label = v.username.includes('@') ? v.username.split('@')[0] : v.username
              const recent = isRecentlyOpenedViewer(v)
              return (
                <Tooltip key={v.username} title={`${v.username} is viewing${recent ? ' (opened in the last 10 min)' : ''}`}>
                  <Chip size="small" label={label} color={recent ? 'error' : 'info'} variant="outlined" sx={{ height: 18, fontSize: 11, cursor: 'default' }} />
                </Tooltip>
              )
            })}
          </>
        )}
        <Tooltip title={translateError ?? ''} placement="bottom">
          <span>
            <Button
              size="small"
              variant={showTranslation ? 'contained' : 'outlined'}
              color={translateError ? 'error' : 'secondary'}
              onClick={handleTranslateToggle}
              disabled={translating}
              sx={{ fontSize: '0.75rem', py: 0.25, px: 1 }}
            >
              {translating ? 'Translating…' : showTranslation ? 'Hide EN' : 'Translate'}
            </Button>
          </span>
        </Tooltip>
        {isAdmin && (
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_e, v) => { if (v) setView(v) }}
            sx={{ '& .MuiToggleButton-root': { fontSize: '0.75rem', py: 0.25, px: 1 } }}>
            <ToggleButton value="messages">Messages</ToggleButton>
            <ToggleButton value="activity">Activity log</ToggleButton>
            <ToggleButton value="raw">Raw data</ToggleButton>
          </ToggleButtonGroup>
        )}
        {/* For the owner the X releases the claim (Thread's cleanup), so the
            contact goes back to the queue; for anyone else it only closes the
            view. The text says which (2026-10-07). */}
        <Tooltip title={closeButtonTooltip(assignment?.isOwner === true)}>
          <IconButton size="small" onClick={onDeselect} aria-label={closeButtonTooltip(assignment?.isOwner === true)}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* A declined Website order (2026-10-19): shown in place of a TRQ order
          when the latest suggestion matched one; nothing otherwise. */}
      <DeclinedOrderHeader conversation={conversation} refreshKey={`${suggestionRefreshKey}-${conversation.updatedAt ?? ''}-${conversation.intent ?? ''}-${conversation.messages?.length ?? 0}`} />

      {/* ── Info widgets: Order Info / Return-Case Info / Item Links / Vehicle
          Info — each only rendered when it has at least one populated field.
          Tracking/Related Documents/Vehicle Info depend on the async
          orderSummary fetch; a shared spinner signals more is still coming
          without blocking the rest of each widget's already-available fields. */}
      {(hasOrderInfoWidget || hasReturnCaseInfoWidget || hasItemLinksWidget || hasVehicleInfoWidget || hasItemsWidget || hasNoOrderVehicleInfo) && (
        <Box sx={{ pl: 0.5, pr: 0.5, py: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap', borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
          {conversation.erpOrderId && orderSummaryLoading && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%' }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">Loading order details…</Typography>
            </Stack>
          )}
          {conversation.erpOrderId && !orderSummaryLoading && orderSummaryError && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%' }}>
              <Typography variant="caption" color="warning.main">
                {orderSummary ? 'Some order details failed to load — showing the last successful result.' : 'Order details failed to load.'}
              </Typography>
              <Button size="small" onClick={() => setOrderSummaryRetryKey((k) => k + 1)}>Retry</Button>
            </Stack>
          )}

          {hasOrderInfoWidget && (
            <InfoWidget wide={!isTestingPeriodTrqRole && !ebayLayout}>
              {/* Only rendered when the latest suggestion's own multi-result
                  TRQ lookup (email/eBay-buyer/email+SKU/tracking-number/
                  phone) returned more than one order — a single-candidate
                  (or order-specific-lookup) match is never ambiguous, so the
                  plain link below is unchanged for that common case. Lets a
                  csr correct which order this contact is tied to
                  (server/src/ai/enrichment.js's lookupOrder()) when the
                  automatic pick was wrong. Rendered UNCONDITIONALLY of
                  erpOrderOverrideBusy below (unlike the rest of this widget,
                  which clears out while busy) — its own displayed value
                  stays pinned to the just-picked option via
                  pendingErpOrderId, so the pick visibly sticks instead of
                  snapping back while disabled. */}
              {conversation.candidateOrders?.length > 1 && (<>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <Select
                    value={(erpOrderOverrideBusy ? pendingErpOrderId : conversation.erpOrderId) ?? ''}
                    onChange={(e) => handleErpOrderIdOverrideChange(e.target.value === '' ? null : e.target.value)}
                    disabled={erpOrderOverrideBusy}
                    displayEmpty
                    sx={{ fontSize: 13, '& .MuiSelect-select': { py: 0.5 } }}
                    inputProps={{ 'aria-label': 'Select order' }}
                    // Closed: just the selected order number, compact. Open:
                    // each option's full line, as wide as it needs within the
                    // viewport — never truncated.
                    renderValue={(v) => v || 'Use automatic selection'}
                    MenuProps={{ PaperProps: { sx: { maxWidth: 'calc(100vw - 32px)', overflowX: 'auto' } } }}
                  >
                    {/* Clears erp_order_id_override back to automatic TRQ
                        lookup — mirrors the intent-override dropdown's own
                        "Use AI classification" entry. */}
                    <MenuItem value="" sx={{ fontSize: 13, fontStyle: 'italic' }}>Use automatic selection</MenuItem>
                    {conversation.candidateOrders.map((o) => (
                      <MenuItem key={o.erpOrderId} value={o.erpOrderId} sx={{ fontSize: 13, whiteSpace: 'nowrap', display: 'block' }}>
                        <Box component="span" sx={{ display: 'block' }}>{orderCandidateOptionText(o)}</Box>
                        {o.vehicle?.trim() && (
                          <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>{o.vehicle.trim()}</Box>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Which order the contact uses and why, and a one-click
                    "Use this order" on each other candidate (2026-10-29,
                    orderChoiceText.js): the same order-correction override
                    as the dropdown, which regenerates. */}
                {!erpOrderOverrideBusy && conversation.erpOrderId && (
                  <Box sx={{ width: '100%' }} data-testid="order-choice">
                    {(() => {
                      const why = orderChoiceText({ orderChoice: conversation.orderChoice, erpOrderId: conversation.erpOrderId, erpOrderIdOverride: conversation.erpOrderIdOverride, erpOrderIdOverrideSetBy: conversation.erpOrderIdOverrideSetBy })
                      return why ? (
                        <Typography variant="caption" color="text.secondary" component="div">
                          Using {conversation.erpOrderId}: {why}
                        </Typography>
                      ) : null
                    })()}
                    {conversation.candidateOrders.filter((o) => o?.erpOrderId && o.erpOrderId !== conversation.erpOrderId).map((o) => (
                      <Stack key={o.erpOrderId} direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="text.secondary">{o.erpOrderId}</Typography>
                        <Button size="small" sx={{ py: 0, minWidth: 0, fontSize: 12, textTransform: 'none' }} aria-label={`Use this order ${o.erpOrderId}`} onClick={() => handleErpOrderIdOverrideChange(o.erpOrderId)}>
                          Use this order
                        </Button>
                      </Stack>
                    ))}
                  </Box>
                )}
              </>)}
              {/* Switching orders clears the REST of this widget out
                  ENTIRELY the moment erpOrderOverrideBusy becomes true,
                  rather than leaving the previous order's now-stale fields
                  on screen for the whole round-trip — the override request
                  runs the full lookup + suggestion regeneration
                  synchronously server-side, so there's no server-sent
                  progress to react to sooner than this. */}
              {summarySwitching ? (summarySwitchingLoading ? <HeaderWidgetLoading /> : null) : (<>
              {!isTestingPeriodTrqRole && orderSummary?.holds?.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {orderSummary.holds.map((h, i) => {
                    const chip = <Chip key={i} size="small" label={`${h.type} Hold`} sx={{ bgcolor: 'error.main', color: 'error.contrastText', fontWeight: 700 }} />
                    return orderSummary.holdReason
                      ? <Tooltip key={i} title={orderSummary.holdReason}>{chip}</Tooltip>
                      : chip
                  })}
                </Stack>
              )}
              <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ width: '100%' }}>
                {/* Order + eBay Buyer ID stacked tightly (spacing 0) for the
                    eBay branch, per Jenn's 2026-09-03 layout request — the
                    Website branch keeps its original 0.5 gap (Order/billingPhone
                    are the only two rows that ever appear there). */}
                <Stack spacing={isTestingPeriodTrqRole ? 0.5 : 0}>
                  <Stack direction="row" spacing={0.25} alignItems="center">
                    <InfoField
                      label="Order"
                      value={conversation.erpOrderId}
                      href={conversation.erpOrderId && conversation.trqClientCode
                        ? `https://portal.trqautoparts.com/orders?clientCode=${conversation.trqClientCode}&document=${conversation.erpOrderId}`
                        : null}
                      copyable
                      hideLabel
                    />
                    {conversation.erpOrderId && (
                      <Tooltip title="View notes">
                        <IconButton size="small" onClick={() => openNotesWindow(conversation.erpOrderId, `Notes — ${conversation.erpOrderId}`)}
                          sx={{ p: 0.25, color: 'text.secondary', alignSelf: 'flex-end' }} aria-label="view order notes">
                          <NotepadIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  {erpOrderOverrideError && (
                    <Alert severity="error" onClose={() => setErpOrderOverrideError(null)} sx={{ py: 0 }}>{erpOrderOverrideError}</Alert>
                  )}
                  {isTestingPeriodTrqRole && orderSummary?.poNumber && (
                    <Stack direction="row" spacing={0.25} alignItems="center">
                      <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>{orderSummary.poNumber}</Typography>
                      <CopyButton value={orderSummary.poNumber} />
                    </Stack>
                  )}
                  {isTestingPeriodTrqRole && orderSummary?.billingPhone && (
                    <Stack direction="row" spacing={0.25} alignItems="center">
                      <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>{orderSummary.billingPhone}</Typography>
                      <CopyButton value={orderSummary.billingPhone} />
                    </Stack>
                  )}
                  {isSalesHoldEbayEmail && orderSummary?.ebayBuyerId && (
                    <InfoField
                      label="eBay Buyer ID"
                      value={orderSummary.ebayBuyerId}
                      href={`https://www.ebay.com/sh/ord/?filter=status%3AALL_ORDERS&search=buyerusername%3A${encodeURIComponent(orderSummary.ebayBuyerId)}`}
                      copyable
                      hideLabel
                    />
                  )}
                  {automatedReturnNotice && (automatedReturnNotice.buyerId ?? orderSummary?.ebayBuyerId) && (
                    <InfoField
                      label="eBay Buyer ID"
                      value={automatedReturnNotice.buyerId ?? orderSummary?.ebayBuyerId}
                      href={`https://www.ebay.com/sh/ord/?filter=status%3AALL_ORDERS&search=buyerusername%3A${encodeURIComponent(automatedReturnNotice.buyerId ?? orderSummary?.ebayBuyerId)}`}
                      copyable
                      hideLabel
                    />
                  )}
                  {headerChannel !== 'm365_email' && (
                    <InfoField
                      label="eBay Buyer ID"
                      value={conversation.customer?.externalUserId}
                      href={conversation.customer?.externalUserId
                        ? `https://www.ebay.com/sh/ord/?filter=status%3AALL_ORDERS&search=buyerusername%3A${encodeURIComponent(conversation.customer.externalUserId)}`
                        : null}
                      copyable
                      hideLabel
                    />
                  )}
                </Stack>
                {orderSummary?.orderDate && (() => {
                  const age = orderAgeInfo(orderSummary.orderDate)
                  const mdy = formatOrderDateMdy(orderSummary.orderDate)
                  return (
                    <Box>
                      <InfoField label="Order Age" value={age?.label} valueColor={age?.color} hideLabel />
                      <Stack direction="row" spacing={0.25} alignItems="center">
                        <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>{mdy}</Typography>
                        <CopyButton value={mdy} />
                      </Stack>
                      {/* Who created the order in TRQ (the PARENT order's
                          createdBy) — Website and Internal clients only. */}
                      {(conversation.client === 'Website' || conversation.client === 'Internal') && orderSummary.createdBy && (
                        <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>Created by {orderSummary.createdBy}</Typography>
                      )}
                    </Box>
                  )
                })()}
              </Stack>
              {!isTestingPeriodTrqRole && <Divider sx={{ my: 0.5 }} />}
              {orderSummary?.isInternational && (
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                  International
                </Typography>
              )}
              {headerChannel === 'm365_email' && (
                <InfoField label="Customer Email" value={headerCustomerEmail(conversation, orderSummary)} copyable hideLabel={isTestingPeriodTrqRole || isEbayClientEmail} />
              )}
              {isTestingPeriodTrqRole && orderSummary?.billingPhone && <Divider sx={{ my: 0.5 }} />}
              {conversation.referenceId && conversation.referenceType === 'ORDER' && (
                <InfoField label={REFTYPE_LABEL.ORDER} value={conversation.referenceId} hideLabel={isTestingPeriodTrqRole} />
              )}
              {headerChannel !== 'm365_email' && orderSystemRefs.map(({ label, value }) => (
                <InfoField key={label} label={label} value={value} />
              ))}
              {conversation.childDocumentRef && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Customer referenced {conversation.childDocumentRef.originalRef} (this order's {conversation.childDocumentRef.documentTypeLabel})
                </Typography>
              )}
              {orderSummary?.freightForwarderMatch?.matched && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, fontWeight: 700, color: 'warning.main' }}>
                    Potential Freight Forwarder
                  </Typography>
                  {orderSummary.freightForwarderMatch.shippingName && (
                    <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>
                      {orderSummary.freightForwarderMatch.shippingName}
                    </Typography>
                  )}
                  {(orderSummary.freightForwarderMatch.shippingAddress1 || orderSummary.freightForwarderMatch.shippingAddress2) && (
                    <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>
                      {[orderSummary.freightForwarderMatch.shippingAddress1, orderSummary.freightForwarderMatch.shippingAddress2].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  {(orderSummary.freightForwarderMatch.shippingCity || orderSummary.freightForwarderMatch.shippingRegion || orderSummary.freightForwarderMatch.shippingPostalCode) && (
                    <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>
                      {[orderSummary.freightForwarderMatch.shippingCity, orderSummary.freightForwarderMatch.shippingRegion, orderSummary.freightForwarderMatch.shippingPostalCode].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                </Box>
              )}
              {orderSummary?.containsDropship && (
                orderSummary?.dropshipPo ? (
                  <Link component="button" variant="caption" onClick={() => setDropshipPoOpen(true)}
                    sx={{ display: 'block', textAlign: 'left', fontWeight: 700, color: 'warning.main' }}>
                    Contains Dropship
                  </Link>
                ) : (
                  <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, fontWeight: 700, color: 'warning.main' }}>
                    Contains Dropship
                  </Typography>
                )
              )}
              {/* TESTING-PERIOD ONLY exception: hidden for 1AW/AMW, where the
                  new Items widget's own per-item tracking replaces this flat
                  combined list — unchanged for every other trqClientCode. */}
              {/* The eBay layout moves tracking to the Items widget's rows. */}
              {!isTestingPeriodTrqRole && !ebayLayout && orderSummary?.trackingSummary && orderSummary.trackingSummary.length > 0 && (
                <Box sx={{ minWidth: 0 }}>
                  {conversation.client !== 'eBay' && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>Tracking</Typography>
                  )}
                  <Stack spacing={0}>
                    {(trackingExpanded ? orderSummary.trackingSummary : orderSummary.trackingSummary.slice(0, 2)).map((t) => (
                      <Box key={t.trackingNumber} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {t.source === 'parent' ? 'Parent order' : `Replacement ${t.source}`}
                        </Typography>
                        {t.url ? (
                          <Link href={t.url} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {t.trackingNumber} ({t.carrier}) ↗
                          </Link>
                        ) : (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{t.trackingNumber}</Typography>
                        )}
                        <CopyButton value={t.trackingNumber} />
                      </Box>
                    ))}
                  </Stack>
                  {orderSummary.trackingSummary.length > 2 && (
                    <Link component="button" variant="caption" onClick={() => setTrackingExpanded((v) => !v)} sx={{ mt: 0.25 }}>
                      {trackingExpanded ? 'View less' : `View more (${orderSummary.trackingSummary.length - 2} more)`}
                    </Link>
                  )}
                </Box>
              )}
              {!isTestingPeriodTrqRole && !ebayLayout && orderSummary?.trackingSummary && orderSummary.trackingSummary.length > 0 && <Divider sx={{ my: 0.5 }} />}
              {isTestingPeriodTrqRole && (
                <AddressBlock heading="Billing"
                  name={orderSummary?.billingName} address1={orderSummary?.billingAddress1} address2={orderSummary?.billingAddress2}
                  city={orderSummary?.billingCity} region={orderSummary?.billingRegion} postalCode={orderSummary?.billingPostalCode} />
              )}
              {isTestingPeriodTrqRole && orderSummary?.billingName && <Divider sx={{ my: 0.5 }} />}
              {isTestingPeriodTrqRole && (
                <AddressBlock heading="Shipping"
                  name={orderSummary?.shippingName} address1={orderSummary?.shippingAddress1} address2={orderSummary?.shippingAddress2}
                  city={orderSummary?.shippingCity} region={orderSummary?.shippingRegion} postalCode={orderSummary?.shippingPostalCode} />
              )}
              {/* Billing/Shipping — every 1AE/AME conversation with the data,
                  not just Sales Hold ones (the old separate hasSalesHoldAddressWidget
                  card, removed). Same AddressBlock/fields the 1AW/AMW branch
                  above already uses; AddressBlock itself renders nothing when
                  every field is empty, so a TRQ order missing these fields
                  just degrades to nothing rather than showing blank fields. */}
              {!isTestingPeriodTrqRole && (
                <AddressBlock heading="Billing"
                  name={orderSummary?.billingName} address1={orderSummary?.billingAddress1} address2={orderSummary?.billingAddress2}
                  city={orderSummary?.billingCity} region={orderSummary?.billingRegion} postalCode={orderSummary?.billingPostalCode}
                  country={ebayLayout ? orderSummary?.billingCountry : null} />
              )}
              {!isTestingPeriodTrqRole && orderSummary?.billingName && <Divider sx={{ my: 0.5 }} />}
              {!isTestingPeriodTrqRole && (
                <AddressBlock heading="Shipping"
                  name={orderSummary?.shippingName} address1={orderSummary?.shippingAddress1} address2={orderSummary?.shippingAddress2}
                  city={orderSummary?.shippingCity} region={orderSummary?.shippingRegion} postalCode={orderSummary?.shippingPostalCode}
                  country={ebayLayout ? orderSummary?.shippingCountry : null} />
              )}
              </>)}
            </InfoWidget>
          )}

          {hasReturnCaseInfoWidget ? (
            <InfoWidget>
              {summarySwitching ? (summarySwitchingLoading ? <HeaderWidgetLoading /> : null) : (<>
              {(headerChannel !== 'm365_email' || isEbayClientEmail) && (<>
              {conversation.ebayCancelId && (
                <InfoField
                  label="eBay Cancel ID"
                  value={conversation.ebayCancelId}
                  href={`https://www.ebay.com/Cancel/Details?cancelId=${conversation.ebayCancelId}`}
                  copyValue={String(conversation.ebayCancelId)}
                  copyable
                />
              )}
              {conversation.ebayCaseId && !ebayCaseIdIsDup && (
                <InfoField
                  label="eBay Case ID"
                  value={conversation.ebayCaseId}
                  href={ebayCaseUrl(conversation.ebayCaseId, conversation.ebayCaseKind)}
                  copyValue={String(conversation.ebayCaseId)}
                  copyable
                />
              )}
              {orderSummary?.ebayCaseOrInquiry && (
                <InfoField
                  label={orderSummary.ebayCaseOrInquiry.kind === 'case' ? 'eBay Case' : 'eBay Inquiry'}
                  value={`${orderSummary.ebayCaseOrInquiry.id} (${orderSummary.ebayCaseOrInquiry.status})`}
                  copyValue={String(orderSummary.ebayCaseOrInquiry.id)}
                  href={ebayCaseUrl(orderSummary.ebayCaseOrInquiry.id, orderSummary.ebayCaseOrInquiry.kind === 'case' ? 'case' : 'inquiry')}
                  copyable
                />
              )}
              {orderSummary?.ebayReturnFound?.escalatedCaseId != null
                && !isDupOfLiveCaseOrInquiry(orderSummary.ebayReturnFound.escalatedCaseId)
                && String(orderSummary.ebayReturnFound.escalatedCaseId) !== String(conversation.ebayCaseId) && (
                <InfoField
                  label="eBay Case (via Return)"
                  value={String(orderSummary.ebayReturnFound.escalatedCaseId)}
                  href={ebayCaseUrl(orderSummary.ebayReturnFound.escalatedCaseId, 'escalated_return')}
                  copyValue={String(orderSummary.ebayReturnFound.escalatedCaseId)}
                  copyable
                />
              )}
              {headerReturn.found && (
                <InfoField
                  label="eBay Return"
                  value={headerReturn.found.value}
                  copyValue={headerReturn.found.id}
                  href={headerReturn.found.href}
                  copyable
                />
              )}
              {conversation.referenceId && conversation.referenceType === 'RETURN' && headerReturn.reference && (
                <InfoField label={REFTYPE_LABEL.RETURN} value={headerReturn.reference.value} href={headerReturn.reference.href} copyValue={String(conversation.referenceId)} copyable />
              )}
              {conversation.referenceId && conversation.referenceType === 'CANCELLATION' && (
                <InfoField label={REFTYPE_LABEL[conversation.referenceType]} value={conversation.referenceId} />
              )}
              {caseSystemRefs.map(({ label, value }) => (
                <InfoField
                  key={label}
                  label={label}
                  value={value}
                  href={label === 'Return ID'
                    ? ebayReturnDetailsUrl(value)
                    : label === 'INR Request'
                      ? ebayCaseUrl(value, 'inquiry')
                      : undefined}
                />
              ))}
              </>)}
              {/* The eBay layout: Status left, Order Total right, then a
                  divider, after the eBay reference IDs — the only place either
                  shows in this box. */}
              {statusTotal && (<>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} data-testid="status-total-line">
                  <Typography variant="caption" sx={{ lineHeight: 1.4, fontSize: 13, color: 'text.secondary' }}>
                    {statusTotal.status != null && (<>
                      <SectionLabel inline>Status: </SectionLabel>
                      <OrderStatusText status={statusTotal.status} />
                    </>)}
                  </Typography>
                  {statusTotal.total != null && (
                    <Typography variant="caption" sx={{ lineHeight: 1.4, fontSize: 13 }}>
                      <SectionLabel inline>Total: </SectionLabel>
                      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{money(statusTotal.total)}</Box>
                    </Typography>
                  )}
                </Stack>
                <Divider sx={{ my: 0.5 }} />
              </>)}
              {hasOrderTotalsBlock && (
                <Stack spacing={0.25}>
                  {(orderSummary?.orderTax != null || orderSummary?.shippingCost != null || (!ebayLayout && orderSummary?.orderTotal != null)) && (
                    <Stack direction="row" spacing={4}>
                      {orderSummary?.orderTax != null && (
                        <Typography variant="caption" sx={{ lineHeight: 1.4, fontSize: 13, color: 'text.secondary' }}>
                          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Tax: </Box>{money(orderSummary.orderTax)}
                        </Typography>
                      )}
                      {orderSummary?.shippingCost != null && (
                        <Typography variant="caption" sx={{ lineHeight: 1.4, fontSize: 13, color: 'text.secondary' }}>
                          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Freight: </Box>{money(orderSummary.shippingCost)}
                        </Typography>
                      )}
                      {!ebayLayout && orderSummary?.orderTotal != null && (
                        <Typography variant="caption" sx={{ lineHeight: 1.4, fontSize: 13 }}>
                          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Total: </Box>
                          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{money(orderSummary.orderTotal)}</Box>
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {(ebayLayout ? (orderSummary?.orderTax != null || orderSummary?.shippingCost != null) : orderSummary?.orderTotal != null) && <Divider sx={{ my: 0.5 }} />}
                  {(orderSummary?.paymentMethodDisplay != null || orderSummary?.paymentId != null) && (
                    <Stack direction="row" spacing={0.25} alignItems="center">
                      <Typography variant="caption" sx={{ lineHeight: 1.4, fontSize: 13, color: 'text.secondary' }}>
                        {ebayLayout
                          ? <SectionLabel inline>Payment: </SectionLabel>
                          : <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Payment: </Box>}
                        {orderSummary?.paymentMethodDisplay}
                        {orderSummary?.paymentMethodDisplay && orderSummary?.paymentId ? (
                          <Box component="span" sx={{ ml: 3 }}>{orderSummary.paymentId}</Box>
                        ) : (
                          orderSummary?.paymentId
                        )}
                      </Typography>
                      {orderSummary?.paymentId != null && <CopyButton value={orderSummary.paymentId} />}
                    </Stack>
                  )}
                  {orderSummary?.orderBatch != null && (
                    <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, fontSize: 13, color: 'text.secondary' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>orderBatch: </Box>{orderSummary.orderBatch}
                    </Typography>
                  )}
                  {!ebayLayout && orderSummary?.status != null && (
                    <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, fontSize: 13, color: 'text.secondary' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>Status: </Box>
                      <OrderStatusText status={orderSummary.status} />
                      {isTestingPeriodTrqRole && orderSummary?.holds?.length > 0 && (() => {
                        const holdText = orderSummary.holds.map((h) => `${h.type} Hold Applied`).join(', ')
                        const holdSpan = <Box component="span" sx={{ fontWeight: 700, color: 'error.main', ml: 3 }}>{holdText}</Box>
                        return orderSummary.holdReason ? <Tooltip title={orderSummary.holdReason}>{holdSpan}</Tooltip> : holdSpan
                      })()}
                    </Typography>
                  )}
                  {orderSummary?.deliveryEstimateWorstCase != null && (
                    <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, fontSize: 13, color: 'text.secondary' }}>
                      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>EDD: </Box>{formatOrderDateMdy(orderSummary.deliveryEstimateWorstCase)}
                    </Typography>
                  )}
                  {(ebayLayout
                    ? (orderSummary?.paymentMethodDisplay != null || orderSummary?.paymentId != null || orderSummary?.orderBatch != null || orderSummary?.deliveryEstimateWorstCase != null)
                    : orderSummary?.status != null) && <Divider sx={{ my: 0.5 }} />}
                </Stack>
              )}
              {orderSummary?.relatedDocuments && (
                <Box sx={{ minWidth: 0 }}>
                  <SectionLabel>Related Documents</SectionLabel>
                  <Stack spacing={0.25}>
                    {(relatedDocsExpanded ? orderSummary.relatedDocuments.documents : orderSummary.relatedDocuments.documents.slice(0, 6)).map((d, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3, fontSize: 13 }}>
                          {[d.erpOrderId, d.date ? `created ${d.date}` : null, d.orderTotal != null ? money(d.orderTotal) : null, d.reasonCode ? `for ${d.reasonCode}` : null].filter(Boolean).join(' ')}
                        </Typography>
                        {d.erpOrderId && (
                          <Tooltip title="View document detail">
                            <IconButton
                              size="small"
                              onClick={() => openDocDetailWindow(d)}
                              sx={{ p: 0.25, color: 'text.secondary' }}
                              aria-label="view document detail"
                            >
                              <EyeIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                  </Stack>
                  {orderSummary.relatedDocuments.documents.length > 6 && (
                    <Link component="button" variant="caption" onClick={() => setRelatedDocsExpanded((v) => !v)} sx={{ mt: 0.25 }}>
                      {relatedDocsExpanded ? 'View less' : `View more (${orderSummary.relatedDocuments.documents.length - 6} more)`}
                    </Link>
                  )}
                </Box>
              )}
              {orderSummary?.returnLabelTracking && orderSummary.returnLabelTracking.length > 0 && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" display="block" sx={{ lineHeight: 1.2, fontWeight: 700 }}>Return Label(s)</Typography>
                  <Stack spacing={0}>
                    {orderSummary.returnLabelTracking.map((t) => (
                      <Box key={t.trackingNumber} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                        {/* The customer's own label (2026-10-23): after ours,
                            "<number> (USPS) · customer's own label". */}
                        {t.url ? (
                          <Link href={t.url} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} data-testid={t.source === 'customer' ? 'return-label-customer' : undefined}>
                            {returnLabelText(t)} ↗
                          </Link>
                        ) : (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{t.trackingNumber}</Typography>
                        )}
                        <CopyButton value={t.trackingNumber} />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
              </>)}
            </InfoWidget>
          ) : hasNoOrderVehicleInfo ? (
            <InfoWidget>
              {summarySwitching ? (summarySwitchingLoading ? <HeaderWidgetLoading /> : null) : (<>
              <Typography variant="caption" display="block" sx={{ lineHeight: 1.2, fontWeight: 700 }}>Customer Vehicle</Typography>
              {conversation.messageVehicleInfo ? (
                <>
                  <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, overflowWrap: 'break-word', color: 'text.secondary' }}>{conversation.messageVehicleInfo}</Typography>
                  {conversation.messageVehicleDetails && (
                    <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, color: 'text.secondary' }}>{conversation.messageVehicleDetails}</Typography>
                  )}
                </>
              ) : (
                <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, color: 'text.secondary' }}>
                  {[conversation.fitmentCheck?.vehicleYear, conversation.fitmentCheck?.vehicleMake, conversation.fitmentCheck?.vehicleModel].filter(Boolean).join(' ')}
                </Typography>
              )}
              </>)}
            </InfoWidget>
          ) : null}

          {hasItemLinksWidget && (
            <InfoWidget>
              {summarySwitching ? (summarySwitchingLoading ? <HeaderWidgetLoading /> : null) : (<>
              {conversation.channel !== 'm365_email' && listingValue && (
                <InfoField
                  hideLabel
                  value="eBay Listing"
                  href={ebayListingUrl(listingValue, conversation.metadata?.listing?.url)}
                  copyValue={listingValue}
                  copyable
                />
              )}
              {conversation.itemInfoSku && (
                <>
                  <InfoField hideLabel value="1A Site" href={oneASiteUrl(conversation.itemInfoSku)} copyValue={conversation.itemInfoSku} copyable />
                  <InfoField hideLabel value="Item Info" href={itemInfoUrl(conversation.itemInfoSku)} copyValue={conversation.itemInfoSku} copyable />
                </>
              )}
              {conversation.itemInfoPrice1A != null && (
                <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>{money(conversation.itemInfoPrice1A)}</Typography>
              )}
              {conversation.itemInfoWarranty && (
                <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>{conversation.itemInfoWarranty}</Typography>
              )}
              {conversation.itemInfoQuantityAvailable != null && (
                <Typography
                  variant="caption" display="block" sx={{ lineHeight: 1.4, fontWeight: 700 }}
                  color={conversation.itemInfoQuantityAvailable > 0 ? 'success.main' : 'error.main'}
                >
                  {conversation.itemInfoQuantityAvailable > 0
                    ? `In Stock (${conversation.itemInfoQuantityAvailable})`
                    : 'Out of Stock'}
                </Typography>
              )}
              {conversation.itemInfoElectricalPart && (
                <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, color: 'error.main', fontWeight: 700 }}>Electrical Part</Typography>
              )}
              {conversation.itemInfoKitIncludes?.length > 0 && (
                <Link component="button" variant="caption" onClick={() => setKitIncludesOpen(true)} sx={{ display: 'block', textAlign: 'left' }}>
                  Kit Includes
                </Link>
              )}
              {(conversation.itemInfoQualifiers?.length > 0 || conversation.itemInfoCompanySpecifications?.length > 0 || conversation.itemInfoItemSpecifications || conversation.itemInfoEbayTitleAM) && (
                <Link component="button" variant="caption" onClick={() => setQualifiersOpen(true)} sx={{ display: 'block', textAlign: 'left' }}>
                  Part Details
                </Link>
              )}
              </>)}
            </InfoWidget>
          )}

          {hasVehicleInfoWidget && (
            <InfoWidget wide>
              {summarySwitching ? (summarySwitchingLoading ? <HeaderWidgetLoading /> : null) : (<>
              {orderSummary?.vehicleInfo ? (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>Customer Vehicle</Typography>
                  <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>{orderSummary.vehicleInfo}</Typography>
                </>
              ) : conversation.messageVehicleInfo && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>Customer Vehicle</Typography>
                  <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, overflowWrap: 'break-word' }}>{conversation.messageVehicleInfo}</Typography>
                  {conversation.messageVehicleDetails && (
                    <Typography variant="caption" display="block" sx={{ lineHeight: 1.4 }}>{conversation.messageVehicleDetails}</Typography>
                  )}
                </>
              )}
              {/* The VIN edit (2026-10-21, VinEdit.jsx), as in the Items header;
                  here only on a VIN shown (the widget keeps its width otherwise). */}
              {conversation.vin ? (
              <VinEdit conversationId={conversation.id} vin={conversation.vin} vinOverride={conversation.vinOverride ?? null} onSaved={handleVinSaved}>
                <Stack direction="row" spacing={2}>
                  <InfoField label="VIN" value={conversation.vin}
                    href={conversation.vin && conversation.vinDecodeResult?.source !== 'comp9' ? northAmericanMotoringVinUrl(conversation.vin) : undefined}
                    copyable />
                  {conversation.vinDecodeStatus === 'success' && (
                    <Tooltip title="View VIN decode results">
                      <IconButton size="small" onClick={() => setVinDecodeModalOpen(true)} sx={{ p: 0.25, color: 'success.main', alignSelf: 'flex-end' }} aria-label="view VIN decode results">
                        <NotepadIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </VinEdit>
              ) : null}
              {overrideVehicle && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.4 }}>{overrideVehicle}</Typography>
              )}
              {conversation.fitmentCheck?.checked && (
                conversation.fitmentCheck.fits === true && conversation.fitmentCheck.additionalFactorsMatch === false ? (
                  // Fits by YMM, but checkFitmentDetails() found a qualifier
                  // beyond YMM (engine, drivetrain, trim hardware, etc.) the
                  // customer's own stated details don't confirm — same amber
                  // framing as before, re-gated to specifically this case.
                  <>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" sx={{ lineHeight: 1.4, fontWeight: 700, color: 'secondary.main' }}>YMM Match — Please Review</Typography>
                      <Chip size="small" color="warning" label={conversation.fitmentCheck.note ?? 'Needs review'}
                        sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'break-word', display: 'block', py: 0.5 } }} />
                    </Stack>
                    {conversation.fitmentCheck.hasVariants ? (
                      <>
                        <Box component="ul" sx={{ m: 0, pl: 2.25, maxHeight: 88, overflowY: 'auto' }}>
                          {(conversation.fitmentCheck.matchedVariantTitles?.length ? conversation.fitmentCheck.matchedVariantTitles : conversation.fitmentCheck.variantTitles).map((t) => (
                            <Typography key={t} component="li" variant="caption" display="list-item" sx={{ lineHeight: 1.4, overflowWrap: 'break-word', '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, mb: 0.5 } }}>
                              {t}
                            </Typography>
                          ))}
                        </Box>
                        <Link component="button" type="button" variant="caption" onClick={openFitListPrefilled}>
                          View Full Fit List
                        </Link>
                      </>
                    ) : (
                      <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, overflowWrap: 'break-word' }}>
                        {conversation.fitmentCheck.title}
                      </Typography>
                    )}
                  </>
                ) : conversation.fitmentCheck.fits === true && conversation.fitmentCheck.additionalFactorsMatch === true ? (
                  // The confident case — YMM matches and every qualifier
                  // beyond YMM (if any) is satisfied.
                  <>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" sx={{ lineHeight: 1.4, fontWeight: 700, color: 'success.main' }}>Listed to Fit - Please Confirm</Typography>
                      <Chip size="small" color="success" label={conversation.fitmentCheck.note ?? 'Confirmed fit'}
                        sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'break-word', display: 'block', py: 0.5 } }} />
                    </Stack>
                    {conversation.fitmentCheck.hasVariants ? (
                      <>
                        <Box component="ul" sx={{ m: 0, pl: 2.25, maxHeight: 88, overflowY: 'auto' }}>
                          {(conversation.fitmentCheck.matchedVariantTitles?.length ? conversation.fitmentCheck.matchedVariantTitles : conversation.fitmentCheck.variantTitles).map((t) => (
                            <Typography key={t} component="li" variant="caption" display="list-item" sx={{ lineHeight: 1.4, overflowWrap: 'break-word', '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider', pb: 0.5, mb: 0.5 } }}>
                              {t}
                            </Typography>
                          ))}
                        </Box>
                        <Link component="button" type="button" variant="caption" onClick={openFitListPrefilled}>
                          View Full Fit List
                        </Link>
                      </>
                    ) : (
                      <Typography variant="caption" display="block" sx={{ lineHeight: 1.4, overflowWrap: 'break-word' }}>
                        {conversation.fitmentCheck.title}
                      </Typography>
                    )}
                  </>
                ) : conversation.fitmentCheck.fits === false && (
                  // No candidate entry strictly matched YMM — checkFitmentMismatch()
                  // may still have something useful to say: a likely YMM-level
                  // near-miss (body-style/trim naming difference, not a
                  // different vehicle) and/or a beyond-YMM qualifier on the
                  // closest candidate the customer hasn't addressed. Same
                  // relocated-chip pattern as the other two cases above; null
                  // when there's truly nothing informative to add.
                  <>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" sx={{ lineHeight: 1.4, fontWeight: 700, color: 'error.main' }}>
                        Does Not Fit
                      </Typography>
                      {conversation.fitmentCheck.note && (
                        <Chip size="small" color="error" label={conversation.fitmentCheck.note}
                          sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'break-word', display: 'block', py: 0.5 } }} />
                      )}
                    </Stack>
                    <Link component="button" type="button" variant="caption" onClick={() => setFitListOpen(true)}>
                      View Fit List
                    </Link>
                  </>
                )
              )}
              </>)}
            </InfoWidget>
          )}

          {hasItemsWidget && (
            <InfoWidget grow flush>
              {itemsWidgetSwitching ? (itemsWidgetSwitchingLoading ? <HeaderWidgetLoading /> : null) : (<>
              {/* GET /:id/order-summary/items resolves independently of (and
                  usually later than) the fast order-summary fetch above —
                  its own small spinner, scoped to just this widget, rather
                  than holding up anything else in this row. Only shown while
                  there's nothing to show yet, so a refetch that lands
                  quickly never flashes it over already-visible items. */}
              {(orderSummaryLoading || itemDetailsLoading) && itemsGrouped.length === 0 && !noOrderItem && (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pl: 2, py: 0.5 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Loading order details…</Typography>
                </Stack>
              )}
              {/* The rows are in; Stock, Fit and Tags fill in per cell
                  (2026-10-29). One caption while they load, or one line
                  with Retry when they didn't. */}
              {slowLoading && (
                <Typography variant="caption" color="text.secondary" data-testid="items-slow-loading" sx={{ pl: 2 }}>Loading stock and part details…</Typography>
              )}
              {slowFailed && (
                <Stack direction="row" spacing={1} alignItems="center" data-testid="items-slow-failed" sx={{ pl: 2 }}>
                  <Typography variant="caption" color="text.secondary">Stock and part details didn't load</Typography>
                  <Link component="button" type="button" variant="caption" onClick={() => setItemsRetryKey((k) => k + 1)} data-testid="items-slow-retry">Retry</Link>
                </Stack>
              )}
              {(vinEditable || headerVins.length > 0 || headerVehicleLabels.length > 0 || itemsGrouped.length > 0) && (
                // pl: 2 (16px) matches the SKU column's own left offset below
                // (ItemsTable's wrapper pl: 1 + the SKU cell's own pl: 1) so
                // this header lines up with the table's SKU text, not the
                // widget edge.
                <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ pl: 2 }}>
                  {/* The eBay layout's full vehicle text wraps inside this
                      group (minWidth 0), so the expand button keeps its place. */}
                  <Stack direction="row" spacing={1.5} alignItems="flex-end" flexWrap="wrap" useFlexGap sx={ebayLayout ? { minWidth: 0, flex: 1 } : undefined}>
                    {headerVins.map((vin, i) => {
                      const display = (
                        <Stack direction="row" spacing={0.5} alignItems="baseline">
                          <Typography variant="caption" sx={{ fontSize: 13, lineHeight: 1.4, fontWeight: 700 }}>VIN:</Typography>
                          {vin === conversation.vin && conversation.vinDecodeResult?.source === 'comp9' ? (
                            <TruncatedCaption text={vin} />
                          ) : (
                            <Link href={northAmericanMotoringVinUrl(vin)} target="_blank" rel="noopener noreferrer" underline="hover">
                              <TruncatedCaption text={vin} />
                            </Link>
                          )}
                          <CopyButton value={vin} />
                          {vin === conversation.vin && conversation.vinDecodeStatus === 'success' && (
                            <Tooltip title="View VIN decode results">
                              <IconButton size="small" onClick={() => setVinDecodeModalOpen(true)} sx={{ p: 0.25, color: 'success.main' }} aria-label="view VIN decode results">
                                <NotepadIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      )
                      // The edit (2026-10-21) sits on the contact's own VIN,
                      // or after the last VIN when the order items name
                      // theirs; it edits the contact's VIN either way.
                      const editsHere = vin === conversation.vin || (i === headerVins.length - 1 && !headerVins.includes(conversation.vin))
                      return editsHere ? (
                        <VinEdit key={vin} conversationId={conversation.id} vin={vin} vinOverride={conversation.vinOverride ?? null} onSaved={handleVinSaved}>
                          {display}
                        </VinEdit>
                      ) : <Fragment key={vin}>{display}</Fragment>
                    })}
                    {headerVins.length === 0 && (
                      <VinEdit conversationId={conversation.id} vin={null} vinOverride={conversation.vinOverride ?? null} onSaved={handleVinSaved} />
                    )}
                    {overrideVehicle && (
                      <Box component="span" data-testid="vin-override-vehicle"><TruncatedCaption text={overrideVehicle} /></Box>
                    )}
                    {headerVehicleLabels.map((label) => (ebayLayout
                      // The eBay layout shows the vehicle in full, wrapping.
                      ? <TruncatedCaption key={label} text={label} cap={null} noWrap={false} sx={{ overflowWrap: 'anywhere' }} />
                      : <TruncatedCaption key={label} text={label} />
                    ))}
                  </Stack>
                  {itemsGrouped.length > 0 && (
                    <Tooltip title="View all items">
                      <IconButton size="small" onClick={() => setItemsModalOpen(true)} aria-label="view all items" sx={ebayLayout ? { flexShrink: 0 } : undefined}>
                        <Typography sx={{ fontWeight: 700, lineHeight: 1 }}>⤢</Typography>
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              )}
              {itemsGrouped.length > 0 ? (
                <>
                  <Box sx={{ mt: (conversation.vin || itemsGrouped.length > 0) ? 1 : 0 }}>
                    <ItemsTable items={itemsGrouped.slice(0, 3)} totalCount={itemsGrouped.length} allItems={itemsGrouped} showEbayLink={ebayLayout}
                      onOpenPartDetails={setPartDetailsRow} onOpenFitDetail={setFitDetailRow} />
                  </Box>
                  <DraggableModal open={itemsModalOpen} onClose={() => setItemsModalOpen(false)}
                    title="All Items" centered
                    width={Math.round(window.innerWidth * 0.9)} height={Math.round(window.innerHeight * 0.85)}
                    minWidth={600} minHeight={300}>
                    <ItemsTable items={itemsGrouped} detailed order={orderSummary} totalCount={itemsGrouped.length} showAmSku={showAmSku} showEbayLink={ebayLayout}
                      onOpenPartDetails={setPartDetailsRow} onOpenFitDetail={setFitDetailRow} />
                  </DraggableModal>
                </>
              ) : noOrderItem ? (
                <Box sx={{ mt: 1 }}>
                  <ItemsTable items={[noOrderItem]} showTracking={false}
                    onOpenPartDetails={setPartDetailsRow} onOpenFitDetail={setFitDetailRow} />
                </Box>
              ) : null}
              </>)}
            </InfoWidget>
          )}

        </Box>
      )}
      </Box>

      <RowResizeHandle onMouseDown={startHeaderDrag} label="Resize header area" />

      {/* A VIN just saved or cleared (2026-10-21): the suggestion still uses
          the old one until it is regenerated. Regenerate is the panel's own
          (its unsaved-draft guard included). */}
      {vinNoticeFor === conversation.id && (
        <Alert severity="info" data-testid="vin-updated-notice" onClose={() => setVinNoticeFor(null)} sx={{ py: 0, mx: 1, mb: 0.5 }}
          action={<Button size="small" color="inherit" onClick={() => setRegenerateRequest((n) => n + 1)} data-testid="vin-updated-regenerate">Regenerate</Button>}>
          {VIN_UPDATED_NOTICE}
        </Alert>
      )}

      {/* ── Two-column area: Messages left, Proposed Resolution right ─────── */}
      <Box ref={threadRowRef} sx={{ flex: 1, display: 'flex', minHeight: 0 }}>

        {/* Left: Messages / Activity / Raw data (Translate now lives in Row 1) */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          {/* Locked above the scrolling messages (eBay channel, from a
              listing): the listing's title, or its id when no title
              resolves, linking to the listing. */}
          {listingBanner && shouldShowListingBanner({
            hasOrder: Boolean(conversation.erpOrderId),
            // Decided from the order's lines (2026-10-29), without waiting
            // on the slow fields; amSku fills in when they arrive.
            itemsState: orderRows
              ? 'loaded'
              : itemDetailsErrorFor && itemDetailsErrorFor === conversation.erpOrderId ? 'error' : 'loading',
            orderItems: orderRows,
            listingId: listingBanner.listingId,
            listingSku,
          }) && (
            <Box data-testid="listing-banner" sx={{ px: 3, py: 0.75, flexShrink: 0, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                Initiated Conversation From Listing:{' '}
                <Link href={listingBanner.url} target="_blank" rel="noopener noreferrer" underline="hover">{listingTitle ?? listingBanner.listingId}</Link>
                {/* The listing's part (2026-10-19): Item Info and the 1A site, on
                    the same line; none without a SKU. */}
                {listingSku && (
                  <>
                    {' · '}
                    <Link href={itemInfoUrl(listingSku)} target="_blank" rel="noopener noreferrer" underline="hover" data-testid="listing-banner-item-info">Item Info</Link>
                    {' · '}
                    <Link href={oneASiteUrl(listingSku)} target="_blank" rel="noopener noreferrer" underline="hover" data-testid="listing-banner-1a-site">1A Site</Link>
                  </>
                )}
              </Typography>
            </Box>
          )}
          <Box ref={messagesPaneRef} data-testid="messages-pane" sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {isAdmin && view === 'activity' ? (
              <ConversationActivity conversationId={conversation.id} refreshKey={activityKey} isAdmin={isAdmin} highlightFeedbackId={reportedFeedbackId} handleTimeMs={conversation.handleTimeMs} reopenedBy={conversation.reopenedBy} reopenedAt={conversation.reopenedAt} erpOrderIdOverride={conversation.erpOrderIdOverride} erpOrderIdOverrideSetBy={conversation.erpOrderIdOverrideSetBy} erpOrderIdOverrideSetAt={conversation.erpOrderIdOverrideSetAt} parks={parkingEnabled ? (conversation.parks ?? null) : null} parkedTimeMs={parkingEnabled ? (conversation.parkedTimeMs ?? null) : null} newEmail={conversation.metadata?.newEmail ?? null} taskOrderLookups={conversation.metadata?.taskOrderLookups ?? null} tasksCreated={conversation.metadata?.tasksCreated ?? null} vinEdits={conversation.metadata?.vinEdits ?? null} otherActionsMessages={conversation.metadata?.otherActionsMessages ?? null} autoClosed={conversation.metadata?.autoClosed ?? null} repairReopened={conversation.metadata?.repairReopened ?? null} relatedReopened={conversation.metadata?.relatedReopened ?? null} orderLookupAttempts={conversation.metadata?.orderLookupAttempts ?? null} />
            ) : isAdmin && view === 'raw' ? (
              <Box component="pre" sx={{ m: 0, p: 3, fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(conversation, null, 2)}
              </Box>
            ) : (
              <Box ref={messagesContentRef} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(conversation.messages ?? []).map((m) => {
                  // A task contact's task (Tasks Phase 1, 2026-10-13): the
                  // Task card with its live fields, history and Edit, in
                  // place of a bubble.
                  if (isTaskMessage(m)) return <TaskContactCard key={m.id} taskId={m.metadata?.taskId} />
                  return (
                  <Fragment key={m.id}>
                  {quotedTurnsFor(m).map((item) => <QuotedHistoryBubble key={item.key} item={item} />)}
                  <Box>
                    <Stack alignItems={m.direction === 'outbound' ? 'flex-end' : 'flex-start'}>
                    <Paper variant="outlined" data-testid={`message-bubble-${m.direction}`} sx={[{ px: 1.5, py: 1, maxWidth: m.direction === 'outbound' ? '85%' : 'calc(100% - 24px)', bgcolor: (theme) => messageBubbleBg(theme, m.direction) }, (theme) => messageTextSx(theme), (theme) => messageLinkSx(theme)]}>
                      {/* A "Leave Us a Message" or contact-form email (metadata.leaveUsAMessage/
                          metadata.contactForm) always renders as its parsed field summary,
                          regardless of bodyContentType — the raw HTML is form-page chrome
                          (banner, heading bar, footer), not a real message. FROM_MEMBERS
                          (customer-facing) otherwise shows RAW so display defects (leaked CSS,
                          footers customers receive) stay visible. FROM_EBAY (eBay's
                          machine-generated HTML emails): show the sanitized text — the raw is
                          unreadable template noise. An M365 email (bodyContentType: 'html') is
                          a real HTML document worth actually rendering, not showing as literal
                          tag soup — see sanitizeHtmlBody's own comment for why this is
                          DOMPurify only, no PII redaction layered on top. Plain-text bodies
                          (system notices like Automated Return Notice/Sales Hold Applied,
                          FROM_EBAY, ordinary M365 text mail) get the header-value font and a
                          display-only pass collapsing runs of extra blank lines, per
                          collapseExtraBlankLines()'s own comment. */}
                      {m.metadata?.leaveUsAMessage ? (
                        <LeaveUsAMessageBody leaveUsAMessage={m.metadata.leaveUsAMessage} fullText={m.bodySanitized} translatedText={showTranslation ? translationMap?.messages?.[m.id] : null} />
                      ) : m.metadata?.contactForm ? (
                        <ContactFormMessageBody contactForm={m.metadata.contactForm} translatedText={showTranslation ? translationMap?.messages?.[m.id] : null} />
                      ) : !isSystemNotice(m) && (isSalesHoldAppliedSubject(conversation.subject) || isSalesHoldPhoneCallOpsEmailSubject(conversation.subject)) && m.direction === 'inbound' ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...MESSAGE_BODY_SX }}>
                          {collapseExtraBlankLines(m.bodySanitized || m.body)}
                        </Typography>
                      ) : m.bodyContentType === 'html' ? (
                        <Box sx={{
                          ...MESSAGE_BODY_SX,
                          overflowWrap: 'anywhere',
                          '& img': { maxWidth: '100%' },
                          '& table': { maxWidth: '100%' },
                          // An imported/authored template's HTML often carries its
                          // own inline font-family/size (e.g. a Word-doc import's
                          // Calibri 11pt, or an inbound message's own inline
                          // sizing from whatever mail client sent it) — override
                          // for BOTH directions, so every body in the thread
                          // renders at the same size/family (MESSAGE_BODY_SX,
                          // matching ContactFormMessageBody/LeaveUsAMessageBody's
                          // plain Typography) regardless of who sent it or which
                          // channel it came in on. font-weight/font-style/color/
                          // text-decoration are untouched, so real <strong>/<b>/
                          // italic/coloured-banner/link styling still renders as
                          // authored.
                          '& *': { fontFamily: 'inherit !important', fontSize: '13px !important' },
                        }}
                          dangerouslySetInnerHTML={{ __html: replaceCidImages(sanitizeHtmlBody(m365DisplayBodyHtml(m), { systemNotice: isSystemNotice(m) }), inlineImageUrls[m.id]) }} />
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...MESSAGE_BODY_SX }}>
                          {collapseExtraBlankLines(conversation.metadata?.ebayConversationType === 'FROM_EBAY' ? (m.bodySanitized || m.body) : m.body)}
                        </Typography>
                      )}
                      {/* Suppressed for the three structured message kinds — each of
                          those now renders its own field-scoped translation
                          inline (see LeaveUsAMessageBody/ContactFormMessageBody's
                          own translatedText prop), so this generic whole-body
                          box would be redundant. An automated return notice
                          (2026-10-12) shows its whole body, so its translation
                          goes here, below it, labelled English. */}
                      {showTranslation && translationMap?.messages?.[m.id]
                        && !m.metadata?.leaveUsAMessage && !m.metadata?.contactForm && (
                        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>English</Typography>
                          {/* Same condition the server used to decide textType 'html'
                              (routes/conversations.js's usedRawHtml) — bodySanitized,
                              when present, is already plain text (sanitize.js's
                              htmlToText), so only the raw-body fallback path (no
                              bodySanitized, e.g. our own sent replies) is real HTML
                              needing dangerouslySetInnerHTML instead of a plain
                              pre-wrap text node. */}
                          {(conversation.metadata?.ebayConversationType === 'FROM_EBAY' || m.bodyContentType === 'html') && !m.bodySanitized ? (
                            <Box sx={{ ...MESSAGE_BODY_SX, overflowWrap: 'anywhere', '& p': { margin: '0 0 0.5em' }, '& p:last-child': { marginBottom: 0 } }}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtmlBody(translationMap.messages[m.id]) }} />
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {translationMap.messages[m.id]}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {/* Buyer-uploaded attachments (eBay messageMedia). Image links to full size. */}
                      {Array.isArray(m.metadata?.media) && m.metadata.media.length > 0 && (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          {m.metadata.media.map((media, i) => (
                            media.url && media.type === 'IMAGE' ? (
                              <Box key={i} component="img" src={media.url} alt={media.name || 'attachment'} draggable
                                onClick={() => {
                                  const idx = conversationImages.findIndex((img) => img.url === media.url && img.messageId === m.id)
                                  if (idx !== -1) setLightboxIndex(idx)
                                }}
                                sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider', display: 'block', cursor: 'pointer' }} />
                            ) : media.url ? (
                              <Link key={i} href={media.url} target="_blank" rel="noopener" variant="caption">{media.name || 'attachment'} ↗</Link>
                            ) : null
                          ))}
                        </Stack>
                      )}
                      {/* m365_email attachments — real bytes in blob storage, streamed
                          through the conversation-scoped download route. `name` is
                          a generic label (never the real filename, which is
                          deliberately never stored — see repositories/
                          message-attachments.js), so it's shown as-is rather than
                          treated as anything a customer or CSR typed. */}
                      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          {m.attachments.map((att) => (
                            att.available ? (
                              <Stack key={att.id} direction="row" spacing={0.5} alignItems="center">
                                <Link component="button" type="button" variant="caption"
                                  onClick={() => downloadAttachment(conversation.id, att.id, att.name).catch((err) => window.alert(`Couldn't download attachment: ${err.message}`))}>
                                  📎 {att.name}
                                </Link>
                                {/^image\//i.test(att.contentType ?? '') && (
                                  <Link component="button" type="button" variant="caption" aria-label={`View ${att.name}`}
                                    onClick={() => openAttachmentImage(conversation.id, att)}>
                                    View
                                  </Link>
                                )}
                              </Stack>
                            ) : (
                              <Typography key={att.id} variant="caption" color="text.secondary">📎 {att.name} (processing)</Typography>
                            )
                          ))}
                        </Stack>
                      )}
                    </Paper>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, width: '100%', justifyContent: m.direction === 'outbound' ? 'flex-end' : 'flex-start' }}>
                      <Typography variant="caption" color="text.secondary">{formatEST(m.receivedAt)}</Typography>
                      {/* Attribution only exists for outbound messages sent after this
                          shipped (a read-time exact-text match against
                          ai_suggestions.sent_reply — see repositories/conversations.js's
                          attributeSentMessages()) — m.sentBy is absent for everything
                          else, rendering nothing rather than a guessed "Unknown". */}
                      {m.direction === 'outbound' && m.sentBy && (
                        <Typography variant="caption" color="text.secondary">
                          · {m.sentBy.automated ? 'Automated Response' : m.sentBy.name}
                        </Typography>
                      )}
                      {/* Other Actions → Message the customer (2026-10-30): a
                          new eBay message to the order's buyer, sent from
                          this contact. */}
                      {m.direction === 'outbound' && m.metadata?.messageKind === 'other_actions_ebay_message' && (
                        <Typography variant="caption" color="text.secondary" fontWeight={600} data-testid="thread-other-actions-ebay-message">
                          · eBay message to the buyer{m.metadata?.ebayBuyerId ? ` (${m.metadata.ebayBuyerId})` : ''}
                        </Typography>
                      )}
                      {/* The reply eBay refused on the return, sent as an
                          eBay message (2026-11-04); or sent that way first
                          because the return's state takes no messages
                          (metadata.returnStateWords, message first). */}
                      {m.direction === 'outbound' && m.metadata?.messageKind === 'return_reply_as_ebay_message' && (
                        <Typography variant="caption" color="text.secondary" fontWeight={600} data-testid="thread-return-reply-ebay-message">
                          · {returnReplyEbayMessageThreadLabel({ messageFirst: Boolean(m.metadata?.returnStateWords), stateWords: m.metadata?.returnStateWords ?? null })}
                        </Typography>
                      )}
                      {/* From/To — the literal envelope addresses for THIS message
                          (mapper.js's fromHeader/receivedAtAddress for inbound,
                          mapOutboundReply()'s own equivalents for a reply sent
                          through CXHub), per message rather than only the
                          conversation's first one, since a long M365 thread can
                          span more than one address pair. Populated on every M365
                          message in both directions — absent/undefined on
                          anything else (eBay), rendering nothing rather than a
                          blank label. No mailto: link, same non-clickable
                          convention as Customer Email — a link would invite
                          replying outside the tracked flow. */}
                      {m.metadata?.fromHeader && (
                        <Typography variant="caption" color="text.secondary">
                          · From: {m.metadata.fromHeader}
                        </Typography>
                      )}
                      {(m.metadata?.receivedAtAddress ?? conversation.metadata?.mailbox) && (
                        <Typography variant="caption" color="text.secondary">
                          · To: {m.metadata?.receivedAtAddress ?? conversation.metadata?.mailbox}
                        </Typography>
                      )}
                    </Stack>
                    {/* CC line (2026-10-08): everyone the message was copied to,
                        under the date/From/To line, only when there are any.
                        Server-curated (mapMessage's ccAddresses). */}
                    {m.ccAddresses?.length > 0 && (
                      <Typography variant="caption" color="text.secondary" display="block" data-testid="message-cc-line" sx={{ width: '100%', textAlign: m.direction === 'outbound' ? 'right' : 'left' }}>
                        CC: {m.ccAddresses.join(', ')}
                      </Typography>
                    )}
                  </Box>
                  </Fragment>
                  )
                })}
                <Box ref={messagesBottomRef} />
              </Box>
            )}
          </Box>
        </Box>

        <ResizeHandle onMouseDown={startSuggestionPanelDrag} label="Resize AI Suggestion panel" />

        {/* Right: Proposed Resolution — always visible, scrolls independently */}
        {/* Wider by the agent-draft column while the shadow comparison is
            side by side and expanded (2026-10-16); the dragged width is
            unchanged and comes back when it collapses or stacks. */}
        {/* Side by side (2026-10-19): the suggestion's width, the handle and the
            agent draft's own resizable width, clamped so the messages column
            keeps its minimum. Text wraps; nothing scrolls sideways. */}
        <Box data-testid="suggestion-panel-column" sx={{ width: aiWidths.suggestion + (shadowCompareSide ? RESIZE_HANDLE_WIDTH + aiWidths.shadow : 0), flexShrink: 0, overflowY: 'auto', overflowX: 'hidden', minHeight: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
          <SuggestionPanel
            conversationId={conversation.id}
            // Related contacts (2026-10-20): the banners, the send guard and
            // the email typo check; a banner's close reloads like Other
            // Messages' own close does.
            relatedContacts={conversation.relatedContacts ?? null}
            onOpenRelatedConversation={onOpenConversationTab ? (id) => onOpenConversationTab(id) : null}
            onRelatedChanged={onOtherConversationClosed}
            conversationMessages={conversation.messages ?? null}
            parkingEnabled={parkingEnabled}
            isOwner={assignment?.isOwner === true}
            viewerUsername={currentUser ?? null}
            workCaptureRef={workCaptureRef}
            showShadowCompare={showShadowCompare}
            onShadowCompareSideChange={setShadowCompareSide}
            shadowColumnWidth={aiWidths.shadow}
            onShadowColumnDrag={startShadowDraftDrag}
            isInternalEmail={conversation.isInternalEmail === true}
            refreshKey={suggestionRefreshKey}
            refreshSkipDraft={refreshSkipDraft}
            forcedDraftText={forcedDraftText}
            fromEbay={conversation.metadata?.ebayConversationType === 'FROM_EBAY'}
            // A task contact's reply box behaves as the channel its response
            // goes out on (taskContact.js).
            channel={conversation.channel === 'task' ? taskPanelChannel(conversation) : (conversation.channel ?? null)}
            taskContact={taskPanelProps(conversation)}
            client={conversation.client ?? null}
            templateAccount={conversation.templateAccount ?? conversation.company}
            // The resolved order's code for a Customer Support mailbox, never
            // the default role (2026-10-13); trqClientCode for every other.
            templateTrqClientCode={conversation.templateTrqClientCode !== undefined ? conversation.templateTrqClientCode : (conversation.trqClientCode ?? null)}
            templateTeam={effectiveTeam(conversation)}
            lastInboundMessage={[...(conversation.messages ?? [])].reverse().find((m) => m.direction === 'inbound') ?? null}
            orderSummaryCustomerEmail={orderSummary?.customerEmail ?? null}
            orderSummaryOrderTax={orderSummary?.orderTax ?? null}
            orderSummaryShippingCost={orderSummary?.shippingCost ?? null}
            orderSummaryOrderTotal={orderSummary?.orderTotal ?? null}
            orderSummaryItemDetails={orderRows}
            orderSummaryTrackingSummary={orderSummary?.trackingSummary ?? null}
            customerExternalUserId={conversation.customer?.externalUserId ?? null}
            mailbox={conversation.metadata?.mailbox ?? null}
            outboundFrom={conversation.channel === 'task' ? (taskPanelProps(conversation)?.fromAddress ?? null) : (conversation.outboundFrom ?? null)}
            newOutbound={conversation.newOutbound ?? null}
            outboundMode={conversation.outboundMode ?? null}
            signatureTemplate={conversation.signatureTemplate ?? null}
            subject={conversation.subject ?? null}
            spamContext={{
              channel: conversation.channel ?? null,
              client: conversation.client ?? null,
              company: conversation.companyCode ?? null,
              fromIdentifier: conversation.customer?.externalUserId ?? conversation.customer?.displayName ?? null,
              subject: conversation.subject ?? null,
            }}
            onDecided={handleDecided}
            onGenerated={() => { setVinNoticeFor(null); onGenerated?.() }}
            regenerateRequest={regenerateRequest}
            onSpamMarked={onSpamMarked}
            orderSwitching={erpOrderOverrideBusy}
            showTranslation={showTranslation}
            draftTranslation={translationMap?.draftReply ?? null}
            historyTranslation={translationMap?.history ?? null}
            shadowDraftTranslation={translationMap?.shadowDraft ?? null}
            isAdmin={isAdmin}
            autoRegenerateStaleMinutes={autoRegenerateStaleMinutes}
            onDraftDirtyChange={onDraftDirtyChange}
            onUntoldCustomerChange={onUntoldCustomerChange}
            automationFailures={conversation.automationFailures}
            conversationStatus={conversation.status}
            latestTransfer={conversation.latestTransfer ?? null}
            latestEscalation={conversation.latestEscalation ?? null}
            conversationWaitingReason={conversation.waitingReason ?? null}
            conversationAutomationDueAt={conversation.automationDueAt ?? null}
            conversationRetryDueAt={conversation.salesHoldRetryDueAt ?? null}
            conversationRetryAttempts={conversation.salesHoldRetryAttempts ?? null}
            conversationRetryMaxAttempts={conversation.salesHoldRetryMaxAttempts ?? null}
            onCloseConversation={handleCloseConversation}
            closingConversation={closingConversation}
            closeError={closeError}
            onDismissCloseError={() => setCloseError(null)}
            onReopenConversation={handleReopenConversation}
            reopeningConversation={reopeningConversation}
            reopenError={reopenError}
            onDismissReopenError={() => setReopenError(null)}
            reviewedByMgmt={conversation.reviewedByMgmt}
            reviewedByMgmtUser={conversation.reviewedByMgmtUser}
            canManageReviewedByMgmt={isElevatedOrSupport}
          />
        </Box>

      </Box>

      <DraggableModal
        open={assignmentModal != null}
        onClose={() => setAssignmentModal(null)}
        title={assignmentModal?.type === 'taken-over' ? 'Conversation Taken Over' : assignmentModal?.type === 'parked' ? 'Contact Parked' : 'Conversation Assigned'}
        width={420}
        height={assignmentModal?.type === 'parked' ? 220 : 180}
        actions={<>
          {(assignmentModal?.type === 'assigned-to-other' || assignmentModal?.type === 'parked') && isElevatedOrSupport && (
            <Button color="error" variant="contained" onClick={handleTakeOverAssignment}>Take Over Contact</Button>
          )}
          {assignmentModal?.type === 'parked' && isAdmin && (
            <Button variant="outlined" onClick={handleReturnParkedToQueue}>Return to Queue</Button>
          )}
          <Button onClick={() => setAssignmentModal(null)}>OK</Button>
        </>}
      >
        {assignmentModal?.type === 'parked' ? (
          <Box data-testid="parked-popup">
            <Typography>{`${assignmentModal.ownerName} parked this contact on ${new Date(assignmentModal.parkedAt).toLocaleString()}.`}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {assignmentModal.note?.trim() ? assignmentModal.note : 'no note'}
            </Typography>
          </Box>
        ) : (
          <Typography>
            {assignmentModal?.type === 'taken-over'
              ? `${assignmentModal.ownerName} has taken over ownership of this conversation.`
              : `This conversation is currently assigned to ${assignmentModal?.ownerName}.`}
          </Typography>
        )}
      </DraggableModal>

      {/* Park dialog (Lab-only, 2026-10-02): an optional note, then Park. */}
      <Dialog open={parkDialog != null} onClose={() => !parkDialog?.saving && setParkDialog(null)} maxWidth="sm" fullWidth
        TransitionProps={{ onEntered: () => parkNoteRef.current?.focus() }}>
        <DialogTitle>Park this contact</DialogTitle>
        <DialogContent>
          {parkDialog?.error && <Alert severity="error" sx={{ mb: 1 }}>{parkDialog.error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            It stays yours and leaves your screen. Opening it again picks it up where you left off. Parked time doesn't count toward handle time.
          </Typography>
          <TextField
            label="Why are you parking this?"
            fullWidth
            multiline
            minRows={5}
            maxRows={12}
            size="small"
            value={parkDialog?.note ?? ''}
            onChange={(e) => setParkDialog((d) => ({ ...d, note: e.target.value }))}
            disabled={parkDialog?.saving}
            inputProps={{ maxLength: 2000 }}
            inputRef={parkNoteRef}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParkDialog(null)} disabled={parkDialog?.saving}>Cancel</Button>
          <Button variant="contained" onClick={confirmPark} disabled={parkDialog?.saving}>
            {parkDialog?.saving ? 'Parking…' : 'Park'}
          </Button>
        </DialogActions>
      </Dialog>

      <DraggableModal
        open={fitListOpen}
        onClose={closeFitList}
        title="Fit List"
        width={480}
        height={420}
        actions={<Button onClick={closeFitList}>Close</Button>}
      >
        {(() => {
          const allEntries = fitListEntries ?? conversation.fitmentCheck?.allFitEntries ?? []
          const yearNum = fitListYearFilter.trim() === '' ? null : Number(fitListYearFilter)
          const yearFilterActive = yearNum !== null && Number.isFinite(yearNum)
          const makeFilterNorm = normalizeVehicleToken(fitListMakeFilter)
          const modelFilterNorm = normalizeVehicleToken(fitListModelFilter)
          const filteredEntries = allEntries.filter((e) => {
            if (yearFilterActive && !(yearNum >= e.yearStart && yearNum <= e.yearEnd)) return false
            if (makeFilterNorm && !normalizeVehicleToken(e.make).includes(makeFilterNorm)) return false
            if (modelFilterNorm && !normalizeVehicleToken(e.model).includes(modelFilterNorm)) return false
            return true
          })
          return (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <TextField size="small" label="Year" value={fitListYearFilter} onChange={(e) => setFitListYearFilter(e.target.value)} sx={{ width: 150 }} />
                <TextField size="small" label="Make" value={fitListMakeFilter} onChange={(e) => setFitListMakeFilter(e.target.value)} fullWidth />
                <TextField size="small" label="Model" value={fitListModelFilter} onChange={(e) => setFitListModelFilter(e.target.value)} fullWidth />
              </Stack>
              {allEntries.length > 0 && filteredEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No matches</Typography>
              ) : (
                <Stack spacing={1} sx={{ '& > *:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider', pb: 1 } }}>
                  {filteredEntries.map((e) => (
                    <Stack key={e.title} direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>{e.title}</Typography>
                      <CopyButton value={e.title} />
                    </Stack>
                  ))}
                </Stack>
              )}
            </>
          )
        })()}
      </DraggableModal>

      <DraggableModal
        open={kitIncludesOpen}
        onClose={() => setKitIncludesOpen(false)}
        title="Kit Includes"
        width={420}
        height={380}
        actions={<Button onClick={() => setKitIncludesOpen(false)}>Close</Button>}
        headerActions={<CopyButton value={(conversation.itemInfoKitIncludes ?? []).map((t) => `- ${t}`).join('\n')} />}
      >
        <Stack spacing={0.5}>
          {(conversation.itemInfoKitIncludes ?? []).map((t, i) => (
            <Typography key={i} variant="body2">- {t}</Typography>
          ))}
        </Stack>
      </DraggableModal>

      <DraggableModal
        open={qualifiersOpen || partDetailsRow != null}
        onClose={closePartDetails}
        title="Part Details"
        width={420}
        height={560}
        actions={<Button onClick={closePartDetails}>Close</Button>}
        headerActions={<CopyButton value={partDetailsCopyText(partDetailsRow ?? {
          ebayTitleAM: conversation.itemInfoEbayTitleAM,
          qualifiers: conversation.itemInfoQualifiers,
          itemSpecifications: conversation.itemInfoItemSpecifications,
          companySpecifications: conversation.itemInfoCompanySpecifications,
        })} />}
      >
        {(() => {
          // partDetailsRow (Items widget, per-row) wins when set; otherwise
          // this is the OLD Item Links widget's conversation-wide data
          // (qualifiersOpen true) — the two are mutually exclusive per
          // conversation.
          const row = partDetailsRow ?? {
            ebayTitleAM: conversation.itemInfoEbayTitleAM,
            qualifiers: conversation.itemInfoQualifiers,
            itemSpecifications: conversation.itemInfoItemSpecifications,
            companySpecifications: conversation.itemInfoCompanySpecifications,
          }
          return (
            <Stack spacing={1.5}>
              {row.ebayTitleAM && (
                <Typography variant="subtitle2" fontWeight={700}>{row.ebayTitleAM}</Typography>
              )}
              {/* Only ever populated on an Items widget row (per Jenn's
                  instruction to merge Kit Includes into Part Details for
                  1AW/AMW) — the OLD conversation-wide Item Links widget
                  keeps its own separate Kit Includes modal/link, untouched. */}
              {row.kitIncludes?.length > 0 && (
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2" fontWeight={700}>Kit Includes</Typography>
                  {row.kitIncludes.map((t, i) => (
                    <Typography key={i} variant="body2">- {t}</Typography>
                  ))}
                </Stack>
              )}
              <Stack spacing={0.5}>
                {(row.qualifiers ?? []).map((t, i) => (
                  <Typography key={i} variant="body2">- {t}</Typography>
                ))}
              </Stack>
              {row.itemSpecifications && (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.itemSpecifications}</Typography>
              )}
              {(row.companySpecifications ?? []).map((spec, i) => (
                <Box key={i}>
                  <Typography variant="subtitle2" fontWeight={700}>{spec.name}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{spec.text}</Typography>
                </Box>
              ))}
            </Stack>
          )
        })()}
      </DraggableModal>

      {/* Per-row fitment detail (Items widget) — Customer Vehicle + Fit
          Listing + an always-visible, filterable Fit List. Factored out into
          its own component — see FitmentDetailModal's own header comment. */}
      <FitmentDetailModal row={fitDetailRow} onClose={() => setFitDetailRow(null)} />

      <DraggableModal
        open={dropshipPoOpen}
        onClose={() => setDropshipPoOpen(false)}
        title="Dropship PO Details"
        width={420}
        height={280}
        actions={<Button onClick={() => setDropshipPoOpen(false)}>Close</Button>}
      >
        <Stack spacing={1}>
          <Typography variant="body2">PO Number: {orderSummary?.dropshipPo?.poNumber}</Typography>
          <Typography variant="body2">Vendor Name: {orderSummary?.dropshipPo?.vendorName}</Typography>
          <Typography variant="body2">Email Address: {orderSummary?.dropshipPo?.emailAddress}</Typography>
          <Typography variant="body2">PO Date: {formatOrderDateMdy(orderSummary?.dropshipPo?.poDate)}</Typography>
        </Stack>
      </DraggableModal>

      <Dialog open={vinDecodeModalOpen} onClose={() => setVinDecodeModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          VIN Decode Results
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {conversation.vin}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {conversation.vinDecodeResult?.source === 'nhtsa' ? (
            <Stack spacing={2}>
              <Chip size="small" label="Data from NHTSA" color="info" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
              <Stack direction="row" spacing={4}>
                <InfoField label="Year" value={conversation.vinDecodeResult?.year} />
                <InfoField label="Make" value={conversation.vinDecodeResult?.make} />
                <InfoField label="Model" value={conversation.vinDecodeResult?.model} />
              </Stack>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box component="tbody">
                  {/* NHTSA's own response carries ~150 mostly-blank fields for
                      any real VIN (its own Message field: "missing decoded
                      values should be interpreted as NHTSA does not have data
                      on the specific variable") — only non-empty fields are
                      shown here, and VIN/ModelYear/Make/Model are skipped
                      since they're already shown above. */}
                  {Object.entries(conversation.vinDecodeResult?.raw ?? {})
                    .filter(([key, value]) => !['VIN', 'ModelYear', 'Make', 'Model'].includes(key) && value !== null && value !== '')
                    .map(([key, value]) => (
                      <Box component="tr" key={key}>
                        <Box component="td" sx={{ py: 0.25, pr: 2, fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{key}</Box>
                        <Box component="td" sx={{ py: 0.25, fontSize: 13 }}>{String(value)}</Box>
                      </Box>
                    ))}
                </Box>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Chip size="small" label="Data from Comp9" color="success" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
              <Stack direction="row" spacing={4}>
                <InfoField label="Year" value={conversation.vinDecodeResult?.year} />
                <InfoField label="Build Date" value={conversation.vinDecodeResult?.buildDate} />
              </Stack>
              {(conversation.vinDecodeResult?.vehicleDetails ?? []).length > 0 && (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <Box component="tbody">
                    {conversation.vinDecodeResult.vehicleDetails.map((d, i) => (
                      <Box component="tr" key={i}>
                        <Box component="td" sx={{ py: 0.25, pr: 2, fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{d.label}</Box>
                        <Box component="td" sx={{ py: 0.25, fontSize: 13 }}>{d.value}</Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <Box component="thead">
                  <Box component="tr">
                    <Box component="th" sx={{ textAlign: 'left', borderBottom: 1, borderColor: 'divider', py: 0.5, pr: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Code</Typography>
                    </Box>
                    <Box component="th" sx={{ textAlign: 'left', borderBottom: 1, borderColor: 'divider', py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Description</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box component="tbody">
                  {(conversation.vinDecodeResult?.codes ?? []).map((c, i) => (
                    <Box component="tr" key={i}>
                      <Box component="td" sx={{ borderBottom: 1, borderColor: 'divider', py: 0.5, pr: 2, fontFamily: 'monospace', fontSize: 13 }}>{c.code}</Box>
                      <Box component="td" sx={{ borderBottom: 1, borderColor: 'divider', py: 0.5, fontSize: 13 }}>{c.description}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVinDecodeModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ImageLightbox
        images={conversationImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
      {attachmentLightbox && (
        <ImageLightbox
          images={attachmentLightbox.images}
          index={attachmentLightbox.index}
          onClose={closeAttachmentLightbox}
          onNavigate={(i) => setAttachmentLightbox((prev) => (prev ? { ...prev, index: i } : prev))}
          fullResUrl={identityImageUrl}
        />
      )}

      {notesWindows.map((w, i) => (
        <OrderNotesWindow
          key={w.key}
          conversationId={conversation.id}
          erpOrderId={w.erpOrderId}
          title={w.title}
          offsetIndex={i}
          onClose={() => closeNotesWindow(w.key)}
        />
      ))}

      {docDetailWindows.map((w, i) => (
        <DocumentDetailWindow
          key={w.key}
          conversationId={conversation.id}
          doc={w.doc}
          offsetIndex={i}
          onClose={() => closeDocDetailWindow(w.key)}
        />
      ))}
    </Box>
  )
}

// Ops area. Tabbed: Health | Alerts | API Status | API Docs | eBay messages | Waiting on OMS API | Costs | Prompt Improvement.
// The QA auditor has its own admin-only Audit page (nav item 'audit').
// Health leads, and is the default: "is it working right now" is the question
// someone opening Ops is usually here to answer. No deep-link support needed —
// nothing currently jumps into this page.
function OpsView() {
  const [tab, setTab] = useState('health')
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab value="health" label="Health" />
          <Tab value="alerts" label="Alerts" />
          <Tab value="status" label="API Status" />
          <Tab value="ebay-api" label="API Docs" />
          <Tab value="ebay-messages" label="eBay messages" />
          <Tab value="waiting-on-oms" label="Waiting on OMS API" />
          <Tab value="costs" label="Costs" />
          <Tab value="prompt-review" label="Prompt Improvement" />
        </Tabs>
      </Box>
      {tab === 'health' ? (
        <HealthView />
      ) : tab === 'alerts' ? (
        <AlertsView />
      ) : tab === 'status' ? (
        <ApiStatusView />
      ) : tab === 'ebay-api' ? (
        <ApiDocs />
      ) : tab === 'ebay-messages' ? (
        <EbayMessagesView />
      ) : tab === 'waiting-on-oms' ? (
        <WaitingOnOmsView />
      ) : tab === 'prompt-review' ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}><PromptReviewPage /></Box>
      ) : (
        <CostView />
      )}
    </Box>
  )
}

// Settings area. Tabbed: Spam Rules | Feedback | Templates | Flows | Prioritization | Run-Time Flags | Failures.
// key/initialTab-based deep-link pattern (needed for the automation-failure
// notification jump — see jumpToFailures()): bumping the nonce that feeds
// this component's `key` forces a remount at `initialTab` even if it's
// already open on a different tab.
function SettingsView({ onOpenInAgent, initialTab, unresolvedFailureCount = 0, onFailuresChanged, canAccessFlows = false, canAccessPrioritization = false, canAccessRuntimeFlags = false, canAccessRoles = false, viewerAgentId = null, viewerSupportPermissions = false, viewerIsElevated = false }) {
  // A non-admin deep-linked at 'flows'/'runtime-flags' (they can't reach either
  // any other way, since those tabs aren't rendered for them) falls back to Spam
  // Rules instead of stranding them on a tab with no visible way back. Roles
  // is gated the same way on canAccessRoles (admin, or a csr holding Support
  // Permissions).
  const gatedTab = (initialTab === 'flows' && !canAccessFlows) || (initialTab === 'prioritization' && !canAccessPrioritization) || (initialTab === 'runtime-flags' && !canAccessRuntimeFlags) || (initialTab === 'roles' && !canAccessRoles)
  const [tab, setTab] = useState(gatedTab ? 'spam' : (initialTab ?? 'spam'))
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
          <Tab value="spam" label="Spam Rules" />
          <Tab value="feedback" label="Feedback" />
          <Tab value="templates" label="Templates" />
          {canAccessFlows && <Tab value="flows" label="Flows" />}
          {canAccessPrioritization && <Tab value="prioritization" label="Prioritization" />}
          {canAccessRuntimeFlags && <Tab value="runtime-flags" label="Run-Time Flags" />}
          {canAccessRoles && <Tab value="roles" label="Permissions" />}
          <Tab value="failures" label={unresolvedFailureCount > 0 ? `Failures (${unresolvedFailureCount})` : 'Failures'} sx={unresolvedFailureCount > 0 ? { color: 'error.main' } : undefined} />
        </Tabs>
      </Box>
      {tab === 'spam' ? (
        <SpamRulesView />
      ) : tab === 'feedback' ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}><FeedbackReview onOpenInAgent={onOpenInAgent} /></Box>
      ) : tab === 'templates' ? (
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}><TemplatesPage /></Box>
      ) : tab === 'flows' && canAccessFlows ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}><FlowsPage /></Box>
      ) : tab === 'prioritization' && canAccessPrioritization ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}><PrioritizationPage /></Box>
      ) : tab === 'runtime-flags' && canAccessRuntimeFlags ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}><RuntimeFlagsPage /></Box>
      ) : tab === 'roles' && canAccessRoles ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <RolesPage viewerAgentId={viewerAgentId} viewerSupportPermissions={viewerSupportPermissions} viewerIsElevated={viewerIsElevated} />
        </Box>
      ) : tab === 'failures' ? (
        <FailuresPage onOpenInAgent={onOpenInAgent} onResolved={onFailuresChanged} />
      ) : (
        <SpamRulesView />
      )}
    </Box>
  )
}

// Signed in with an account that holds no CXHub role. The usual cause is the
// wrong account (a privileged admin.* sign-in instead of the everyday one),
// so the way out is a sign-out, not a role request — the button uses the
// same MSAL redirect flow as the AccountMenu, which this screen replaces.
function AccessDenied({ account }) {
  const { instance } = useMsal()
  return (
    <Box sx={{ height: '100vh', display: 'grid', placeItems: 'center', p: 4 }}>
      <Box sx={{ textAlign: 'center', maxWidth: 440 }}>
        <Typography variant="h6" gutterBottom>No access to CXHub</Typography>
        <Typography color="text.secondary" gutterBottom>
          {account ? `${account} is` : 'You are'} signed in, but not assigned to CXHub.
          Ask an administrator to add you to the <b>CXHub-Agent</b> or{' '}
          <b>CXHub-Admin</b> group, then sign in again.
        </Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          Signed in with the wrong account? Sign out and pick another one.
        </Typography>
        <Button variant="outlined" size="small" onClick={() => instance.logoutRedirect({ account: instance.getActiveAccount() })}>
          Sign out
        </Button>
      </Box>
    </Box>
  )
}

// Persistent left icon nav — replaces the old Agent/Ops/Settings/Reporting
// ToggleButtonGroup with a fixed, full-height rail so Queue/Closed/Ops/
// Settings/Reporting are all one click away regardless of what's on screen.
// Two widths, not a hide/show toggle: expanded shows icon + label, collapsed
// shows icon only — the arrow at the bottom toggles between them.
function NavRail({ navPage, onNavigate, collapsed, onToggleCollapsed, effectiveIsAdmin, effectiveIsElevatedOrSupport, parkingEnabled = false, parkedCount = 0, tasksVisible = false }) {
  const width = collapsed ? NAV_RAIL_WIDTH_COLLAPSED : NAV_RAIL_WIDTH_EXPANDED
  const entries = [
    { page: 'queue', label: 'Queue', Icon: QueueIcon, show: true },
    // Parked contacts (Lab-only, 2026-10-02). The badge is a csr's own parked
    // contacts, or every parked contact for an admin; hidden at 0.
    { page: 'parked', label: 'Parked', Icon: CarIcon, show: parkingEnabled, badge: parkedCount },
    // "New" email (2026-10-08): compose a brand-new email from a service@
    // address. Every csr and admin, no toggle. Beneath Queue, and beneath Parked
    // when Parked is shown.
    { page: 'new', label: 'New', Icon: NewEmailIcon, show: true },
    // Tasks (Lab-only, 2026-10-12): between New and Closed, only when the
    // feature is on and the requester can use it (canSeeTasks()).
    { page: 'tasks', label: 'Tasks', Icon: TasksIcon, show: tasksVisible },
    { page: 'closed', label: 'Closed', Icon: ClosedIcon, show: true },
    // External link-out, not an internal page — see SCHEDULE_HUB_URL's own
    // comment. Was admin plus a small pilot allowlist while most of
    // the floor hadn't been trained on Schedule Hub yet; opened to everyone
    // (show: true, matching Queue/Closed) once training caught up.
    { page: 'schedule-hub', label: 'Schedule Hub', Icon: ScheduleHubIcon, show: true, external: true, href: SCHEDULE_HUB_URL },
    { page: 'ops', label: 'Ops', Icon: OpsIcon, show: effectiveIsAdmin },
    // The QA auditor (qa-observer/, docs/qa-observer.md): its own page, admin
    // only, not a tab inside Ops (Gordon, 2026-10-14).
    { page: 'audit', label: 'Audit', Icon: AuditIcon, show: effectiveIsAdmin },
    { page: 'settings', label: 'Settings', Icon: SettingsIcon, show: effectiveIsElevatedOrSupport },
    { page: 'reporting', label: 'Reporting', Icon: ReportingIcon, show: canSeeReportingTab(effectiveIsAdmin) },
    // Self-service per-agent stats -- the mirror image of the admin
    // Reporting entry above: shown to everyone who doesn't have the full
    // Reporting page (Reporting is admin-only; My Stats is for csr).
    { page: 'my-stats', label: 'My Stats', Icon: ReportingIcon, show: canSeeMyStatsTab(effectiveIsAdmin) },
  ]
  return (
    <Box sx={{
      width, flexShrink: 0, bgcolor: 'primary.main', color: '#fff',
      display: 'flex', flexDirection: 'column', height: '100vh',
      transition: 'width 0.15s ease', overflow: 'hidden',
    }}>
      {/* Collapse/expand control — at the TOP (not the bottom) and a real
          pill-button treatment (contrasting fill, larger tap target) so it
          reads as a deliberate control rather than an easy-to-miss detail. */}
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
        <Box
          role="button"
          aria-label={collapsed ? 'Expand nav' : 'Collapse nav'}
          onClick={onToggleCollapsed}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.22)', cursor: 'pointer',
            fontSize: 20, fontWeight: 700, userSelect: 'none',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.34)' },
          }}
        >
          {collapsed ? '›' : '‹'}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {entries.filter((e) => e.show).map(({ page, label, Icon, external, href, badge }) => (
          <Box
            key={page}
            role="button"
            aria-label={external ? `${label} (opens in a new tab)` : label}
            // External entries open Schedule Hub in a new tab instead of
            // going through goToNavPage -- it isn't an internal page value
            // that function (or navPage's own highlight logic below) knows
            // about, and never should be.
            onClick={external ? () => window.open(href, '_blank', 'noopener,noreferrer') : () => onNavigate(page)}
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 0.5, cursor: 'pointer', py: 1.25, px: 1,
              bgcolor: !external && navPage === page ? 'rgba(255,255,255,0.18)' : 'transparent',
              borderLeft: !external && navPage === page ? '3px solid #fff' : '3px solid transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            {badge > 0
              ? <Badge badgeContent={badge} color="error" max={99} data-testid={`nav-badge-${page}`}><Icon /></Badge>
              : <Icon />}
            {!collapsed && <Typography variant="caption" sx={{ color: '#fff', lineHeight: 1, whiteSpace: 'nowrap' }}>{label}</Typography>}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// One conversation's independently-loaded detail/suggestion-panel state and
// its own Thread instance — the unit the tab strip below multiplexes. With
// zero open tabs, Shell renders exactly one of these (visually and
// behaviorally identical to the single-conversation view from before tabs
// existed). With tabs open, one mounts per open conversation; the inactive
// ones are hidden via CSS rather than unmounted, so their own Thread
// instance's assignment-claim heartbeat (Thread's own effect, keyed on
// conversation?.id) keeps running and the claim stays held — closing a tab
// is what actually unmounts its pane and releases the claim (see
// closeConversationTab in Shell).
function ConversationPane({
  conversationId, visible, currentUser, isAdmin, isElevatedOrSupport, autoRegenerateStaleMinutes,
  reloadConversations, onAssignmentChanged, onDraftDirtyChange, onUntoldCustomerChange = null,
  registerPaneApi, unregisterPaneApi, onOpenConversationTab, onRequestClose, onDecided, onInactivityPrompt,
  onOrderSummaryEmailChange, parkingEnabled = false, onParked,
  latestSuggestionId = null,
  // An admin not in View as Agent (2026-10-16): the shadow-draft comparison.
  showShadowCompare = false,
  // The contact tab strip (2026-10-21): this contact's number, customer,
  // timer state and latest customer message, from this pane's own detail and
  // assignment heartbeat.
  onTabMeta = null,
}) {
  const [detail, setDetail] = useState(null)
  const [assignmentState, setAssignmentState] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [suggestionRefreshSignal, setSuggestionRefreshSignal] = useState({ key: 0, skipDraft: false, forcedDraftText: undefined })

  useEffect(() => {
    if (!conversationId) { setDetail(null); return }
    let alive = true
    setDetailLoading(true)
    // The header's order reads start now, beside this request, not after it
    // (2026-10-29, orderSummaryPrefetch.js); the header takes them.
    prefetchContactOrderSummary(conversationId)
    fetchConversation(conversationId)
      .then((d) => { if (alive) setDetail(d) })
      .catch(console.error)
      .finally(() => { if (alive) setDetailLoading(false) })
    return () => { alive = false }
  }, [conversationId])

  const refreshDetail = useCallback(() => {
    if (!conversationId) return
    fetchConversation(conversationId).then((d) => setDetail(d)).catch(console.error)
  }, [conversationId])

  const bumpSuggestionRefresh = useCallback((forcedDraftText) => {
    setSuggestionRefreshSignal((s) => (
      forcedDraftText != null
        ? { key: s.key + 1, skipDraft: false, forcedDraftText }
        : { key: s.key + 1, skipDraft: true, forcedDraftText: undefined }
    ))
  }, [])

  // A new suggestion while the contact is open (2026-10-13): a new buyer
  // message regenerates an open contact at ingest, and the conversation
  // list's existing 10 s poll carries the newest suggestion's id. When it
  // changes, re-fetch the thread and the suggestion through the panel's
  // normal refresh (its unsaved edits are kept; it shows "New buyer message:
  // a new draft is available" when it holds a newer one). No poller of its own.
  const seenSuggestionIdRef = useRef(latestSuggestionId)
  useEffect(() => {
    const seen = seenSuggestionIdRef.current
    seenSuggestionIdRef.current = latestSuggestionId
    if (!conversationId || !latestSuggestionId || !seen || seen === latestSuggestionId) return
    refreshDetail()
    setSuggestionRefreshSignal((s) => ({ key: s.key + 1, skipDraft: false, forcedDraftText: undefined }))
  }, [latestSuggestionId, conversationId, refreshDetail])

  // Exposes the same refresh surface handleManualActionCompleted used at the
  // old single-pane Shell level, so ManualActionsSidebar (a Shell-level
  // sibling, not nested inside any one pane) can drive whichever pane is
  // currently active without Shell needing to know that pane's internals.
  useEffect(() => {
    if (!conversationId) return
    registerPaneApi(conversationId, { refreshDetail, bumpSuggestionRefresh })
    return () => unregisterPaneApi(conversationId)
  }, [conversationId, registerPaneApi, unregisterPaneApi, refreshDetail, bumpSuggestionRefresh])

  const handleGenerated = useCallback(() => {
    reloadConversations()
    refreshDetail()
  }, [reloadConversations, refreshDetail])

  const handleSpamMarked = useCallback(() => {
    reloadConversations()
    refreshDetail()
  }, [reloadConversations, refreshDetail])

  const handleIntentOverrideChanged = useCallback(() => {
    reloadConversations()
    refreshDetail()
    setSuggestionRefreshSignal((s) => ({ key: s.key + 1, skipDraft: false }))
  }, [reloadConversations, refreshDetail])

  // The untold-customer reminder (2026-10-30, SuggestionPanel.jsx's
  // remindUntoldCustomer()): registered per pane for Shell's leave guards.
  const handleUntoldCustomerChange = useCallback((remind) => {
    onUntoldCustomerChange?.(conversationId, remind)
  }, [conversationId, onUntoldCustomerChange])
  const handleDraftDirtyChange = useCallback((isDirty) => {
    onDraftDirtyChange(conversationId, isDirty)
  }, [conversationId, onDraftDirtyChange])

  const handleDecided = useCallback((_status, extra) => {
    onDecided(conversationId, extra)
  }, [conversationId, onDecided])

  const handleDeselect = useCallback(() => {
    onRequestClose(conversationId)
  }, [conversationId, onRequestClose])

  // What this contact's tab shows (contactTabs.js). The detail stays the last
  // one loaded when a refresh fails (a contact reassigned or escalated away
  // from this viewer), so its number still shows.
  useEffect(() => {
    if (!conversationId || !onTabMeta) return
    onTabMeta(conversationId, {
      convNumber: detail?.id === conversationId ? (detail.convNumber ?? null) : null,
      customerSource: detail?.id === conversationId ? { channel: detail.channel, metadata: detail.metadata, customer: detail.customer, customerEmail: detail.customerEmail ?? null } : null,
      status: detail?.id === conversationId ? detail.status : null,
      parks: detail?.id === conversationId ? (detail.parks ?? null) : null,
      assignment: assignmentState,
      lastInboundId: detail?.id === conversationId ? latestInboundId(detail) : null,
    })
  }, [conversationId, detail, assignmentState, onTabMeta])

  return (
    <Box sx={{ display: visible ? 'flex' : 'none', flex: 1, minHeight: 0, flexDirection: 'column', overflow: 'hidden' }}>
      <Thread
        conversation={detail}
        loading={detailLoading}
        onDecided={handleDecided}
        onGenerated={handleGenerated}
        onSpamMarked={handleSpamMarked}
        onIntentOverrideChanged={handleIntentOverrideChanged}
        suggestionRefreshKey={suggestionRefreshSignal.key}
        refreshSkipDraft={suggestionRefreshSignal.skipDraft}
        forcedDraftText={suggestionRefreshSignal.forcedDraftText}
        currentUser={currentUser}
        isAdmin={isAdmin}
        showShadowCompare={showShadowCompare}
        isElevatedOrSupport={isElevatedOrSupport}
        autoRegenerateStaleMinutes={autoRegenerateStaleMinutes}
        onDraftDirtyChange={handleDraftDirtyChange}
        onUntoldCustomerChange={handleUntoldCustomerChange}
        onDeselect={handleDeselect}
        onAssignmentChanged={onAssignmentChanged}
        onOpenConversationTab={onOpenConversationTab}
        onOtherConversationClosed={handleGenerated}
        onInactivityPrompt={onInactivityPrompt}
        onOrderSummaryEmailChange={onOrderSummaryEmailChange}
        parkingEnabled={parkingEnabled}
        onParked={onParked}
        onParkChanged={handleGenerated}
        onAssignmentState={setAssignmentState}
      />
    </Box>
  )
}

// Tab strip — appears above the Thread pane only once a second conversation
// has been opened (a related-contacts banner, Other Messages, the related
// chip; see Shell's tabIds). One tab per open conversation; closing one is the
// same "stop viewing" semantics as the single-pane X button always had (see
// closeConversationTab in Shell).
// Each tab (2026-10-21, contactTabs.js) shows the contact's number, its
// customer as the Queue row shows them (full value in a tooltip) and its
// live handle-time timer (the header's value and state: running, paused or
// parked), ticking every second from data already loaded. "n contacts open"
// starts the strip; the active tab is bold with a 3px primary top border on
// the page background; a background tab whose contact got a new customer
// message shows a dot until it is opened.
function TabStrip({ tabIds, activeId, metaById, rowsById = new Map(), seenInbound = {}, onSwitch, onClose }) {
  const [, forceTick] = useState(0)
  const anyRunning = tabIds.some((id) => tabTimer(metaById.get(id) ?? {})?.state === 'running')
  useEffect(() => {
    if (!anyRunning) return
    const t = setInterval(() => forceTick((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [anyRunning])
  return (
    <Box data-testid="contact-tab-strip" role="tablist" sx={{ display: 'flex', alignItems: 'stretch', borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover', flexShrink: 0, overflowX: 'auto' }}>
      <Typography variant="body2" data-testid="contact-tabs-count" sx={{ display: 'flex', alignItems: 'center', px: 1.5, fontWeight: 700, color: 'text.primary', flexShrink: 0, whiteSpace: 'nowrap' }}>
        {tabsOpenLabel(tabIds.length)}
      </Typography>
      {tabIds.map((id) => {
        const meta = metaById.get(id) ?? {}
        const row = rowsById.get(id) ?? null
        const active = id === activeId
        const number = tabNumber({ convNumber: meta.convNumber ?? row?.convNumber ?? null }, id)
        const customer = tabCustomer(row, meta.customerSource)
        const timer = tabTimer(meta)
        const dot = hasNewMessage(seenInbound, id, meta.lastInboundId ?? null, active)
        return (
          <Box
            key={id}
            role="tab"
            data-testid={`conversation-tab-${id}`}
            data-active={active ? 'true' : 'false'}
            aria-selected={active}
            onClick={() => onSwitch(id)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 1.5, minHeight: 36, cursor: 'pointer',
              borderRight: 1, borderRightColor: 'divider', flexShrink: 0, maxWidth: 360,
              borderTop: '3px solid', borderTopColor: active ? 'primary.main' : 'transparent',
              bgcolor: active ? 'background.default' : 'action.selected',
              color: 'text.primary',
            }}
          >
            {dot && <Box component="span" data-testid={`conversation-tab-dot-${id}`} aria-label="New customer message" title="New customer message" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />}
            <Typography variant="body2" data-testid="conversation-tab-number" sx={{ fontWeight: active ? 700 : 400, color: 'text.primary', whiteSpace: 'nowrap' }}>{number}</Typography>
            {customer && (
              <Tooltip title={customer}>
                <Typography variant="body2" data-testid="conversation-tab-customer" noWrap sx={{ fontWeight: active ? 700 : 400, color: 'text.primary', minWidth: 0 }}>{customer}</Typography>
              </Tooltip>
            )}
            {timer && (
              <Typography variant="body2" data-testid="conversation-tab-timer" data-state={timer.state} title="Handle time" sx={{ fontFamily: timer.state === 'parked' ? undefined : 'monospace', fontWeight: active ? 700 : 400, color: 'text.primary', whiteSpace: 'nowrap' }}>
                {timer.state === 'paused' ? `${timer.text} (paused)` : timer.text}
              </Typography>
            )}
            <IconButton size="small" aria-label={`close tab ${id}`} onClick={(e) => { e.stopPropagation(); onClose(id) }} sx={{ p: 0.25 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        )
      })}
    </Box>
  )
}

// Shell-level, genuinely blocking check-in — "Are you still working on this
// contact?" — for any open claim held 15+ minutes (see
// repositories/assignments.js's pauseIfDue(), surfaced via each Thread
// instance's existing 30s heartbeat tick, bubbled up regardless of which
// tab has focus). A real MUI Dialog (not the non-blocking DraggableModal
// every other dialog in this app uses), following the house style of the
// two other genuinely-blocking dialogs (TemplatesPage.jsx's
// DeleteConfirmDialog, FlowsPage.jsx's FlowToggleConfirmDialog):
// disableEscapeKeyDown and no onClose at all -- there is no dismiss path
// except answering a row, which is the whole point. Lists every entry in
// `prompts` together (one combined check-in, not sequential per-contact
// prompts) with its own Yes/No and busy/error state, since different
// contacts can get different answers.
function InactivityCheckInDialog({ prompts, conversationsById, onResolved }) {
  const entries = Object.entries(prompts)
  const [busyId, setBusyId] = useState(null)
  const [errorById, setErrorById] = useState({})

  async function respond(conversationId, pauseId, yes) {
    setBusyId(conversationId)
    setErrorById((prev) => ({ ...prev, [conversationId]: null }))
    try {
      if (yes) {
        await confirmStillWorking(conversationId, pauseId)
      } else {
        await releaseAssignment(conversationId, pauseId)
      }
      onResolved(conversationId, yes)
    } catch (err) {
      setErrorById((prev) => ({ ...prev, [conversationId]: err.message }))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Dialog open={entries.length > 0} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>Still working on {entries.length > 1 ? 'these contacts' : 'this contact'}?</DialogTitle>
      <DialogContent>
        {entries.map(([conversationId, { pauseId }]) => {
          const meta = conversationsById.get(conversationId)
          const label = meta?.convNumber ?? meta?.externalConversationId ?? conversationId
          const busy = busyId === conversationId
          return (
            <Box key={conversationId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>{label}</Typography>
                <Typography variant="caption" color="text.secondary">Claimed for 15+ minutes</Typography>
                {errorById[conversationId] && <Alert severity="error" sx={{ mt: 0.5 }}>{errorById[conversationId]}</Alert>}
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button size="small" disabled={busy} onClick={() => respond(conversationId, pauseId, false)}>No</Button>
                <Button size="small" variant="contained" disabled={busy}
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                  onClick={() => respond(conversationId, pauseId, true)}>
                  Yes
                </Button>
              </Stack>
            </Box>
          )
        })}
      </DialogContent>
    </Dialog>
  )
}

function Shell({ colorMode, onToggleColorMode }) {
  // The signed-in identity (2026-10-30, useSignedInIdentity.js): /api/me,
  // else MSAL's active account, never the cache's first account.
  const { name: accountName } = useSignedInIdentity()
  const [conversationListWidth, startConversationListDrag] = useResizableWidth('conversationList', 190, { min: 160, max: 480 })
  const [actionSidebarOpen, setActionSidebarOpen] = useState(false)
  // The shared "are you sure?" dialog (ConfirmDialog.jsx, confirmPrompts.js).
  const [askConfirm, confirmDialog] = useConfirmDialog()
  // As condensed as possible now that every button opens its own
  // DraggableModal instead of an inline form — this only needs to hold a
  // column of button labels on one line each (the longest, "Cancel
  // Replacement Order", is the floor), not a full form anymore. Right-docked,
  // so its resize handle sits on its LEFT edge (the panel is to the right of
  // its handle) — invert: true for the same reason the assistant's is.
  const [actionSidebarWidth, startActionSidebarDrag] = useResizableWidth('actionSidebar', 230, { min: 190, max: 360, invert: true })
  const [assistantOpen, setAssistantOpen] = useState(false)
  // Width lives here, not inside AssistantChat, because the layout has to know
  // it: the assistant DOCKS (shrinks the app) rather than floating over it. The
  // manual-actions sidebar deliberately does the opposite — it overlays so a CSR
  // can copy between it and the panel behind. The assistant is the other case:
  // you read the AI Suggestion *while* asking about it, so covering it defeats
  // the point.
  const [assistantWidth, startAssistantDrag] = useResizableWidth('assistantChat', 380, { min: 280, max: 720, invert: true })
  const [me, setMe] = useState(null)
  // Lifted here (from its own original spot much further down, alongside the
  // rest of the roles/isAdmin block) so goToNavPage below — declared before
  // that block — can read it without a temporal-dead-zone crash.
  const [viewingAsAgent, setViewingAsAgent] = useState(false)
  const [mode, setMode] = useState('agent')
  const [conversations, setConversations] = useState([])
  const [convTotal, setConvTotal] = useState(0)
  const [queueTotal, setQueueTotal] = useState(null) // non-closed total (open+pending+escalated)
  // Persisted (localStorage — see useLocalStorageState above): status, intent,
  // company, date range, sort, and the review/spam/actedByUser/automated
  // toggles below all survive a reload now, so a CSR's filtered view doesn't
  // reset itself away every time the tab refreshes. Channel/To-Address stay
  // plain useState (unchanged) — they're purely client-side re-filters of an
  // already-fetched page (ConversationList's own `filtered` memo), not part
  // of this persistence pass.
  const [dateFrom, setDateFrom] = useLocalStorageState('dateFrom', '')
  const [dateTo, setDateTo] = useLocalStorageState('dateTo', '')
  // Lifted up (not ConversationList-local) so it can drive the server-side
  // fetch below — the status filter is what's ACTUALLY rendered, and total/
  // "Load more" must be scoped to the same query, not a client-side re-filter
  // of an arbitrary oldest/newest page slice.
  // Roles aren't known yet at mount (me is still null) — always starts CSR-
  // shaped; the effect below upgrades it once roles resolve and say admin.
  const [statusFilter, setStatusFilter] = useLocalStorageState('statusFilter', CSR_DEFAULT_STATUS_FILTER)
  // Lifted up (not ConversationList-local) for the same reason as
  // statusFilter above — intent filtering now runs server-side too (it used
  // to be a client-side re-filter of an already-fetched page, which could
  // hide most of the actual queue the same way status filtering used to).
  const [intentFilter, setIntentFilter] = useLocalStorageState('intentFilter', [])
  // Lifted for the same server-side reason as statusFilter/intentFilter above
  // (was a client-side re-filter of an already-fetched page in
  // ConversationList — see that component's own filtered memo). Plain
  // useState, NOT useLocalStorageState — this one is deliberately reset on
  // every nav-page entry (goToNavPage below) and by typing a search, so
  // persisting it across sessions would fight both of those.
  const [sinceFilter, setSinceFilter] = useState('all')
  // Lifted for the same server-side reason as statusFilter/intentFilter above
  // (2026-09-25) — free-text/ID search now runs server-side (see
  // conversations.js's listConversations() q-branch) instead of re-filtering
  // an already-fetched page client-side, which could hide a real match that
  // simply hadn't been paged in yet. Plain useState, not useLocalStorageState
  // — same reasoning as sinceFilter: a search shouldn't persist across
  // sessions. ConversationList still owns the raw/debounced TextField state
  // itself (for responsive typing) and reports the debounced value up here.
  const [searchQuery, setSearchQuery] = useState('')
  // Lifted for a stronger reason than the two above: a company that hasn't been
  // surfaced yet (company_settings.surfaced=false — how AM_EBAY is held dark
  // while it ingests) is withheld by the server unless the request NAMES it, so
  // no client-side re-filter could ever reveal it. Brand codes ('1A'/'AM'), the
  // same values the toggle row renders.
  // Key bumped from 'companyFilter' when the default widened to both brands
  // (2026-09-29): every browser had the old 1A-only default saved, so without
  // a new key nobody would ever see the new one. Resets each saved choice once.
  const [companyFilter, setCompanyFilter] = useLocalStorageState('companyFilterV2', DEFAULT_COMPANY_FILTER)
  const [sortBy, setSortBy] = useLocalStorageState('sortBy', 'oldest')
  // The Queue's priority levels (intent_priorities, migration 0093), the same
  // map the server's ORDER BY reads, for the 10s poll's own re-sort below.
  // Loaded on mount, refreshed every minute, and at once when an admin changes
  // a level in Settings → Prioritization (INTENT_PRIORITIES_CHANGED_EVENT).
  // A ref too, so the poll's interval reads the latest without restarting.
  const [intentPriorities, setIntentPriorities] = useState(null)
  const intentPrioritiesRef = useRef(null)
  // Who is looking, for the task contacts' toggle rule (2026-10-14), read by
  // the live-update callbacks below.
  const queueToggleViewerRef = useRef(null)
  useEffect(() => {
    // From the shared 30 s session tick (sessionTick.js); best-effort: until
    // it arrives, the list keeps the server's own order.
    const unsubscribe = sessionTick.subscribe((tick) => {
      const p = tick.intentPriorities
      if (!p?.tiers) return
      // Only a real change replaces the state (and re-sorts the list).
      if (JSON.stringify(p) === JSON.stringify(intentPrioritiesRef.current)) return
      intentPrioritiesRef.current = p
      setIntentPriorities(p)
    })
    const reload = () => { sessionTick.refresh() }
    window.addEventListener(INTENT_PRIORITIES_CHANGED_EVENT, reload)
    return () => { unsubscribe(); window.removeEventListener(INTENT_PRIORITIES_CHANGED_EVENT, reload) }
  }, [])
  // Scoped to the Spam intent filter (meaningless for any other intent) —
  // 'Yes' | 'No' | 'All'. Resets to 'No' whenever intentFilter transitions
  // TO 'spam' (see the effect below) so admins land on what needs
  // review, but otherwise persists freely once set.
  const [reviewedByMgmtFilter, setReviewedByMgmtFilter] = useLocalStorageState('reviewedByMgmtFilter', 'No')
  // Scoped to the Spam intent filter, same as reviewedByMgmtFilter above —
  // 'rule' and/or 'agent'. Both selected (the default) means unfiltered
  // ("All"); lifted to Shell for the same reason (drives the server-side
  // fetch).
  const [spamSetByFilter, setSpamSetByFilter] = useLocalStorageState('spamSetByFilter', ['rule', 'agent'])
  // Filter by acting user (any CXHub user, any role) and to automated
  // contacts (successful or failed) — both independent of intent, unlike
  // reviewedByMgmt/spamSetBy above.
  const [actedByUserFilter, setActedByUserFilter] = useLocalStorageState('actedByUserFilter', [])
  const [automatedFilter, setAutomatedFilter] = useLocalStorageState('automatedFilter', false)
  const [actedByUserOptions, setActedByUserOptions] = useState([])
  useEffect(() => { fetchConversationActors().then(setActedByUserOptions) }, [])
  // Channel is otherwise a purely client-side re-filter of the already-
  // fetched page (see ConversationList's own `filtered` memo) — lifted to
  // Shell anyway (unlike every other purely-client filter) solely so
  // includeToAddressFilter below can tell whether it's currently in scope:
  // sending it to the server while Channel is showing eBay only would
  // silently filter the eBay view down to nothing (eBay conversations carry
  // no toAddress at all).
  const [channelFilter, setChannelFilter] = useState(DEFAULT_CHANNEL_FILTER)
  // Tasks (Phase 1, 2026-10-13): the Tasks channel is an option, and in the
  // default selection (Queue and Closed share this filter), only for someone
  // who may use Tasks (taskContact.js).
  const myDefaultChannels = useMemo(() => defaultChannelFilterFor(DEFAULT_CHANNEL_FILTER, me), [me])
  const myChannelOptions = useMemo(() => channelOptionsFor(KNOWN_CHANNELS, me), [me])
  useEffect(() => {
    setChannelFilter((prev) => (prev.length === DEFAULT_CHANNEL_FILTER.length && DEFAULT_CHANNEL_FILTER.every((c) => prev.includes(c)) ? myDefaultChannels : prev))
  }, [myDefaultChannels])
  // "To Address" filter — server-side (routes/conversations.js's
  // includeToAddress param), only ever meaningful/sent while channelFilter
  // includes m365_email (see reloadConversations/loadMoreConversations
  // below). INCLUDE-style state (which addresses to SHOW) as of 2026-09-24
  // — an earlier exclude-style version could never hide an m365_email row
  // whose toAddress was NULL or not a registered address at all (nothing to
  // match against a "hide these" list), which is exactly how prod contact
  // 4418852 leaked through a supposedly-narrowed filter (see
  // docs/decisions.md). Empty here is a real, deliberate "show none" (the
  // CSR unticked every address) — see includeToAddressParam below for how
  // that's distinguished from "filter not in scope"/"every address ticked"
  // (both of which send nothing to the server). Options (for rendering the
  // checkbox list) still come from the registry (channels/m365/config.js's
  // MAILBOXES), not invented client-side. Starts empty; the auto-select
  // effect just below sets the real default the moment toAddressFilterOptions
  // loads.
  const [includeToAddressFilter, setIncludeToAddressFilter] = useState([])
  // The registry's directory ({ address, company, testMailbox }); the checklist
  // renders just the addresses (toAddressFilterOptions).
  const [toAddressDirectory, setToAddressDirectory] = useState([])
  useEffect(() => { fetchM365Addresses().then(setToAddressDirectory) }, [])
  // NO_TO_ADDRESS first: the "No To address" option (2026-10-30).
  const toAddressFilterOptions = useMemo(() => [NO_TO_ADDRESS, ...toAddressDirectory.map((d) => d.address)], [toAddressDirectory])
  // Auto-select re-applies (overwriting whatever the agent may have manually
  // picked in the To Address checklist since) every time the Company toggle
  // or Channel selection changes while m365_email is an active channel — a
  // genuine reactive rule that also supplies includeToAddressFilter's real
  // initial value once toAddressFilterOptions loads (its own useState above
  // starts empty, since the default depends on data not yet fetched at mount
  // time). A no-op while m365_email isn't selected — this filter is already
  // only sent to the server in that case (see includeToAddressParam below),
  // so there's nothing to keep in sync.
  //
  // General rule, not special-cased per company combination: an address is
  // grouped by the mailbox that owns it, from the server's registry, never by
  // its email domain (2026-09-30), so a new mailbox or alias needs no change
  // here — see toAddressAutoSelect.js's computeIncludeToAddress() for the
  // actual rule (extracted so it's unit-testable with node --test, no React
  // harness available).
  useEffect(() => {
    if (!channelFilter.includes('m365_email')) return
    setIncludeToAddressFilter(defaultIncludeToAddress({ companyFilter, toAddressDirectory }))
  }, [channelFilter, companyFilter, toAddressDirectory])
  // Which nav rail icon is highlighted. Decoupled from `mode` because Queue
  // and Closed are BOTH mode === 'agent' with different filter presets — mode
  // alone can't tell them apart for highlighting purposes.
  const [navPage, setNavPage] = useState('queue')
  // The address bar shows what is on screen (2026-10-19; viewUrl.js), so a
  // refresh lands where the person was. Refs, because goToNavPage is declared
  // before selectedId. convNumberByIdRef: numbers learned from a selection, a
  // deep link or a lookup, for a contact not in the loaded list (a tab opened
  // from Other Messages).
  const navPageRef = useRef('queue')
  navPageRef.current = navPage
  const selectedIdRef = useRef(null)
  const conversationsRef = useRef(conversations)
  conversationsRef.current = conversations
  const convNumberByIdRef = useRef(new Map())
  const convNumberOf = useCallback((id) => (id == null ? null : (conversationsRef.current.find((c) => c.id === id)?.convNumber ?? convNumberByIdRef.current.get(id) ?? null)), [])
  // replaceState, never pushState: no new history entry. A primary contact
  // whose number is not known yet gets '/' now and its /contact/<n> once a
  // lookup finds it, if it is still the one on screen.
  const syncUrlToView = useCallback(({ page, primaryId = null }) => {
    const convNumber = page === 'queue' ? convNumberOf(primaryId) : null
    const path = viewPath({ page, convNumber, currentPath: window.location.pathname })
    if (window.location.pathname !== path) window.history.replaceState({}, '', path)
    if (page === 'queue' && primaryId != null && convNumber == null) {
      fetchConversation(primaryId).then((c) => {
        if (c?.convNumber == null) return
        convNumberByIdRef.current.set(primaryId, c.convNumber)
        if (selectedIdRef.current === primaryId && navPageRef.current === 'queue') window.history.replaceState({}, '', convNumberPath(c.convNumber))
      }).catch(() => {})
    }
  }, [convNumberOf])
  // Lifted out of ConversationList (was local state there) so nav rail clicks
  // (Closed opens the panel, Queue collapses it) can drive it from up here.
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [navRailCollapsed, setNavRailCollapsed] = useState(() => localStorage.getItem(NAV_RAIL_COLLAPSED_KEY) === 'true')
  const toggleNavRailCollapsed = useCallback(() => {
    setNavRailCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(NAV_RAIL_COLLAPSED_KEY, String(next))
      return next
    })
  }, [])
  // Each rail click deterministically resets to that destination's canonical
  // state — Queue and Closed are fixed, predictable destinations, not "go
  // there and leave whatever filters happened to be set."
  // The New view's unsent email (2026-10-08): leaving it by the rail asks
  // first, and closing or reloading the tab gets the browser's own prompt.
  // Nothing is saved as a draft.
  const newEmailDirtyRef = useRef(false)
  const setNewEmailDirty = useCallback((dirty) => { newEmailDirtyRef.current = dirty }, [])
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!newEmailDirtyRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])
  const goToNavPage = useCallback(async (page) => {
    if (page !== 'new' && newEmailDirtyRef.current) {
      if (!(await askConfirm(DISCARD_EMAIL_PROMPT))) return
      newEmailDirtyRef.current = false
    }
    setNavPage(page)
    localStorage.setItem(NAV_PAGE_KEY, page)
    // The URL follows the page (2026-10-19): leaving the queue clears
    // /contact/<n>, returning to it with a contact still selected restores
    // it; Tasks has /tasks, Parked keeps /parked; everything else is '/'.
    syncUrlToView({ page, primaryId: selectedIdRef.current })
    if (page === 'queue') {
      setMode('agent')
      // Same isAdmin computation as the roles/isAdmin block further down
      // (effectiveIsElevated) — inlined here since that block is declared
      // later in this component and goToNavPage only closes over `me`/
      // `viewingAsAgent`, not those derived consts.
      setStatusFilter(defaultStatusFilter((me?.roles ?? []).includes('admin') && !viewingAsAgent))
      setActedByUserFilter([])
      setFiltersOpen(false)
      setSortBy('oldest')
      setSinceFilter('all')
      // Support access: every Queue click resets the toggles to all three.
      const queueIsAdmin = (me?.roles ?? []).includes('admin') && !viewingAsAgent
      if (hasSupportToggleAccess(me, { isAdmin: queueIsAdmin })) setCompanyFilter(defaultToggleSelection(me, { isAdmin: queueIsAdmin }))
    } else if (page === 'closed') {
      setMode('agent')
      setStatusFilter(['closed'])
      // NAME first, not username -- decided_by/closed_by are themselves
      // written server-side with name preferred over username (see
      // routes/ai.js, routes/conversations.js), so this filter has to match
      // that same string to actually find "my own" decisions. Every other
      // `me.name ?? me.username` spot in this file (ConversationList/
      // ConversationPane's currentUser prop) exists for the same reason --
      // matching routes/assignments.js's callerIdentity(), which agrees with
      // this same name-first order deliberately (see its own comment).
      setActedByUserFilter([me ? (me.name ?? me.username ?? '') : ''])
      setFiltersOpen(true)
      setSortBy('newest')
      // Closed defaults to a narrow Timeframe (Last 7 days) — without it,
      // entering Closed re-fetches (and the CSR waits on) the entire closed
      // archive by default. EVERY entry into Closed, not just the first one
      // in a session, so a CSR who widened it to All time earlier gets the
      // narrow default again on their next visit.
      setSinceFilter('7')
    } else if (page === 'parked') {
      setMode('parked')
    } else if (page === 'new') {
      setMode('new')
    } else if (page === 'tasks') {
      setMode('tasks')
    } else if (page === 'ops') {
      setMode('ops')
    } else if (page === 'audit') {
      setMode('audit')
    } else if (page === 'settings') {
      setMode('settings')
    } else if (page === 'reporting') {
      setMode('reporting')
    } else if (page === 'my-stats') {
      setMode('my-stats')
    }
  }, [me, viewingAsAgent, syncUrlToView, askConfirm])
  const prevIntentFilterRef = useRef(intentFilter)
  useEffect(() => {
    if (intentFilter.includes('spam') && !prevIntentFilterRef.current.includes('spam')) {
      setReviewedByMgmtFilter('No')
    }
    prevIntentFilterRef.current = intentFilter
  }, [intentFilter])
  const [listLoading, setListLoading] = useState(true)
  // Surfaced load failures: the old `.catch(console.error)` made a dead
  // backend indistinguishable from an empty queue ("No conversations yet.").
  const [fetchError, setFetchError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  selectedIdRef.current = selectedId
  // The manual-actions sidebar's own toggle tab is hidden with no
  // conversation selected (nothing useful to toggle) — if a conversation is
  // deselected while the panel is open, close it too, so it can never get
  // stuck open with no visible control left to close it.
  useEffect(() => { if (!selectedId) setActionSidebarOpen(false) }, [selectedId])
  // Extra conversations opened alongside selectedId via the Other Messages
  // modal's "Open Conversation" link — selectedId is conceptually "the first
  // tab"; each entry here is one more. Each id in the combined set
  // (selectedId + these) gets its OWN ConversationPane (own detail fetch, own
  // draft-dirty tracking, own assignment-claim heartbeat via its own Thread
  // instance) — see tabIds/ConversationPane below. Empty (the common case)
  // means no tab strip at all: a single pane, identical to the pre-tabs view.
  const [openTabs, setOpenTabs] = useState([])
  // Which open tab is currently displayed. null means "whatever selectedId
  // is" — the common/default case, and what every non-tab code path (list
  // selection, decisions, etc.) resets it back to.
  const [activeTabId, setActiveTabId] = useState(null)
  // The contact tab strip (2026-10-21): what each open contact's pane
  // reported (number, customer, timer state, latest customer message), and
  // the latest customer message the viewer has had on screen per tab, for
  // the new-message dot.
  const [tabMetaById, setTabMetaById] = useState({})
  const [seenInbound, setSeenInbound] = useState({})
  const handleTabMeta = useCallback((id, meta) => setTabMetaById((prev) => ({ ...prev, [id]: meta })), [])
  // Bumped whenever the CURRENT user's own action releases or takes over a
  // conversation's assignment (Thread's onAssignmentChanged) — passed to
  // ConversationList as a prop so its polling effect fires an immediate tick
  // instead of waiting up to 10s for the next one. Only makes the acting
  // agent's own list instant; other agents still see it within the existing
  // ~10s cadence (no websocket/real-time infra is being added for this).
  const [assignmentNonce, setAssignmentNonce] = useState(0)
  const handleAssignmentChanged = useCallback(() => setAssignmentNonce((n) => n + 1), [])
  // conversationId -> { pauseId, pausedAt } for every open tab (regardless of
  // which one has focus — see Thread's own heartbeat-tick comment) currently
  // awaiting an inactivity check-in response ("Are you still working on this
  // contact?", see InactivityCheckInDialog below). Non-empty renders ONE
  // combined blocking dialog listing every entry — never a per-tab modal.
  const [inactivityPrompts, setInactivityPrompts] = useState({})
  const handleInactivityPrompt = useCallback((conversationId, prompt) => {
    setInactivityPrompts((prev) => {
      if (!prompt) {
        if (!(conversationId in prev)) return prev
        const next = { ...prev }; delete next[conversationId]; return next
      }
      if (prev[conversationId]?.pauseId === prompt.pauseId) return prev
      return { ...prev, [conversationId]: prompt }
    })
  }, [])
  // conversationId -> whether that pane's SuggestionPanel currently has
  // unsaved draft edits. A ref (not state) since it's only read at
  // selection/close time, never rendered — updated on every draft keystroke
  // would be wasteful as state. One entry per open pane (see draftDirtyRef's
  // single-conversation predecessor, before tabs existed).
  const draftDirtyMapRef = useRef(new Map())
  const handleDraftDirtyChange = useCallback((conversationId, isDirty) => {
    draftDirtyMapRef.current.set(conversationId, isDirty)
  }, [])
  // conversationId -> remind(via), the pane's untold-customer reminder
  // (2026-10-30): set while an Other Actions step's reply was never pasted
  // or sent. Asked before closing the tab or switching contacts; resolves
  // true to go ahead.
  const untoldMapRef = useRef(new Map())
  const handleUntoldCustomerChange = useCallback((conversationId, remind) => {
    if (remind) untoldMapRef.current.set(conversationId, remind)
    else untoldMapRef.current.delete(conversationId)
  }, [])
  // conversationId -> { refreshDetail, bumpSuggestionRefresh }, one entry per
  // currently-mounted ConversationPane (registered/unregistered in that
  // component's own effect). Shell-level actions that used to reach into the
  // single `detail`/`suggestionRefreshSignal` state directly (a bulk
  // generate, a manual-actions-sidebar step) now look up whichever pane is
  // ACTIVE here instead — see handleManualActionCompleted/handleBulkGenerated
  // below.
  const paneApiRegistry = useRef(new Map())
  const registerPaneApi = useCallback((id, api) => { paneApiRegistry.current.set(id, api) }, [])
  const unregisterPaneApi = useCallback((id) => { paneApiRegistry.current.delete(id) }, [])
  // Timestamp (ISO string) as of the last successful full reload — read
  // inside the incremental-poll interval below (a ref, not state, same
  // pattern as ConversationList's own assignmentRef) to know what's changed
  // since. Captured BEFORE the fetch is issued (not after it resolves) so
  // anything created/changed during the request itself is never missed by
  // the next poll.
  const lastPolledAtRef = useRef(null)

  // Stale-response guard, shared by reloadConversations/loadMoreConversations
  // below (both mutate the same `conversations` state, so a stale response
  // from EITHER must not be allowed to land after a newer one already has) —
  // rapid filter changes (e.g. quick To Address checkbox clicks) fire
  // overlapping fetches, and network/response timing gives no guarantee the
  // requests resolve in the order they were sent. Incremented before each
  // fetch; a response only applies if this ref still holds the exact value
  // captured when that fetch started, i.e. nothing newer has started since.
  const conversationsRequestSeqRef = useRef(0)

  useEffect(() => { loadMe().then(setMe).catch(console.error) }, [])
  // The Queue toggles on first load (2026-10-07): Support access lands on all
  // three (overriding the saved selection); anyone else keeps their saved
  // selection, with Support dropped silently if they have no access to it.
  const togglesInitializedRef = useRef(false)
  useEffect(() => {
    if (!me || togglesInitializedRef.current) return
    togglesInitializedRef.current = true
    setCompanyFilter((saved) => (hasSupportToggleAccess(me) ? defaultToggleSelection(me) : sanitizeToggleSelection(saved, me)))
  }, [me]) // eslint-disable-line react-hooks/exhaustive-deps
  // Roles resolve asynchronously, after statusFilter's own initial (CSR-
  // shaped) value is already set — an admin whose stored/initial filter is
  // still exactly the CSR default gets upgraded to the admin default once
  // roles come back. Never overrides a filter the user chose themselves
  // (anything other than exactly the old default) — e.g. an admin who
  // narrowed the filter to just ['open'] before roles resolved keeps that.
  // viewingAsAgent can't be true yet at this point (its own toggle requires
  // roles to already show admin), so it's deliberately left out of this
  // one-time check.
  useEffect(() => {
    if (!me?.roles?.includes('admin')) return
    setStatusFilter((prev) => (isDefaultStatusFilter(prev, false) ? defaultStatusFilter(true) : prev))
  }, [me])

  // reviewedByMgmt is only ever sent scoped to the Spam intent filter — it's
  // meaningless (and ignored server-side too) for any other intent.
  const reviewedByMgmtParam = intentFilter.includes('spam') && reviewedByMgmtFilter !== 'All'
    ? reviewedByMgmtFilter === 'Yes'
    : undefined

  // Same scoping/undefined-means-unfiltered convention as reviewedByMgmtParam
  // above — both values selected means "All", same as no filter at all.
  const spamSetByParam = intentFilter.includes('spam') && spamSetByFilter.length === 1
    ? spamSetByFilter
    : undefined

  // Independent of intent (unlike reviewedByMgmtParam/spamSetByParam above).
  const actedByUserParam = actedByUserFilter.length > 0 ? actedByUserFilter : undefined
  const automatedParam = automatedFilter ? true : undefined

  // Only ever sent while Channel includes m365_email — sending it otherwise
  // would silently filter out every non-m365 conversation (e.g. eBay, which
  // never has a toAddress at all), since the server condition simply has
  // nothing to match. See ConversationList's own "To Address" FormControl,
  // gated on the exact same condition. Sent whenever the CSR's selection is
  // a STRICT SUBSET of the known options — including the empty subset (every
  // address unticked), a real "show none" request, not "no opinion" (see
  // includeToAddressFilter's own comment and api.js's fetchConversations(),
  // which sends this even when empty rather than omitting it the way every
  // other `.length > 0 ? arr : undefined` include-style param here does).
  // Every address ticked is the one case that DOES mean unfiltered — same as
  // the old exclude-style version's own empty-set case.
  const includeToAddressParam = channelFilter.includes('m365_email') && includeToAddressFilter.length < toAddressFilterOptions.length ? includeToAddressFilter : undefined

  // Sent whenever anything is selected — including the "all companies selected"
  // case, which is still an explicit request for exactly those brands, not
  // "unfiltered" (the server treats an absent filter as "surfaced companies
  // only"). Deselecting everything sends nothing and the client-side filter in
  // ConversationList renders the empty result. Memoized on the joined value so
  // the array identity doesn't re-fire the fetch effects on every render.
  // The Queue toggles go to the server as `toggles` (2026-10-07): always
  // sent, an empty string when nothing is selected (nothing shown). The
  // server applies the same company + team rule and ignores Support for
  // anyone without access.
  const companyParam = useMemo(() => companyFilter.join(','), [companyFilter])

  const reloadConversations = useCallback(() => {
    const seq = ++conversationsRequestSeqRef.current
    const capturedAt = new Date().toISOString()
    return fetchConversations({ limit: CONVERSATION_PAGE_SIZE, offset: 0, from: dateFrom || undefined, to: dateTo || undefined, status: statusFilter, intent: intentFilter.length > 0 ? intentFilter : undefined, since: sinceFilter !== 'all' ? sinceFilter : undefined, q: searchQuery || undefined, toggles: companyParam, reviewedByMgmt: reviewedByMgmtParam, spamSetBy: spamSetByParam, actedByUser: actedByUserParam, automated: automatedParam, includeToAddress: includeToAddressParam, sortBy })
      .then(({ conversations: items, total, queueTotal: qt }) => {
        // A newer reload/loadMore has started since this one — a filter
        // changed again (or Load More fired) before this response landed.
        // Applying it now would overwrite the newer, still-in-flight
        // request's eventual result with stale data.
        if (seq !== conversationsRequestSeqRef.current) return
        setFetchError(null)
        lastPolledAtRef.current = capturedAt
        setConvTotal(total)
        setQueueTotal(qt)
        setConversations(items)
      })
      .catch((e) => { if (seq === conversationsRequestSeqRef.current) { console.error(e); setFetchError(`Couldn't load conversations (${e.message})`) } })
      .finally(() => { if (seq === conversationsRequestSeqRef.current) setListLoading(false) })
  }, [dateFrom, dateTo, statusFilter, intentFilter, sinceFilter, searchQuery, companyParam, reviewedByMgmtParam, spamSetByParam, actedByUserParam, automatedParam, includeToAddressParam, sortBy])

  // Append the next page (offset = what we already have) for "Load more".
  // Shares reloadConversations()'s own sequence ref (see its comment) — a
  // reload that starts and finishes while a Load More is still in flight
  // must not have its fresh list clobbered by the Load More's stale append.
  const loadMoreConversations = useCallback(() => {
    const seq = ++conversationsRequestSeqRef.current
    return fetchConversations({ limit: CONVERSATION_PAGE_SIZE, offset: conversations.length, from: dateFrom || undefined, to: dateTo || undefined, status: statusFilter, intent: intentFilter.length > 0 ? intentFilter : undefined, since: sinceFilter !== 'all' ? sinceFilter : undefined, q: searchQuery || undefined, toggles: companyParam, reviewedByMgmt: reviewedByMgmtParam, spamSetBy: spamSetByParam, actedByUser: actedByUserParam, automated: automatedParam, includeToAddress: includeToAddressParam, sortBy })
      .then(({ conversations: items, total, queueTotal: qt }) => {
        if (seq !== conversationsRequestSeqRef.current) return
        setFetchError(null)
        setConvTotal(total)
        setQueueTotal(qt)
        setConversations((prev) => [...prev, ...items])
      })
      .catch((e) => { if (seq === conversationsRequestSeqRef.current) { console.error(e); setFetchError(`Couldn't load more conversations (${e.message})`) } })
  }, [conversations.length, dateFrom, dateTo, statusFilter, intentFilter, sinceFilter, searchQuery, companyParam, reviewedByMgmtParam, spamSetByParam, actedByUserParam, automatedParam, includeToAddressParam, sortBy])

  // Initial load + refetch whenever the date range, status filter, intent
  // filter, or reviewedByMgmt filter changes (all server-side filters now —
  // intent used to be a client-side-only re-filter of an already-fetched
  // page, which could hide most of the actual queue the same way status
  // filtering used to; see repositories/conversations.js's listConversations()
  // comment).
  useEffect(() => { setListLoading(true); reloadConversations() }, [dateFrom, dateTo, statusFilter, intentFilter, sinceFilter, searchQuery, companyParam, reviewedByMgmtParam, spamSetByParam, actedByUserParam, automatedParam, includeToAddressParam, sortBy]) // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic incremental refresh: picks up brand-new conversations and
  // status/intent changes on already-loaded ones without a manual filter
  // change or full page reload. Uses the list endpoint's `updatedSince`
  // filter (an absolute ISO timestamp against conversations.updated_at — a
  // DB-assigned column, NOT `from`/`to`, which filter on last_message_at, the
  // SOURCE message's own timestamp; see repositories/conversations.js's own
  // comment on updatedSince for why that distinction is load-bearing: a
  // last_message_at-based cutoff could permanently miss a conversation whose
  // ingest lagged behind its own message timestamp) to fetch only what
  // changed since the last poll/load, then merges those rows into the
  // already-loaded array IN PLACE (mergeAndSort) rather than replacing it
  // wholesale — so already-pulled "Load More" pages and scroll position are
  // never disrupted, and untouched rows never re-render. Separate from
  // ConversationList's own 10s assignment/ownership heartbeat (which only
  // refreshes assignmentsById for already-loaded rows) — this poll handles
  // new-arrival + status visibility, which that one doesn't cover;
  // deliberately left as its own independent poll rather than merged into it.
  //
  // dateFrom/dateTo (the CSR's own last-message date-range filter) are sent
  // alongside updatedSince, unmodified — the two conditions AND together
  // server-side, so a conversation must satisfy both "within the CSR's
  // chosen date range" and "changed since the last poll tick", exactly the
  // same as reloadConversations()'s own from/to.
  //
  // Deliberately does NOT touch convTotal/queueTotal: this query's
  // updatedSince is an incremental "since the last poll" cutoff (a few
  // seconds wide), and the server derives total/queueTotal from that SAME
  // filter (see listConversations()'s baseConditions), so they'd reflect "how
  // much changed in the last ~10s" rather than the true queue size -- almost
  // always near zero. Storing that into the shared queueTotal/convTotal
  // state stomped the correct value from reloadConversations()/
  // loadMoreConversations() every ~10s (the "queue keeps dropping to 0" bug).
  // Only those two, which query with the CSR's own actual filters, may set
  // those two totals.
  useEffect(() => {
    let alive = true
    const tick = () => {
      const pollFrom = lastPolledAtRef.current
      if (!pollFrom) return
      const capturedAt = new Date().toISOString()
      fetchConversations({
        from: dateFrom || undefined, to: dateTo || undefined, updatedSince: pollFrom,
        // Deliberately NOT status: statusFilter here (unlike
        // reloadConversations/loadMoreConversations) — this poll needs to see
        // a conversation transition OUT of the current filter (e.g. another
        // agent closing it) just as much as changes within it, or that row
        // would linger in view indefinitely (only a later full reload, e.g. a
        // filter change, would ever catch it). mergeAndSort's own
        // allowedStatuses param (passed statusFilter below) drops any row —
        // existing or freshly merged — whose status no longer matches once
        // this broader response comes back.
        intent: intentFilter.length > 0 ? intentFilter : undefined,
        // Timeframe IS applied here (unlike statusFilter above) — otherwise a
        // conversation whose lastMessageAt falls outside the current window
        // could get merged back into view just because it happened to be
        // updated in the last poll tick.
        since: sinceFilter !== 'all' ? sinceFilter : undefined,
        // q IS applied here (unlike status above) — same reasoning as intent:
        // a positive content filter, not a visibility/status transition this
        // poll exists to catch regardless of the current filter.
        q: searchQuery || undefined,
        toggles: companyParam,
        reviewedByMgmt: reviewedByMgmtParam, spamSetBy: spamSetByParam,
        actedByUser: actedByUserParam, automated: automatedParam, includeToAddress: includeToAddressParam, sortBy, limit: 200, offset: 0,
      }).then(({ conversations: changed }) => {
        if (!alive) return
        const closedOnly = statusFilter.length === 1 && statusFilter[0] === 'closed'
        setConversations((prev) => mergeAndSort(prev, changed, sortBy, closedOnly, statusFilter, intentPrioritiesRef.current, companyFilter, queueToggleViewerRef.current))
        lastPolledAtRef.current = capturedAt
      }).catch(() => {}) // best-effort, same as the assignment heartbeat's own error handling
    }
    const id = setInterval(tick, 10000)
    return () => { alive = false; clearInterval(id) }
  }, [dateFrom, dateTo, statusFilter, intentFilter, sinceFilter, searchQuery, companyParam, reviewedByMgmtParam, spamSetByParam, actedByUserParam, automatedParam, includeToAddressParam, sortBy]) // eslint-disable-line react-hooks/exhaustive-deps

  // A changed priority level re-sorts the already-loaded list at once (the
  // server's next full load sorts the same way, from the same table). Each
  // Queue row carries its own server-computed level (2026-10-29), so when
  // the priorities version moves (an admin changed an intent level or a
  // quick-reply rule, seen through the session tick) the list is reloaded
  // in full; the first map to arrive never reloads.
  const seenIntentPrioritiesRef = useRef(null)
  useEffect(() => {
    if (!intentPriorities) return
    const previous = seenIntentPrioritiesRef.current
    seenIntentPrioritiesRef.current = intentPriorities
    if (shouldReloadQueueForPriorities(previous, intentPriorities)) {
      reloadConversations()
      return
    }
    const closedOnly = statusFilter.length === 1 && statusFilter[0] === 'closed'
    setConversations((prev) => (prev.length ? [...prev].sort((a, b) => conversationComparator(a, b, sortBy, closedOnly, intentPriorities)) : prev))
    // Only a change of the levels themselves should trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentPriorities])

  // Near-immediate counterpart to the 10s poll above — pushes every
  // conversation status change (routes/conversations.js's GET
  // /status-stream, repositories/conversations.js's broadcastStatusChange())
  // the instant it happens, same experience assignment claims already get via
  // subscribeAssignmentStream. A status that no longer matches the current
  // filter drops the row immediately (matching the poll's own allowedStatuses
  // behavior); one that still matches is updated in place (e.g. open →
  // escalated) rather than waiting for the next poll or a full reload. Same
  // in-memory/single-replica caveat as the assignment stream: a second
  // Container Apps replica only reaches subscribers connected to IT.
  useEffect(() => {
    const unsubscribe = subscribeConversationStatusStream(({ conversationId, status }) => {
      setConversations((prev) => {
        if (!statusFilter.includes(status)) return prev.filter((c) => c.id !== conversationId)
        // The toggle rule depends on status (escalated), so a row it no longer
        // includes leaves; the rest re-sort (escalated first).
        const closedOnly = statusFilter.length === 1 && statusFilter[0] === 'closed'
        return prev
          .map((c) => (c.id === conversationId ? { ...c, status } : c))
          .filter((c) => c.id !== conversationId || conversationMatchesToggles(c, companyFilter, queueToggleViewerRef.current))
          .sort((a, b) => conversationComparator(a, b, sortBy, closedOnly, intentPrioritiesRef.current))
      })
    })
    return unsubscribe
  }, [statusFilter, companyFilter, sortBy])

  // The full set of currently-open conversation ids — selectedId (the
  // "first"/primary tab) plus openTabs, deduped. Empty openTabs collapses
  // this to just [selectedId] (or [] with nothing selected), the exact
  // pre-tabs shape — no tab strip renders and there's exactly one
  // ConversationPane, identical in behavior to the old single-pane Shell.
  const tabIds = openTabs.length > 0
    ? [selectedId, ...openTabs.map((t) => t.conversationId)].filter((v, i, arr) => v != null && arr.indexOf(v) === i)
    : (selectedId ? [selectedId] : [])
  const effectiveActiveId = (activeTabId != null && tabIds.includes(activeTabId)) ? activeTabId : selectedId
  useEffect(() => {
    setSeenInbound((prev) => tabIds.reduce((acc, id) => nextSeenInbound(acc, id, tabMetaById[id]?.lastInboundId ?? null, id === effectiveActiveId), prev))
  }, [tabIds.join(','), effectiveActiveId, tabMetaById])

  // Shared by every "switching away from selectedId would discard unsaved
  // draft edits" guard below (a manual click, a popstate back/forward) — one
  // prompt, not three copies drifting apart. Resolves true to go ahead.
  const confirmDiscardDraft = useCallback(async () => {
    // The customer not told about an Other Actions step yet (2026-10-30).
    const remind = untoldMapRef.current.get(selectedId)
    // It replaces the edited-reply prompt (Jenn, 2026-10-30): one prompt only.
    if (remind) return remind('switch_contact')
    if (!draftDirtyMapRef.current.get(selectedId)) return true
    return askConfirm(SWITCH_CONTACT_PROMPT)
  }, [selectedId, askConfirm])

  // Guards the existing "select a different conversation" path: switching away
  // from a conversation whose SuggestionPanel has unsaved draft edits would
  // otherwise silently discard them (the panel re-seeds the draft from the
  // newly-fetched suggestion regardless of local edits). Also brings the
  // newly-selected conversation into view even if a different tab was active.
  //
  // convNumber (optional — the row's own display id, already available on
  // every ConversationList item, per that component's own render) pushes
  // the deep-linkable /contact/<convNumber> URL for this selection — each
  // conversation opened is its own history entry, so the browser's back/
  // forward buttons step through them the way they do any other navigation.
  const trySelectConversation = useCallback(async (id, convNumber) => {
    if (id === selectedId) return
    if (!(await confirmDiscardDraft())) return
    setSelectedId(id)
    setActiveTabId(null)
    if (convNumber != null) {
      convNumberByIdRef.current.set(id, convNumber)
      window.history.pushState({}, '', convNumberPath(convNumber))
    }
  }, [selectedId, confirmDiscardDraft])

  const deepLinkPendingRef = useRef(false)
  const parkedDeepLinkRef = useRef(false)
  const tasksDeepLinkRef = useRef(false)

  // Boot-time resolution of a deep-linkable /contact/<convNumber> URL.
  // consumeDeepLinkPath() (authConfig.js) returns the path IF MSAL's own
  // redirect flow had to preserve it across the login round trip; null
  // means either a normal already-authenticated load (window.location.pathname
  // already holds the real value — it was never lost) or no deep link at
  // all. replaceState throughout, not pushState — this resolves what's
  // ALREADY in the address bar, never a navigation the CSR didn't ask for.
  //
  // deepLinkPendingRef is set synchronously (before the async id lookup
  // below resolves) so the nav-restore effect further down — which reads
  // last-viewed page from localStorage and can land a brand-new tab on
  // Settings/Ops/etc. — knows to defer to this URL instead of fighting it.
  // Without it, a /contact/<n> link opened in a fresh tab would still show
  // whatever admin page was last open in some OTHER tab, since localStorage
  // is shared across tabs of the same origin.
  useEffect(() => {
    const path = consumeDeepLinkPath() ?? window.location.pathname
    // /parked (2026-10-08): resolved in the nav-restore effect below, which
    // waits for /api/me and so knows whether parking is on.
    if (isParkedPath(path)) {
      parkedDeepLinkRef.current = true
      deepLinkPendingRef.current = true
      return
    }
    // /tasks (2026-10-12): resolved below once /api/me says whether the
    // feature is on and this requester can use it.
    if (isTasksPath(path)) {
      tasksDeepLinkRef.current = true
      deepLinkPendingRef.current = true
      return
    }
    const convNumberFromPath = parseConvNumberFromPath(path)
    if (!convNumberFromPath) return
    deepLinkPendingRef.current = true
    fetchConversationIdByNumber(convNumberFromPath).then((id) => {
      if (id) {
        convNumberByIdRef.current.set(id, convNumberFromPath)
        setSelectedId(id)
        setMode('agent')
        setNavPage('queue')
        window.history.replaceState({}, '', convNumberPath(convNumberFromPath))
      } else {
        // Not found / no access — fall back to the normal default view
        // rather than hard-failing app boot, and clear the bad URL. Also
        // clears the pending flag so the nav-restore effect below goes back
        // to restoring the last-viewed page instead of permanently skipping
        // it for this load (there's no conversation left to defer to).
        deepLinkPendingRef.current = false
        window.history.replaceState({}, '', '/')
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Browser back/forward — window.location has ALREADY changed by the time
  // this fires, so this never calls pushState itself (that would corrupt
  // the history stack); on a declined dirty-draft guard, the URL is pushed
  // back to the STILL-current conversation's own path to undo the
  // browser's already-applied navigation and keep the two in sync.
  useEffect(() => {
    async function onPopState() {
      const convNumberFromPath = parseConvNumberFromPath(window.location.pathname)
      const revertUrl = () => {
        const current = conversations.find((c) => c.id === selectedId)
        window.history.pushState({}, '', convNumberPath(current?.convNumber))
      }
      if (!convNumberFromPath) {
        if (!(await confirmDiscardDraft())) { revertUrl(); return }
        setSelectedId(null)
        setActiveTabId(null)
        return
      }
      fetchConversationIdByNumber(convNumberFromPath).then(async (id) => {
        if (!id || id === selectedId) return
        if (!(await confirmDiscardDraft())) { revertUrl(); return }
        setSelectedId(id)
        setActiveTabId(null)
      })
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [selectedId, conversations, confirmDiscardDraft])

  // Closes one open conversation — same "stop viewing" semantics regardless
  // of whether it's the primary (selectedId) tab or one opened via the Other
  // Messages modal: no status change, just release its view/claim (Thread's
  // own heartbeat-cleanup-on-unmount releases the assignment automatically
  // once its ConversationPane unmounts below). Collapsing back to a single
  // remaining conversation restores the exact pre-tabs look (no strip, no
  // openTabs). skipDirtyGuard: true is used by handleDecided below, where a
  // decision was just recorded and there's nothing left to discard.
  const closeConversationTab = useCallback(async (id, { skipDirtyGuard = false } = {}) => {
    // The customer not told about an Other Actions step yet (2026-10-30).
    const remind = skipDirtyGuard ? null : untoldMapRef.current.get(id)
    // It replaces the edited-reply prompt (Jenn, 2026-10-30): one prompt only.
    if (remind && !(await remind('close_tab'))) return
    if (!remind && !skipDirtyGuard && draftDirtyMapRef.current.get(id) && !(await askConfirm(CLOSE_CONTACT_PROMPT))) {
      return
    }
    draftDirtyMapRef.current.delete(id)
    untoldMapRef.current.delete(id)
    const currentTabIds = openTabs.length > 0
      ? [selectedId, ...openTabs.map((t) => t.conversationId)].filter((v, i, arr) => v != null && arr.indexOf(v) === i)
      : (selectedId ? [selectedId] : [])
    const remaining = currentTabIds.filter((t) => t !== id)

    if (remaining.length <= 1) {
      const next = remaining[0] ?? null
      setSelectedId(next)
      setOpenTabs([])
      setActiveTabId(null)
      // The primary changed (2026-10-19): the URL follows. Closing a
      // non-primary tab leaves it alone.
      if (next !== selectedId) syncUrlToView({ page: navPageRef.current, primaryId: next })
      return
    }
    if (id === selectedId) {
      // The primary tab itself was closed with others still open — promote
      // the next one to primary (selectedId is always "tab 0" conceptually).
      const [newPrimary, ...rest] = remaining
      setSelectedId(newPrimary)
      syncUrlToView({ page: navPageRef.current, primaryId: newPrimary })
      setOpenTabs((prev) => prev.filter((t) => rest.includes(t.conversationId)))
      setActiveTabId((cur) => (cur === id ? newPrimary : cur))
    } else {
      setOpenTabs((prev) => prev.filter((t) => t.conversationId !== id))
      setActiveTabId((cur) => (cur === id ? null : cur))
    }
  }, [openTabs, selectedId, syncUrlToView, askConfirm])

  // "Other Messages" modal's "Open Conversation" link — adds a new tab (or
  // just switches to it if it's already open/primary).
  const openConversationTab = useCallback((targetId) => {
    if (targetId === selectedId) { setActiveTabId(null); return }
    if (openTabs.some((t) => t.conversationId === targetId)) { setActiveTabId(targetId); return }
    setOpenTabs((prev) => [...prev, { conversationId: targetId, openedAt: new Date().toISOString() }])
    setActiveTabId(targetId)
  }, [selectedId, openTabs])

  const switchTab = useCallback((id) => setActiveTabId(id === selectedId ? null : id), [selectedId])

  // A pick from a Queue row's "related ▾" dropdown (2026-10-22,
  // queueRelatedOpenMode in contactTabs.js): with a contact open it opens in
  // a contact tab, or switches to the tab it already has, as the related
  // banners do; with nothing open it opens the normal way, as a row click
  // does. A tab opened this way never claims a closed contact (the same
  // pane the banners open).
  const openQueueRelated = useCallback((id, convNumber) => {
    if (queueRelatedOpenMode(tabIds, id) === 'select') { trySelectConversation(id, convNumber); return }
    openConversationTab(id)
  }, [tabIds.join(','), trySelectConversation, openConversationTab])

  // "Stop viewing" (the Thread header's X button, and each tab's own X) —
  // deselects/closes without recording any decision or touching status.
  const handleRequestClose = useCallback((id) => closeConversationTab(id), [closeConversationTab])

  // After a human decision (Send/Escalate/Close/Spam) on any open pane:
  // refresh the list (chips) and close that pane — leaving it open would keep
  // its assignment claimed and risks another agent thinking it's still being
  // worked, when the decision just closed that out. No dirty-guard: a
  // completed decision means whatever was dirty is now moot.
  // The storefront delivery estimate re-estimated at Send (2026-10-21,
  // docs/edd-api.md): "Delivery estimate updated to 10/24/2026", shown after
  // the sent contact's tab closes.
  const [deliveryEstimateNotice, setDeliveryEstimateNotice] = useState(null)
  // A reply eBay refused on the return, sent as an eBay message instead
  // (2026-11-04, ReturnReplyRefusedNotice.jsx): the green confirmation,
  // shown after the contact's tab closes.
  const [sentAsEbayMessage, setSentAsEbayMessage] = useState(null)
  const handleDecided = useCallback((id, extra) => {
    if (extra?.deliveryEstimateNotice) setDeliveryEstimateNotice(extra.deliveryEstimateNotice)
    if (extra?.sentAsEbayMessage) setSentAsEbayMessage(extra.sentAsEbayMessage)
    reloadConversations()
    closeConversationTab(id, { skipDirtyGuard: true })
  }, [reloadConversations, closeConversationTab])

  // Parking (Lab-only, 2026-10-02): the contact just parked leaves the
  // owner's screen through the ordinary tab close, WITHOUT releasing the
  // claim (Thread's parkedRef stops its cleanup from releasing).
  const handleParked = useCallback((id) => {
    closeConversationTab(id, { skipDirtyGuard: true })
    reloadConversations()
    parkRefreshRef.current?.()
  }, [reloadConversations, closeConversationTab])
  const parkRefreshRef = useRef(null)

  // Shell-level actions that used to reach straight into the single `detail`/
  // `suggestionRefreshSignal` state now target whichever pane is ACTIVE,
  // via the registry each ConversationPane maintains itself.
  const handleBulkGenerated = useCallback(() => {
    reloadConversations()
    const api = paneApiRegistry.current.get(effectiveActiveId)
    api?.refreshDetail()
    api?.bumpSuggestionRefresh()
  }, [reloadConversations, effectiveActiveId])

  const handleManualActionCompleted = useCallback((_type, forcedDraftText) => {
    reloadConversations()
    const api = paneApiRegistry.current.get(effectiveActiveId)
    api?.refreshDetail()
    api?.bumpSuggestionRefresh(forcedDraftText)
  }, [reloadConversations, effectiveActiveId])

  // ManualActionsSidebar's Resend Order/Shipping Confirmation Email buttons'
  // prefill — the ACTIVE pane's already-loaded order-summary customerEmail,
  // bubbled up from ConversationPane rather than a second order-summary fetch
  // of the sidebar's own (see ConversationPane's onOrderSummaryEmailChange).
  // Reset on every active-conversation switch so a stale email from the
  // previous conversation never briefly shows for the new one.
  const [activeOrderCustomerEmail, setActiveOrderCustomerEmail] = useState(null)
  useEffect(() => { setActiveOrderCustomerEmail(null) }, [effectiveActiveId])
  const handleOrderSummaryEmailChange = useCallback((conversationId, customerEmail) => {
    if (conversationId === effectiveActiveId) setActiveOrderCustomerEmail(customerEmail)
  }, [effectiveActiveId])

  const handleAgentReload = useCallback(() => {
    reloadConversations()
  }, [reloadConversations])

  const handleAgentBulkGenerated = useCallback(() => {
    handleBulkGenerated()
  }, [handleBulkGenerated])

  const roles = me?.roles ?? []
  const isAdmin = roles.includes('admin')
  const effectiveIsAdmin = isAdmin && !viewingAsAgent
  const queueToggleViewer = queueToggleViewerFor(me, { isAdmin: effectiveIsAdmin })
  queueToggleViewerRef.current = queueToggleViewer
  // "Elevated" now means admin: the senior role was retired 2026-09-15
  // (only csr and admin remain). The isElevated/effectiveIsElevated names
  // are kept so the many props keyed on them keep working.
  const isElevated = isAdmin
  const effectiveIsElevated = isElevated && !viewingAsAgent
  // /api/me's own fields (server/src/routes/me.js) — a plain csr's Support
  // Permissions grant, part of the eventual retirement of the Entra 'senior'
  // role. meAgentId/meSupportPermissions feed RolesPage's own row-graying
  // (which agent row is the CURRENT viewer's own); effectiveIsElevatedOrSupport
  // is the broader "admin, OR csr with Support Permissions" gate,
  // used ONLY where the server itself now accepts that third state (see each
  // usage's own comment) — canAccessFlows/canAccessRuntimeFlags and
  // Reporting/My Stats are deliberately NOT among them, staying admin-only.
  const meAgentId = me?.agentId ?? null
  const meSupportPermissions = me?.supportPermissions ?? false
  const isElevatedOrSupport = isElevated || meSupportPermissions
  const effectiveIsElevatedOrSupport = isElevatedOrSupport && !viewingAsAgent

  // When "View as Agent" is on and the admin is on an admin-only view, drop
  // back to agent -- and the mirror case: when View-as-Agent turns back OFF
  // while on the agent-only 'my-stats' page, drop back too, so an admin
  // doesn't land back in an agent-only page after returning to admin.
  useEffect(() => {
    if (viewingAsAgent && (mode === 'ops' || mode === 'audit' || mode === 'settings' || mode === 'reporting')) {
      setMode('agent')
      setNavPage('queue')
      syncUrlToView({ page: 'queue', primaryId: selectedIdRef.current })
    } else if (!viewingAsAgent && mode === 'my-stats') {
      setMode('agent')
      setNavPage('queue')
      syncUrlToView({ page: 'queue', primaryId: selectedIdRef.current })
    }
  }, [viewingAsAgent]) // eslint-disable-line react-hooks/exhaustive-deps

  // Restores the nav rail's last page across a reload — replaces the old
  // 'cxhub_layout' key, which was only ever written, never read. Runs once,
  // gated on `me` having loaded so an admin-only saved page can be validated
  // against this user's actual role before being restored.
  const navRestoredRef = useRef(false)
  useEffect(() => {
    if (navRestoredRef.current || !me) return
    navRestoredRef.current = true
    // A /parked deep link opens the Parked page when parking is on, and falls
    // back to '/' like any unknown path when it is off.
    if (tasksDeepLinkRef.current) {
      tasksDeepLinkRef.current = false
      deepLinkPendingRef.current = false
      if (navPageForTasksPath(TASKS_PATH, me)) goToNavPage('tasks')
      else window.history.replaceState({}, '', '/')
      return
    }
    if (parkedDeepLinkRef.current) {
      parkedDeepLinkRef.current = false
      deepLinkPendingRef.current = false
      const page = navPageForParkedPath(PARKED_PATH, Boolean(me?.parkingEnabled))
      if (page) {
        goToNavPage(page)
        window.history.replaceState({}, '', PARKED_PATH)
      } else {
        window.history.replaceState({}, '', '/')
      }
      return
    }
    // A /contact/<n> URL (opened directly, e.g. from an admin "Open in Agent
    // View" link) wins over the last-viewed page — the whole point of that
    // link is to land on the conversation, not on wherever this browser's
    // OTHER tabs last left Settings/Ops/etc. in shared localStorage.
    if (deepLinkPendingRef.current) return
    const saved = localStorage.getItem(NAV_PAGE_KEY)
    if (!saved || saved === 'queue') return
    if ((saved === 'ops' || saved === 'audit') && !effectiveIsAdmin) return
    if (saved === 'settings' && !effectiveIsElevated) return
    if (saved === 'reporting' && !canSeeReportingTab(effectiveIsAdmin)) return
    if (saved === 'my-stats' && effectiveIsElevated) return
    if (saved === 'closed' || saved === 'ops' || saved === 'audit' || saved === 'settings' || saved === 'reporting' || saved === 'my-stats') goToNavPage(saved)
  }, [me, effectiveIsAdmin, effectiveIsElevated, goToNavPage])

  // Flows automation failure count — admin-only top-nav notification. Polled
  // (not just fetched once) so a failure surfacing while the admin is
  // already logged in still gets noticed without a page reload.
  // The count rides on the shared 30 s session tick (sessionTick.js); the
  // server sends it only to an admin (null otherwise).
  const [unresolvedFailureCount, setUnresolvedFailureCount] = useState(0)
  const refreshFailureCount = useCallback(() => {
    if (!effectiveIsAdmin) return
    sessionTick.refresh()
  }, [effectiveIsAdmin])
  useEffect(() => {
    if (!effectiveIsAdmin) { setUnresolvedFailureCount(0); return }
    return sessionTick.subscribe((tick) => {
      if (typeof tick.automationFailureCount === 'number') setUnresolvedFailureCount(tick.automationFailureCount)
    })
  }, [effectiveIsAdmin])

  // Parked contacts (Lab-only, 2026-10-02): my parks for everyone, every park
  // (and how many are past the alert threshold) for an admin. Polled every
  // 30s, the assignment heartbeat's own cadence, and refreshed at once after
  // a park.
  const parkingEnabled = Boolean(me?.parkingEnabled)
  const [myParks, setMyParks] = useState([])
  const [allParks, setAllParks] = useState([])
  // GET /api/parks's own alert threshold (2026-10-08): the Parked page's red
  // rows use it, the same number as the header alert.
  const [parkAlertMinutes, setParkAlertMinutes] = useState(PARK_ALERT_MINUTES)
  const [parkOverdueCount, setParkOverdueCount] = useState(0)
  const [parkedScrollNonce, setParkedScrollNonce] = useState(0)
  // Pending auto re-queue notices (2026-10-07), oldest first, from the same
  // 30s request as my parks; the first one shows as a modal.
  const [autoRequeueNotices, setAutoRequeueNotices] = useState([])
  const [noticeBusy, setNoticeBusy] = useState(false)
  const [noticeError, setNoticeError] = useState(null)
  // Both ride on the shared 30 s session tick (sessionTick.js): `parks.mine`
  // for everyone, `parks.all` for an admin (null otherwise). refreshParks()
  // is the "now" after a park, a release or a notice answered.
  const refreshParks = useCallback(() => {
    if (!parkingEnabled) return
    sessionTick.refresh()
  }, [parkingEnabled])
  useEffect(() => {
    if (!parkingEnabled) { setMyParks([]); setAllParks([]); setParkOverdueCount(0); return }
    return sessionTick.subscribe((tick) => {
      const mine = tick.parks?.mine
      if (mine) { setMyParks(mine.parks ?? []); setAutoRequeueNotices(mine.autoRequeued ?? []) }
      const all = tick.parks?.all
      if (all && effectiveIsAdmin) { setAllParks(all.parks ?? []); setParkOverdueCount(all.overdueCount ?? 0); setParkAlertMinutes(all.alertMinutes ?? PARK_ALERT_MINUTES) }
    })
  }, [parkingEnabled, effectiveIsAdmin])
  const parkedBadgeCount = effectiveIsAdmin ? allParks.length : myParks.length
  parkRefreshRef.current = refreshParks
  // A row on the Parked page opens that contact; for its owner, opening it
  // unparks it (the first heartbeat of the open does that, server-side).
  const openParkedConversation = useCallback((id) => {
    setMode('agent')
    setNavPage('queue')
    // The URL follows the page and the primary contact (2026-10-19).
    if (!selectedId || selectedId === id) {
      setSelectedId(id)
      syncUrlToView({ page: 'queue', primaryId: id })
    } else {
      openConversationTab(id)
      syncUrlToView({ page: 'queue', primaryId: selectedId })
    }
  }, [selectedId, openConversationTab, syncUrlToView])
  // Ok / Re-Park on the current notice: the agent stays exactly where they
  // are; the badge and lists refresh either way.
  const currentNotice = autoRequeueNotices[0] ?? null
  async function answerNotice(kind) {
    if (!currentNotice) return
    setNoticeBusy(true); setNoticeError(null)
    try {
      if (kind === 'repark') await reparkConversation(currentNotice.conversationId, currentNotice.id)
      else await acknowledgeAutoRequeue(currentNotice.id)
      setAutoRequeueNotices((list) => list.filter((n) => n.id !== currentNotice.id))
      refreshParks()
    } catch (e) {
      setNoticeError(reparkErrorText(e.body?.error) ?? e.message)
      refreshParks()
    } finally {
      setNoticeBusy(false)
    }
  }
  function jumpToParkedAlert() {
    goToNavPage('parked')
    setParkedScrollNonce((n) => n + 1)
  }

  // End-of-shift reminder (2026-10-07, Lab-only behind PARKING_ENABLED): 30
  // minutes before the agent's shift ends, if they have parked contacts, an
  // in-app reminder, once per shift. The schedule is the header widget's own
  // (no second request); the Lab's "Test shift end" override stands in for it
  // only when the app reports DEPLOY_ENV=lab. Checked on every park refresh
  // (30s) and every minute.
  const [headerSchedule, setHeaderSchedule] = useState(null)
  const [labShiftEnd, setLabShiftEndState] = useState(() => readLabShiftEnd())
  const setLabShiftEnd = useCallback((value) => { writeLabShiftEnd(value); setLabShiftEndState(value) }, [])
  const deployEnv = me?.deployEnv ?? 'prod'
  const reminderSchedule = labOverrideSchedule({ deployEnv, overrideHHMM: labShiftEnd, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }) ?? headerSchedule
  const [reminderTick, setReminderTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setReminderTick((n) => n + 1), 60000)
    return () => clearInterval(timer)
  }, [])
  const [shiftReminder, setShiftReminder] = useState(null) // { key, shiftEnd, count }
  const reminderAgent = me?.username ?? me?.name ?? ''
  useEffect(() => {
    try {
      if (!parkingEnabled || !reminderSchedule?.timezone || !reminderSchedule.shift_end) { setShiftReminder(null); return }
      const key = shiftReminderKey({ agent: reminderAgent, date: dateInZone(reminderSchedule.timezone), shiftEnd: reminderSchedule.shift_end })
      const isDue = shiftEndReminderDue({
        parkingEnabled, schedule: reminderSchedule, nowHHMM: nowHHMMInZone(reminderSchedule.timezone),
        parkCount: myParks.length, alreadyShown: readFlag(key),
      })
      setShiftReminder(isDue ? { key, shiftEnd: reminderSchedule.shift_end, count: myParks.length } : null)
    } catch {
      setShiftReminder(null)
    }
  }, [parkingEnabled, reminderSchedule?.timezone, reminderSchedule?.shift_end, reminderSchedule?.shift_start, reminderSchedule?.day_off, myParks, reminderTick, reminderAgent]) // eslint-disable-line react-hooks/exhaustive-deps
  function dismissShiftReminder() {
    if (shiftReminder) writeFlag(shiftReminder.key)
    setShiftReminder(null)
  }
  // Opens the Parked page in a NEW tab (2026-10-08) so this tab keeps its open
  // contact and claim. The seen flag is written first, so the new tab doesn't
  // show the same reminder again.
  function viewParkedFromReminder() {
    dismissShiftReminder()
    window.open(`${window.location.origin}${PARKED_PATH}`, '_blank', 'noopener')
  }

  // Deep-link into SettingsView at a specific tab (the failure notification) —
  // SettingsView owns its own tab state internally, so bumping `nonce` forces
  // a remount at `tab` even if SettingsView is already open on a different tab.
  const [settingsJump, setSettingsJump] = useState({ tab: 'spam', nonce: 0 })
  function jumpToFailures() {
    setMode('settings')
    setNavPage('settings')
    syncUrlToView({ page: 'settings' })
    setSettingsJump((j) => ({ tab: 'failures', nonce: j.nonce + 1 }))
  }

  // Signed in but not assigned to any CXHub role → show the access notice.
  if (me && roles.length === 0) return <AccessDenied account={accountName} />

  // One source of truth for "the assistant is taking up space on the right", so
  // the layout padding and the panel can never disagree (e.g. dropping to
  // "View as Agent" with it open must reclaim the space, not leave a gap).
  const assistantDocked = assistantOpen && effectiveIsAdmin

  const navRailWidth = navRailCollapsed ? NAV_RAIL_WIDTH_COLLAPSED : NAV_RAIL_WIDTH_EXPANDED

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', height: '100vh' }}>
      {confirmDialog}
      <NavRail
        navPage={navPage}
        onNavigate={goToNavPage}
        collapsed={navRailCollapsed}
        onToggleCollapsed={toggleNavRailCollapsed}
        effectiveIsAdmin={effectiveIsAdmin}
        effectiveIsElevatedOrSupport={effectiveIsElevatedOrSupport}
        tasksVisible={canSeeTasks(me)}
        parkingEnabled={parkingEnabled}
        parkedCount={parkedBadgeCount}
      />
      <Box sx={{
        display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0,
        // Make room for the docked assistant. The panel itself is position:fixed,
        // so padding here shrinks the app (AppBar included) without moving it —
        // the three-pane row reflows instead of being covered.
        paddingRight: assistantDocked ? `${assistantWidth}px` : 0,
        transition: 'padding-right 0.15s ease',
      }}>
      <AppBar position="static" elevation={0}>
        <Toolbar variant="dense" sx={{ gap: 2, minHeight: 40, py: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box component="img" src={cxhubLogo} alt="CXHub" sx={{ height: 28, width: 'auto', display: 'block' }} />
            {/* Which environment this is, from GET /api/me's deployEnv — the
                SAME value the server's outbound door keys off (see
                server/src/outbound.js), so the banner cannot disagree with what
                the box will actually do. This replaced a client-side hostname
                check that read anything-but-staging as "Production", which
                would have labelled the Lab — real credentials, real customer
                data, sending disabled — as prod. Renders nothing until /api/me
                resolves rather than guessing. */}
            {me?.deployEnv && (
              <Typography
                variant="h6"
                sx={{ color: me.deployEnv === 'prod' ? 'error.main' : 'warning.main' }}
              >
                {envLabel(me)}
              </Typography>
            )}
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={0.75} alignItems="center">
            {/* Parked Contacts Alert (Lab-only, 2026-10-02): admin-only, the
                number of contacts parked for more than 60 minutes (from the
                current park), polled like the failure chip beside it. */}
            {effectiveIsAdmin && parkingEnabled && parkOverdueCount > 0 && (
              <Chip
                size="small" color="warning" clickable
                label={`Parked Contacts Alert (${parkOverdueCount})`}
                onClick={jumpToParkedAlert}
                data-testid="parked-alert-chip"
              />
            )}
            {effectiveIsAdmin && unresolvedFailureCount > 0 && (
              <Chip
                size="small" color="error" clickable
                label={`${unresolvedFailureCount} automation failure${unresolvedFailureCount > 1 ? 's' : ''}`}
                onClick={jumpToFailures}
              />
            )}
            {viewingAsAgent ? (
              <>
                <Chip size="small" label="Viewing as Agent" color="warning" variant="outlined" />
                <Chip
                  size="small" label="Back to Admin" variant="outlined" clickable
                  sx={{ fontSize: '0.7rem' }}
                  onClick={() => setViewingAsAgent(false)}
                />
              </>
            ) : (
              <>
                {roles.map((r) => <Chip key={r} size="small" label={r} color={r === 'admin' ? 'primary' : 'default'} />)}
                {isAdmin && (
                  <Chip
                    size="small" label="View as Agent" variant="outlined" clickable
                    sx={{ fontSize: '0.7rem' }}
                    onClick={() => setViewingAsAgent(true)}
                  />
                )}
              </>
            )}
            {/* Today's-schedule widget -- open to every agent (any
                csr/admin); was a short pilot allowlist, lifted once
                the widget proved out end to end. The route
                (routes/schedule.js) is the real enforcement; this is just
                the UI affordance. */}
            <ScheduleWidget onSchedule={setHeaderSchedule} />
            <Snackbar open={Boolean(sentAsEbayMessage)} autoHideDuration={15000} onClose={(_e, reason) => { if (reason !== 'clickaway') setSentAsEbayMessage(null) }} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
              <Box onClick={() => setSentAsEbayMessage(null)} sx={{ maxWidth: 560 }}><ReturnReplyResentNotice sent={sentAsEbayMessage} /></Box>
            </Snackbar>
            <Snackbar open={Boolean(deliveryEstimateNotice)} autoHideDuration={10000} onClose={(_e, reason) => { if (reason !== 'clickaway') setDeliveryEstimateNotice(null) }} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
              <Alert severity="info" onClose={() => setDeliveryEstimateNotice(null)} data-testid="delivery-estimate-notice">{deliveryEstimateNotice}</Alert>
            </Snackbar>
            {parkingEnabled && (
              <AutoRequeueNotice key={currentNotice?.id ?? 'none'} notice={currentNotice} busy={noticeBusy} error={noticeError}
                onOk={() => answerNotice('ok')} onRepark={() => answerNotice('repark')} />
            )}
            {/* End-of-shift reminder (2026-10-08): the same modal as the
                re-queue notice, shown only once no re-queue notice is
                waiting, so the two never stack. Stays until dismissed or
                used. */}
            {parkingEnabled && !currentNotice && (
              <ShiftEndReminderNotice reminder={shiftReminder} onViewParked={viewParkedFromReminder} onDismiss={dismissShiftReminder} />
            )}
            {/* E2E test seam: the e2e build has no MSAL account, so the menu (and
                its Sign out) would never render there. Only in that build, and only
                with parking on, it falls back to /api/me's name so the parked-
                contacts logout confirm can be tested. Inert in production. */}
            <AccountMenu accountName={accountName || (E2E && parkingEnabled ? (me?.name ?? '') : '')} parkingEnabled={parkingEnabled} />
            {/* Admin-only AI chat. Hidden under "View as Agent" like every
                other admin affordance; the route is the real gate. */}
            {effectiveIsAdmin && (
              <AssistantChatButton open={assistantOpen} onToggle={() => setAssistantOpen((o) => !o)} />
            )}
            <ColorModeToggle colorMode={colorMode} onToggle={onToggleColorMode} />
          </Stack>
        </Toolbar>
      </AppBar>
      {mode === 'ops' && effectiveIsAdmin ? (
        <OpsView />
      ) : mode === 'audit' && effectiveIsAdmin ? (
        <Box sx={{ flex: 1, overflowY: 'auto' }}><QaObserverView /></Box>
      ) : mode === 'settings' && effectiveIsElevatedOrSupport ? (
        <SettingsView
          key={settingsJump.nonce}
          initialTab={settingsJump.tab}
          onOpenInAgent={(_conversationId, convNumber) => {
            // Always a NEW tab (not this same Settings tab) — an admin working
            // Feedback/Failures wants to compare the note they're writing
            // against the live conversation, not lose their place here. The
            // new tab's own boot-time /contact/<n> resolution (above) is what
            // lands it on Agent View instead of wherever Settings last was.
            if (convNumber != null) window.open(convNumberPath(convNumber), '_blank', 'noopener')
          }}
          unresolvedFailureCount={unresolvedFailureCount}
          onFailuresChanged={refreshFailureCount}
          canAccessFlows={effectiveIsAdmin}
          canAccessPrioritization={effectiveIsAdmin}
          canAccessRuntimeFlags={effectiveIsAdmin}
          canAccessRoles={effectiveIsElevatedOrSupport}
          viewerAgentId={meAgentId}
          viewerSupportPermissions={meSupportPermissions}
          viewerIsElevated={effectiveIsElevated}
        />
      ) : mode === 'reporting' && canSeeReportingTab(effectiveIsAdmin) ? (
        <ReportingPage
          onOpenInAgent={(_conversationId, convNumber) => {
            // Same "always a new tab" reasoning as Settings' own onOpenInAgent
            // above -- an admin comparing a report row against the live
            // conversation wants to keep their place in Reporting, not lose it.
            if (convNumber != null) window.open(convNumberPath(convNumber), '_blank', 'noopener')
          }}
          showForecastingTab={canSeeForecastingTab(me?.username)}
        />
      ) : mode === 'tasks' && canSeeTasks(me) ? (
        <TasksView onOpenContact={openParkedConversation} />
      ) : mode === 'new' ? (
        <NewEmailView firstName={me?.name?.trim().split(/\s+/)[0] ?? ''} onDirtyChange={setNewEmailDirty} />
      ) : mode === 'my-stats' && canSeeMyStatsTab(effectiveIsAdmin) ? (
        <MyStatsPage />
      ) : mode === 'parked' && parkingEnabled ? (
        <ParkedView
          showLabShiftOverride={deployEnv === 'lab'}
          labShiftEnd={labShiftEnd}
          onLabShiftEndChange={setLabShiftEnd}
          isAdmin={effectiveIsAdmin}
          myParks={myParks}
          allParks={allParks}
          alertMinutes={parkAlertMinutes}
          adminKey={me?.username ?? me?.name ?? ''}
          onOpen={openParkedConversation}
          onRefresh={refreshParks}
          scrollToAllNonce={parkedScrollNonce}
        />
      ) : (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {fetchError && (
            <Alert severity="error" sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, boxShadow: 3 }}
              action={<Button color="inherit" size="small" onClick={() => { setFetchError(null); setListLoading(true); reloadConversations() }}>Retry</Button>}
              onClose={() => setFetchError(null)}>
              {fetchError}
            </Alert>
          )}
          <ConversationList items={conversations} parkingEnabled={parkingEnabled} selectedId={selectedId} onSelect={trySelectConversation} onOpenRelated={openQueueRelated} loading={listLoading} onReload={handleAgentReload} onBulkGenerated={handleAgentBulkGenerated} canPurge={effectiveIsElevated} canGenerateAll={false} width={conversationListWidth} total={convTotal} queueTotal={queueTotal} dateFrom={dateFrom} dateTo={dateTo} onDateFrom={setDateFrom} onDateTo={setDateTo} statusFilter={statusFilter} onStatusFilterChange={setStatusFilter} intentFilter={intentFilter} onIntentFilterChange={setIntentFilter} companyFilter={companyFilter} onCompanyFilterChange={setCompanyFilter} channelFilter={channelFilter} onChannelFilterChange={setChannelFilter} sortBy={sortBy} onSortByChange={setSortBy} reviewedByMgmtFilter={reviewedByMgmtFilter} onReviewedByMgmtFilterChange={setReviewedByMgmtFilter} spamSetByFilter={spamSetByFilter} onSpamSetByFilterChange={setSpamSetByFilter} actedByUserFilter={actedByUserFilter} onActedByUserFilterChange={setActedByUserFilter} actedByUserOptions={actedByUserOptions} includeToAddressFilter={includeToAddressFilter} onIncludeToAddressFilterChange={setIncludeToAddressFilter} toAddressFilterOptions={toAddressFilterOptions} toAddressDirectory={toAddressDirectory} automatedFilter={automatedFilter} onAutomatedFilterChange={setAutomatedFilter} sinceFilter={sinceFilter} onSinceFilterChange={setSinceFilter} onSearchQueryChange={setSearchQuery} onLoadMore={loadMoreConversations} hasMore={conversations.length < convTotal} currentUser={me ? (me.name ?? me.username ?? '') : null} assignmentNonce={assignmentNonce} deployEnv={me?.deployEnv ?? 'prod'} filtersOpen={filtersOpen} onFiltersOpenChange={setFiltersOpen} navPage={navPage} isElevated={effectiveIsElevatedOrSupport} isAdmin={effectiveIsElevated} showSupportToggle={hasSupportToggleAccess(me, { isAdmin: effectiveIsAdmin })} toggleViewer={queueToggleViewer} defaultToggles={defaultToggleSelection(me, { isAdmin: effectiveIsAdmin })} defaultChannels={myDefaultChannels} channelOptions={myChannelOptions} />
          <ResizeHandle onMouseDown={startConversationListDrag} label="Resize conversation list" />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            {/* Tab strip: only once a second conversation has been opened via
                the Other Messages modal — with zero/one open conversations
                this renders nothing and the single ConversationPane below is
                visually and behaviorally identical to the pre-tabs view. */}
            {tabIds.length > 1 && (
              <TabStrip
                tabIds={tabIds}
                activeId={effectiveActiveId}
                metaById={new Map(tabIds.map((id) => [id, tabMetaById[id] ?? {}]))}
                rowsById={new Map(conversations.filter((c) => tabIds.includes(c.id)).map((c) => [c.id, c]))}
                seenInbound={seenInbound}
                onSwitch={switchTab}
                onClose={closeConversationTab}
              />
            )}
            {(tabIds.length > 0 ? tabIds : [null]).map((id) => (
              <ConversationPane
                showShadowCompare={effectiveIsAdmin}
                key={id ?? 'none'}
                conversationId={id}
                latestSuggestionId={conversations.find((c) => c.id === id)?.latestSuggestionId ?? null}
                visible={tabIds.length <= 1 || id === effectiveActiveId}
                currentUser={me ? (me.name ?? me.username ?? '') : null}
                isAdmin={effectiveIsElevated}
                isElevatedOrSupport={effectiveIsElevatedOrSupport}
                autoRegenerateStaleMinutes={me?.aiAutoRegenerateStaleMinutes ?? 5}
                reloadConversations={reloadConversations}
                onAssignmentChanged={handleAssignmentChanged}
                onDraftDirtyChange={handleDraftDirtyChange}
                onUntoldCustomerChange={handleUntoldCustomerChange}
                registerPaneApi={registerPaneApi}
                unregisterPaneApi={unregisterPaneApi}
                onOpenConversationTab={openConversationTab}
                onRequestClose={handleRequestClose}
                onDecided={handleDecided}
                onInactivityPrompt={handleInactivityPrompt}
                onOrderSummaryEmailChange={handleOrderSummaryEmailChange}
                parkingEnabled={parkingEnabled}
                onParked={handleParked}
                onTabMeta={handleTabMeta}
              />
            ))}
          </Box>
        </Box>
      )}
      </Box>
      {/* Manual actions sidebar: a plain fixed-position Box (no MUI Drawer/
          Dialog) so it never traps focus or blocks clicks to the rest of the
          page — the CSR needs to keep copy/pasting between it and the
          conversation/suggestion panel behind it. Lives as a sibling of the
          AppBar/mode-ternary above (not inside the 3-pane row) so opening/
          closing it never reflows the agent layout — a plain right-edge
          overlay (design experiment #2; #1 pushed the layout aside instead),
          `right` offsets by its own width once open, same convention as the
          old left-edge version just mirrored. Every action now opens its own
          DraggableModal instead of expanding inline, so the panel itself only
          ever needs to hold a column of button labels.
          Hidden entirely with no conversation selected (nothing useful to
          toggle), AND whenever mode isn't 'agent' — effectiveActiveId stays
          set after navigating to Settings/Ops/Reporting/My Stats (it's just
          "a conversation is selected," not "the agent workspace is showing"),
          so without this second gate the tab/panel used to leak onto every
          other page too. A solid grey.700 tab with vertical "Other Actions" text —
          a bordered paper tab with an icon glyph got lost visually; this
          reads unambiguously. Height (100) is sized to the actual rotated
          text's rendered extent (~79px at this font size/weight), not
          guessed — width stays narrow since the label is vertical, not
          horizontal. */}
      {effectiveActiveId && mode === 'agent' && (
        <Box
          onClick={() => setActionSidebarOpen((o) => !o)}
          role="button"
          aria-label={actionSidebarOpen ? 'Close manual actions' : 'Open manual actions'}
          data-testid="manual-actions-tab"
          sx={{
            position: 'fixed', top: '50%', transform: 'translateY(-50%)',
            right: actionSidebarOpen ? actionSidebarWidth : 0,
            zIndex: 1201,
            width: 30, height: 100, borderRadius: '8px 0 0 8px',
            bgcolor: 'grey.700', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: 2, userSelect: 'none',
            transition: 'right 0.15s ease',
          }}
        >
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, letterSpacing: '0.3px', whiteSpace: 'nowrap', transform: 'rotate(-90deg)' }}>
            Other Actions
          </Typography>
        </Box>
      )}
      {/* Stays MOUNTED whenever a conversation is selected, regardless of
          actionSidebarOpen — every button below owns a DraggableModal, and a
          button click auto-collapses this panel (onModalOpen), which must
          NOT tear down the modal it just opened. So "closed" is a visibility
          toggle here, not an unmount: visibility: hidden (not display: none,
          which no descendant could override) hides the panel chrome, and
          DraggableModal explicitly overrides back to visible/interactive on
          itself, so an already-open modal stays fully usable regardless of
          whether this panel is shown. */}
      {effectiveActiveId && mode === 'agent' && (
        <Box data-testid="manual-actions-panel" sx={{
          position: 'fixed', top: 0, bottom: 0, right: 0, width: actionSidebarWidth,
          zIndex: 1200, display: 'flex', bgcolor: 'background.paper', boxShadow: 4,
          visibility: actionSidebarOpen ? 'visible' : 'hidden',
          pointerEvents: actionSidebarOpen ? 'auto' : 'none',
        }}>
          <ResizeHandle onMouseDown={startActionSidebarDrag} label="Resize manual actions sidebar" />
          <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
            <ManualActionsSidebar conversationId={effectiveActiveId} onActionCompleted={handleManualActionCompleted} onModalOpen={() => setActionSidebarOpen(false)} isAdmin={effectiveIsElevated} orderSummaryCustomerEmail={activeOrderCustomerEmail} taskViewer={queueToggleViewer} canCreateTasks={canSeeTasks(me)} onTaskCreated={() => paneApiRegistry.current.get(effectiveActiveId)?.refreshDetail()} />
          </Box>
        </Box>
      )}
      {/* Admin AI chat, mirroring the manual-actions sidebar on the right edge.
          Unmounts (and so forgets its transcript) if admin is dropped. */}
      {assistantDocked && (
        <AssistantChat
          onClose={() => setAssistantOpen(false)}
          conversationId={effectiveActiveId}
          width={assistantWidth}
          onResizeStart={startAssistantDrag}
        />
      )}
      <InactivityCheckInDialog
        prompts={inactivityPrompts}
        conversationsById={new Map(conversations.map((c) => [c.id, c]))}
        onResolved={(conversationId, yes) => {
          handleInactivityPrompt(conversationId, null)
          if (!yes) {
            // A release alone isn't enough: this agent's own tab for this
            // conversation is still mounted and still heartbeating every
            // 30s, and claimOrRefresh's own WHERE clause lets anyone
            // (including the same agent) re-claim an unowned row on the
            // very next tick -- silently undoing the "No" within 30
            // seconds and resetting the 15-minute clock. Closing the tab
            // is what actually stops that (same helper handleDecided
            // already uses for the same reason after a real decision).
            closeConversationTab(conversationId, { skipDirtyGuard: true })
          }
        }}
      />
    </Box>
  )
}

// E2E/visual-test mode (Playwright): skip the MSAL login gate and render the
// Shell directly, so tests can mock the API and exercise UI states. Only ever
// true in a test build (VITE_E2E=true); production never sets it.
const E2E = import.meta.env.VITE_E2E === 'true'

// ?view=activity on the boot URL (the Feedback Review's "Activity log"
// link), read once at load before the address bar is rewritten.
let bootThreadView = (() => {
  try { return new URLSearchParams(window.location.search).get('view') === 'activity' ? 'activity' : null } catch { return null }
})()
// Read without being used up (2026-10-29): React's development double render
// evaluates a useRef() argument twice and keeps the second, so taking it in
// render lost it. The effect that applies it clears it.
function peekBootThreadView() {
  return bootThreadView
}
function clearBootThreadView() {
  bootThreadView = null
}
// &feedback=<id> with it (2026-10-29, the Feedback Review's Activity log
// link): the Activity Log scrolls to that report and marks it "Reported here".
const bootFeedbackId = (() => {
  try { return feedbackIdFromSearch(window.location.search) } catch { return null }
})()

export default function App({ colorMode, onToggleColorMode }) {
  // Checked at the same boot-time point /contact/:id's own resolution
  // happens (see parseFlowchartViewerPath's own comment) — renders the
  // flowchart viewer INSTEAD of the normal Shell, still behind the same
  // MSAL auth wall below.
  const flowchartFlowKey = parseFlowchartViewerPath(window.location.pathname)
  if (flowchartFlowKey) {
    const params = new URLSearchParams(window.location.search)
    const viewer = (
      <Suspense fallback={<Box sx={{ p: 3 }}><CircularProgress /></Box>}>
        <FlowchartViewer
          flowKey={flowchartFlowKey}
          company={params.get('company')}
          channel={params.get('channel')}
          client={params.get('client')}
          team={params.get('team')}
        />
      </Suspense>
    )
    if (E2E) return viewer
    return (
      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={apiRequest}>
        {viewer}
      </MsalAuthenticationTemplate>
    )
  }

  if (E2E) return <Shell colorMode={colorMode} onToggleColorMode={onToggleColorMode} />
  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={apiRequest}>
      <Shell colorMode={colorMode} onToggleColorMode={onToggleColorMode} />
    </MsalAuthenticationTemplate>
  )
}
